﻿// Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';

// VITE_ENVIRONMENT is stamped at build time by deploy.sh; unset when running
// the local Vite dev server, which counts as dev too.
function isDevEnvironment() {
    const envTag = import.meta.env.VITE_ENVIRONMENT;
    return envTag !== 'prod' && envTag !== 'production';
}

// Authentication state
let currentUser = null;
let authToken = null;

// Global state
let currentProject = null;
let currentStudy   = null;
let currentInvocation = null;
// When set, the "Create New Project" form on the main view is in edit mode for
// this project id (re-using the create fields). null = normal create mode.
let editingProjectId = null;

// Cached project list — populated by loadProjects(), consumed by renderProjectsList()
let _allProjects = [];

function getFavoriteIds() {
    return new Set(_allProjects.filter(p => p.is_favorite).map(p => p.id));
}

async function toggleFavorite(projectId) {
    const project = _allProjects.find(p => p.id === projectId);
    if (!project) return;
    const wasFavorite = project.is_favorite;
    // Optimistic update
    project.is_favorite = !wasFavorite;
    renderProjectsList();
    try {
        if (wasFavorite) {
            await ApiClient.delete(`/projects/${projectId}/favorite`);
        } else {
            await ApiClient.post(`/projects/${projectId}/favorite`, {});
        }
    } catch (err) {
        // Roll back on failure
        project.is_favorite = wasFavorite;
        renderProjectsList();
        showToast('Error', 'Could not update favorite', 'error');
    }
}

// Progress Bar Component
class ProgressBar {
    constructor(container, options = {}) {
        this.container = container;
        this.options = {
            showPercentage: true,
            showSteps: false,
            steps: [],
            animated: true,
            ...options
        };
        this.currentStep = 0;
        this.percentage = 0;
        this.init();
    }

    init() {
        this.container.innerHTML = `
            <div class="progress-text">
                <span class="progress-message">${this.options.message || 'Processing...'}</span>
                ${this.options.showPercentage ? '<span class="progress-percentage">0%</span>' : ''}
            </div>
            <div class="progress-bar">
                <div class="progress-fill" style="width: 0%"></div>
            </div>
            ${this.options.showSteps ? '<div class="progress-steps"></div>' : ''}
        `;

        this.progressFill = this.container.querySelector('.progress-fill');
        this.progressMessage = this.container.querySelector('.progress-message');
        this.progressPercentage = this.container.querySelector('.progress-percentage');
        this.progressSteps = this.container.querySelector('.progress-steps');

        if (this.options.showSteps && this.options.steps.length > 0) {
            this.renderSteps();
        }
    }

    renderSteps() {
        if (!this.progressSteps) return;

        this.progressSteps.innerHTML = this.options.steps
            .map((step, index) => {
                let className = 'progress-step';
                if (index < this.currentStep) className += ' completed';
                if (index === this.currentStep) className += ' active';
                return `<span class="${className}">${step}</span>`;
            })
            .join('');
    }

    updateProgress(percentage, message) {
        this.percentage = Math.max(0, Math.min(100, percentage));

        if (this.progressFill) {
            this.progressFill.style.width = `${this.percentage}%`;
        }

        if (this.progressPercentage && this.options.showPercentage) {
            this.progressPercentage.textContent = `${Math.round(this.percentage)}%`;
        }

        if (message && this.progressMessage) {
            this.progressMessage.textContent = message;
        }
    }

    nextStep(message) {
        if (this.currentStep < this.options.steps.length - 1) {
            this.currentStep++;
            this.renderSteps();

            const stepProgress = (this.currentStep / this.options.steps.length) * 100;
            this.updateProgress(stepProgress, message || this.options.steps[this.currentStep]);
        }
    }

    complete(message = 'Complete!') {
        this.updateProgress(100, message);
        this.currentStep = this.options.steps.length;
        this.renderSteps();

        setTimeout(() => {
            this.container.style.display = 'none';
        }, 2000);
    }

    reset() {
        this.percentage = 0;
        this.currentStep = 0;
        this.updateProgress(0, this.options.message || 'Processing...');
        this.renderSteps();
        this.container.style.display = 'block';
    }

    hide() {
        this.container.style.display = 'none';
    }

    show() {
        this.container.style.display = 'block';
    }
}

// View management
let currentView = 'main';
const views = {
    main: document.getElementById('mainView'),
    projectDetails: document.getElementById('projectDetailsView'),
    studyDetail: document.getElementById('studyDetailView'),
    usabilityReport: document.getElementById('usabilityReportView'),
    fileSummary: document.getElementById('fileSummaryView'),
    admin: document.getElementById('adminView'),
    hfAdvisor: document.getElementById('hfAdvisorView'),
    designChange: document.getElementById('designChangeView'),
};

// DOM Elements
const elements = {
    // Authentication
    loginView: document.getElementById('loginView'),
    appHeader: document.getElementById('appHeader'),
    appFooter: document.getElementById('appFooter'),
    appContainer: document.getElementById('appContainer'),
    loginForm: document.getElementById('loginForm'),
    loginEmail: document.getElementById('loginEmail'),
    loginPassword: document.getElementById('loginPassword'),
    loginBtn: document.getElementById('loginBtn'),
    loginError: document.getElementById('loginError'),
    logoutBtn: document.getElementById('logoutBtn'),
    userDisplayName: document.getElementById('userDisplayName'),
    tenantDisplayName: document.getElementById('tenantDisplayName'),

    // OAuth
    oauthLoginSection: document.getElementById('oauthLoginSection'),
    localLoginSection: document.getElementById('localLoginSection'),
    emailEntryStep: document.getElementById('emailEntryStep'),
    providerButtonsStep: document.getElementById('providerButtonsStep'),
    emailLookupForm: document.getElementById('emailLookupForm'),
    workEmail: document.getElementById('workEmail'),
    continueBtn: document.getElementById('continueBtn'),
    providerMessage: document.getElementById('providerMessage'),
    ssoSignInBtn: document.getElementById('ssoSignInBtn'),
    ssoButtonText: document.getElementById('ssoButtonText'),
    googleSignInBtn: document.getElementById('googleSignInBtn'),
    microsoftSignInBtn: document.getElementById('microsoftSignInBtn'),
    backToEmailBtn: document.getElementById('backToEmailBtn'),
    passwordLoginOption: document.getElementById('passwordLoginOption'),
    providerPasswordForm: document.getElementById('providerPasswordForm'),
    providerPasswordInput: document.getElementById('providerPasswordInput'),
    providerPasswordBtn: document.getElementById('providerPasswordBtn'),
    switchToPasswordBtn: document.getElementById('switchToPasswordBtn'),
    switchToSsoBtn: document.getElementById('switchToSsoBtn'),
    tenantSelectModal: document.getElementById('tenantSelectModal'),
    tenantSelect: document.getElementById('tenantSelect'),
    confirmTenantBtn: document.getElementById('confirmTenantBtn'),
    tenantSelectError: document.getElementById('tenantSelectError'),

    // Project creation
    projectName: document.getElementById('projectName'),
    projectDescription: document.getElementById('projectDescription'),
    projectRegPathGrid: document.getElementById('projectRegPathGrid'),
    projectEnvGrid: document.getElementById('projectEnvGrid'),
    createProjectBtn: document.getElementById('createProjectBtn'),
    createProjectProgress: document.getElementById('createProjectProgress'),
    originalClearanceSection: document.getElementById('originalClearanceSection'),
    originalRegPathway: document.getElementById('originalRegPathway'),
    originalDeviceName: document.getElementById('originalDeviceName'),
    originalClearanceNumber: document.getElementById('originalClearanceNumber'),
    originalClearanceDate: document.getElementById('originalClearanceDate'),
    
    // Projects list
    projectsList: document.getElementById('projectsList'),
    refreshProjectsBtn: document.getElementById('refreshProjectsBtn'),
    projectsSearch: document.getElementById('projectsSearch'),
    
    // Project details
    backBtn: document.getElementById('backBtn'),
    projectNameTitle: document.getElementById('projectNameTitle'),
    projectDescriptionContent: document.getElementById('projectDescriptionContent'),
    
    // File summary
    backToReportBtn: document.getElementById('backToReportBtn'),
    summaryFileName: document.getElementById('summaryFileName'),
    summaryContentDisplay: document.getElementById('summaryContentDisplay'),
    copySummaryTextBtn: document.getElementById('copySummaryTextBtn'),
    
    // Categories & Questions
    categoriesList: document.getElementById('categoriesList'),
    addCategoryBtn: document.getElementById('addCategoryBtn'),
    inlineCategoryForm: document.getElementById('inlineCategoryForm'),
    categoryModalTitle: document.getElementById('categoryModalTitle'),
    categoryForm: document.getElementById('categoryForm'),
    categoryId: document.getElementById('categoryId'),
    categoryProjectId: document.getElementById('categoryProjectId'),
    categoryName: document.getElementById('categoryName'),
    categoryNameCounter: document.getElementById('categoryNameCounter'),
    categoryDescription: document.getElementById('categoryDescription'),
    saveCategoryBtn: document.getElementById('saveCategoryBtn'),
    cancelCategoryBtn: document.getElementById('cancelCategoryBtn'),
    closeCategoryModal: document.getElementById('closeCategoryModal'),
    categoryFormError: document.getElementById('categoryFormError'),

    // Question modal
    categoryQuestionModal: document.getElementById('categoryQuestionModal'),
    categoryQuestionModalTitle: document.getElementById('categoryQuestionModalTitle'),
    categoryQuestionForm: document.getElementById('categoryQuestionForm'),
    questionId: document.getElementById('questionId'),
    questionCategoryId: document.getElementById('questionCategoryId'),
    questionText: document.getElementById('questionText'),
    saveQuestionToCategoryBtn: document.getElementById('saveQuestionToCategoryBtn'),
    cancelQuestionFormBtn: document.getElementById('cancelQuestionFormBtn'),
    closeCategoryQuestionModal: document.getElementById('closeCategoryQuestionModal'),
    questionFormError: document.getElementById('questionFormError'),

    // File upload
    uploadArea: document.getElementById('uploadArea'),
    fileInput: document.getElementById('fileInput'),
    uploadProgress: document.getElementById('uploadProgress'),
    filesList: document.getElementById('filesList'),
    filesCount: document.getElementById('filesCount'),
    
    // Summary generation
    generateExcelAnalysisBtn: document.getElementById('generateExcelAnalysisBtn'),
    existingExcelAnalysis: document.getElementById('existingExcelAnalysis'),
    downloadExistingAnalysisBtn: document.getElementById('downloadExistingAnalysisBtn'),
    summaryProgress: document.getElementById('summaryProgress'),
    summaryResult: document.getElementById('summaryResult'),

    // Usability report generation
    generateUsabilityReportBtn: document.getElementById('generateUsabilityReportBtn'),
    existingUsabilityReport: document.getElementById('existingUsabilityReport'),
    downloadExistingReportBtn: document.getElementById('downloadExistingReportBtn'),
    reportProgress: document.getElementById('reportProgress'),
    
    // Admin panel
    adminBtn: document.getElementById('adminBtn'),
    adminView: document.getElementById('adminView'),
    backToMainBtn: document.getElementById('backToMainBtn'),
    
    // Loading overlay
    loadingOverlay: document.getElementById('loadingOverlay'),
    
    // Toast container
    toastContainer: document.getElementById('toastContainer')
};

// Authentication Functions
class AuthManager {
    static saveToken(token) {
        localStorage.setItem('authToken', token);
        authToken = token;
    }

    static getToken() {
        if (!authToken) {
            authToken = localStorage.getItem('authToken');
        }
        return authToken;
    }

    static removeToken() {
        localStorage.removeItem('authToken');
        authToken = null;
    }

    static saveUser(user) {
        localStorage.setItem('currentUser', JSON.stringify(user));
        currentUser = user;
    }

    static getUser() {
        if (!currentUser) {
            const userData = localStorage.getItem('currentUser');
            currentUser = userData ? JSON.parse(userData) : null;
        }
        return currentUser;
    }

    static removeUser() {
        localStorage.removeItem('currentUser');
        currentUser = null;
    }

    /**
     * Decode the JWT exp claim without a library.
     * Returns true if the token is missing, malformed, or past its expiry.
     */
    static isTokenExpired() {
        const token = this.getToken();
        if (!token) return true;
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            // exp is in seconds; Date.now() is in ms
            return payload.exp * 1000 < Date.now();
        } catch {
            // Malformed token — treat as expired
            return true;
        }
    }

    static isAuthenticated() {
        return !!(this.getToken() && this.getUser() && !this.isTokenExpired());
    }

    /**
     * @param {string} [reason] - Optional message shown to the user before redirecting.
     */
    static async logout(reason) {
        this.removeToken();
        this.removeUser();

        if (reason) {
            // Show the message then redirect after a short delay so the user can read it
            showToast('Session ended', reason, 'info');
            await new Promise(resolve => setTimeout(resolve, 2000));
        }

        // Try to get Cognito logout URL for full provider sign-out
        try {
            const response = await fetch(`${API_BASE_URL}/auth/oauth/logout`);
            if (response.ok) {
                const data = await response.json();
                if (data.url) {
                    window.location.href = data.url;
                    return;
                }
            }
        } catch (e) {
            // Cognito not configured, fall through to local logout
        }

        showLoginView();
    }

    static async login(email, password) {
        try {
            console.log('Attempting login with URL:', `${API_BASE_URL}/login`);
            const response = await fetch(`${API_BASE_URL}/login`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, password })
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || 'Login failed');
            }

            const data = await response.json();
            this.saveToken(data.token);
            this.saveUser(data.user);

            return data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    }

    static async initiateOAuth(options = {}) {
        try {
            const params = new URLSearchParams();
            if (options.email) params.set('email', options.email);
            if (options.provider) params.set('provider', options.provider);

            const url = `${API_BASE_URL}/auth/oauth/authorize?${params.toString()}`;
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to initiate OAuth');
            }
            const data = await response.json();
            window.location.href = data.url;
        } catch (error) {
            console.error('OAuth initiation error:', error);
            throw error;
        }
    }

    static async lookupEmail(email) {
        try {
            const response = await fetch(`${API_BASE_URL}/auth/oauth/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            if (!response.ok) {
                throw new Error('Lookup failed');
            }
            return await response.json();
        } catch (error) {
            console.error('Email lookup error:', error);
            return { found: false };
        }
    }
}

// View Management Functions
function showLoginView() {
    elements.loginView.style.display = 'flex';
    elements.appHeader.style.display = 'none';
    elements.appFooter.style.display = 'none';
    elements.appContainer.style.display = 'none';
    elements.loginEmail?.focus();
}

function showAppView() {
    elements.loginView.style.display = 'none';
    elements.appHeader.style.display = 'block';
    elements.appFooter.style.display = 'block';
    elements.appContainer.style.display = 'block';

    // Update user display
    const user = AuthManager.getUser();
    if (user) {
        elements.userDisplayName.textContent = user.username;
        elements.tenantDisplayName.textContent = user.tenant_name || 'Organization';

        // Show/hide admin button based on role
        if (elements.adminBtn) {
            if (user.role === 'admin' || user.role === 'system_admin') {
                elements.adminBtn.style.display = '';  // Reset to default (CSS will handle)
                elements.adminBtn.style.visibility = 'visible';
            } else {
                elements.adminBtn.style.display = 'none';
            }
        }

        // Show/hide Agent Prompts tab for system_admin only
        const agentPromptsTabBtn = document.getElementById('agentPromptsTabBtn');
        if (agentPromptsTabBtn) {
            agentPromptsTabBtn.style.display = user.role === 'system_admin' ? '' : 'none';
        }

        // Show/hide Regulatory Registry tab for system_admin only
        const regulatoryRegistryTabBtn = document.getElementById('regulatoryRegistryTabBtn');
        if (regulatoryRegistryTabBtn) {
            regulatoryRegistryTabBtn.style.display = user.role === 'system_admin' ? '' : 'none';
        }

        // Export seed / New reference buttons: system_admin, and only in the dev environment
        const regulatoryExportSeedBtn = document.getElementById('regulatoryExportSeedBtn');
        if (regulatoryExportSeedBtn) {
            regulatoryExportSeedBtn.style.display = (user.role === 'system_admin' && isDevEnvironment()) ? '' : 'none';
        }
        const regulatoryNewRefBtn = document.getElementById('regulatoryNewRefBtn');
        if (regulatoryNewRefBtn) {
            regulatoryNewRefBtn.style.display = (user.role === 'system_admin' && isDevEnvironment()) ? '' : 'none';
        }

        // Show/hide Tenants tab for system_admin only
        const tenantsTabBtn = document.getElementById('tenantsTabBtn');
        if (tenantsTabBtn) {
            tenantsTabBtn.style.display = user.role === 'system_admin' ? '' : 'none';
        }
    }

    // Load initial data
    loadProjects();
}

function showLoginError(message) {
    elements.loginError.textContent = message;
    elements.loginError.style.display = 'block';
}

function hideLoginError() {
    elements.loginError.style.display = 'none';
}

// API Functions
class ApiClient {
    static async request(endpoint, options = {}) {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = AuthManager.getToken();

        const headers = {
            ...(token && { 'Authorization': `Bearer ${token}` }),
            ...options.headers
        };

        // Don't set Content-Type for FormData (file uploads) - let browser set it
        if (!(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const config = {
            headers,
            ...options
        };
        
        console.log(`[ApiClient] Making request to: ${url}`);
        console.log('[ApiClient] Request config:', {
            method: config.method || 'GET',
            headers: config.headers,
            bodyType: config.body ? (config.body instanceof FormData ? 'FormData' : typeof config.body) : 'none'
        });
        
        try {
            console.log('[ApiClient] Calling fetch...');
            const response = await fetch(url, config);
            console.log('[ApiClient] Fetch completed, response status:', response.status);
            console.info("HTTP response --- " + response.status)
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Unauthorized (401) or Forbidden (403) - redirect to login
                    AuthManager.logout('Your session expired. Please log in again.');
                    return;
                }
                const errorText = await response.text();
                let errorMessage;
                try {
                    // Try to parse JSON error response
                    const errorData = JSON.parse(errorText);
                    errorMessage = errorData.error || errorData.message || errorText;
                } catch {
                    // If not JSON, use the text as-is
                    errorMessage = errorText;
                }
                const err = new Error(`HTTP ${response.status}: ${errorMessage}`);
                err.apiMessage = errorMessage;
                throw err;
            }
            
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            } else {
                return await response.text();
            }
        } catch (error) {
            console.error('[ApiClient] Request failed at:', error.name);
            console.error('[ApiClient] Error message:', error.message);
            console.error('[ApiClient] Full error:', error);
            throw error;
        }
    }

    static async get(endpoint) {
        return this.request(endpoint, { method: 'GET' });
    }

    static async post(endpoint, data) {
        return this.request(endpoint, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    static async put(endpoint, data) {
        return this.request(endpoint, {
            method: 'PUT',
            body: JSON.stringify(data)
        });
    }

    static async patch(endpoint, data) {
        return this.request(endpoint, {
            method: 'PATCH',
            body: JSON.stringify(data)
        });
    }

    static async delete(endpoint) {
        return this.request(endpoint, { method: 'DELETE' });
    }

    static async uploadFile(endpoint, formData) {
        console.log(`[ApiClient] uploadFile called with endpoint: ${endpoint}`);
        console.log('[ApiClient] FormData contents:');
        for (let [key, value] of formData.entries()) {
            console.log(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
        }
        
        // Direct request without AbortController to avoid method confusion
        return this.request(endpoint, {
            method: 'POST',
            body: formData
        });
    }

    static async postBlob(endpoint, data) {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = AuthManager.getToken();
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` }),
            },
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            if (response.status === 401 || response.status === 403) { AuthManager.logout('Your session expired. Please log in again.'); return; }
            const text = await response.text();
            throw new Error(`HTTP ${response.status}: ${text}`);
        }
        return response.blob();
    }

    static async downloadFile(endpoint, filename) {
        const url = `${API_BASE_URL}${endpoint}`;
        const token = AuthManager.getToken();

        const headers = {
            ...(token && { 'Authorization': `Bearer ${token}` })
        };

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Unauthorized (401) or Forbidden (403) - redirect to login
                    AuthManager.logout('Your session expired. Please log in again.');
                    return;
                }
                throw new Error(`HTTP ${response.status}: Failed to download file`);
            }

            // Get the blob from response
            const blob = await response.blob();

            // Get filename from Content-Disposition header if not provided
            if (!filename) {
                const contentDisposition = response.headers.get('Content-Disposition');
                if (contentDisposition) {
                    // Try to match filename="..." or filename=... formats
                    // This regex handles both quoted and unquoted filenames
                    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        // Remove quotes if present
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
            }

            // Fallback to a default filename if still not found
            if (!filename) {
                filename = 'download';
            }

            // Create a download link and trigger it
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the object URL
            setTimeout(() => window.URL.revokeObjectURL(downloadUrl), 100);

        } catch (error) {
            console.error('Download failed:', error);
            throw error;
        }
    }
}

// Utility Functions
function showToast(title, message, type = 'info', warningNote = '') {
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;

    // Convert newlines to <br> for multi-line messages (e.g., validation errors)
    const formattedMessage = escapeHtml(message).replace(/\n/g, '<br>');
    const warningHtml = warningNote ? `<div class="toast-warning">${escapeHtml(warningNote)}</div>` : '';

    toast.innerHTML = `
        <div class="toast-title">${escapeHtml(title)}</div>
        <div class="toast-message">${formattedMessage}</div>
        ${warningHtml}
    `;

    elements.toastContainer.appendChild(toast);

    // Show error/validation messages longer (10s) so users can read details
    const duration = type === 'error' ? 10000 : 5000;

    setTimeout(() => {
        toast.remove();
    }, duration);
}

function showLoading(show = true) {
    elements.loadingOverlay.style.display = show ? 'flex' : 'none';
}

function formatFileSize(bytes) {
    // Convert string to number if needed
    const numBytes = parseInt(bytes);
    if (!numBytes || numBytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatDate(dateString) {
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
}

const pathwayLabels = {
    new_predicate:    'New Product w/ Predicate',
    new_no_predicate: 'New Product — No Predicate',
    design_change:    'Design Change',
    capa:             'CAPA Iteration',
};

function renderProjectsList() {
    const favs = getFavoriteIds();
    const query = (elements.projectsSearch?.value || '').trim().toLowerCase();
    const isSystemAdmin = AuthManager.getUser()?.role === 'system_admin';

    let projects = _allProjects;
    if (query) {
        projects = projects.filter(p => p.name.toLowerCase().includes(query) || (p.description || '').toLowerCase().includes(query));
    }
    // Favorites first, then original server order
    projects = [
        ...projects.filter(p => favs.has(p.id)),
        ...projects.filter(p => !favs.has(p.id)),
    ];

    if (projects.length === 0) {
        elements.projectsList.innerHTML = `
            <div class="no-files">
                <i class="fas fa-folder-open" style="font-size: 3rem; color: #ddd; margin-bottom: 16px;"></i>
                <p>${query ? 'No projects match your filter.' : 'No projects yet. Create your first project above!'}</p>
            </div>
        `;
        return;
    }

    elements.projectsList.innerHTML = projects.map(project => {
        const isFav = favs.has(project.id);
        const pathwayLabel = pathwayLabels[project.regulatory_pathway] || project.regulatory_pathway || null;
        const envs = project.use_environments || [];
        const badgesHtml = (pathwayLabel || envs.length) ? `
            <div class="project-badges">
                ${pathwayLabel ? `<span class="badge badge-info">${escapeHtml(pathwayLabel)}</span>` : ''}
                ${envs.map(e => `<span class="badge badge-secondary">${escapeHtml(e)}</span>`).join('')}
            </div>` : '';
        const favBtn = `
            <button class="project-favorite-btn${isFav ? ' is-favorite' : ''}" title="${isFav ? 'Remove from favorites' : 'Add to favorites'}" onclick="event.stopPropagation(); toggleFavorite(${project.id})">
                <i class="fas fa-star"></i>
            </button>`;
        const editBtn = `
            <button class="project-edit-btn" title="Edit project" onclick="event.stopPropagation(); editProject(${project.id})">
                <i class="fas fa-edit"></i>
            </button>`;
        const deleteBtn = isSystemAdmin ? `
            <button class="project-delete-btn" title="Delete project" onclick="event.stopPropagation(); deleteProject(${project.id}, ${jsArg(project.name)})">
                <i class="fas fa-trash-alt"></i>
            </button>` : '';
        return `
        <div class="project-card${isFav ? ' is-favorite' : ''}" onclick="openProject(${project.id})">
            <div class="project-card-header">
                <h3>${highlightQuery(project.name, query)}</h3>
                <div class="project-card-actions">${favBtn}${editBtn}${deleteBtn}</div>
            </div>
            ${project.description ? `<p>${highlightQuery(project.description, query)}</p>` : ''}
            ${badgesHtml}
            <div class="project-meta">
                <span>Created: ${formatDate(project.created_at)}</span>
                <span class="files-count">${project.study_count} ${project.study_count === 1 ? 'study' : 'studies'}</span>
            </div>
        </div>`;
    }).join('');
}

// Project Functions
async function loadProjects() {
    try {
        elements.projectsList.innerHTML = '<div class="loading">Loading projects...</div>';

        const response = await ApiClient.get('/projects');
        _allProjects = response.projects || [];

        if (_allProjects.length === 0) {
            elements.projectsList.innerHTML = `
                <div class="no-files">
                    <i class="fas fa-folder-open" style="font-size: 3rem; color: #ddd; margin-bottom: 16px;"></i>
                    <p>No projects yet. Create your first project above!</p>
                </div>
            `;
            return;
        }

        renderProjectsList();

    } catch (error) {
        console.error('Error loading projects:', error);
        elements.projectsList.innerHTML = `
            <div class="error">
                <i class="fas fa-exclamation-triangle" style="color: #dc3545; margin-right: 8px;"></i>
                Failed to load projects. Please try again.
            </div>
        `;
        showToast('Error', 'Failed to load projects', 'error');
    }
}

async function deleteProject(projectId, projectName) {
    if (!confirm(`Are you sure you want to delete "${projectName}"?\n\nThis will permanently delete the project and all its studies, files, and data.`)) {
        return;
    }

    try {
        showLoading(true);
        await ApiClient.delete(`/projects/${projectId}`);
        showToast('Success', `Project "${projectName}" deleted successfully`, 'success');
        loadProjects();
    } catch (error) {
        console.error('Error deleting project:', error);
        showToast('Error', error.message || 'Failed to delete project', 'error');
    } finally {
        showLoading(false);
    }
}

const PROJECT_DOC_INPUT_MAP = [
    { inputId: 'projectDocUfmea',   docType: 'ufmea',                label: 'UFMEA' },
    { inputId: 'projectDocSysReq',  docType: 'system-requirements',  label: 'System Requirements' },
    { inputId: 'projectDocUserReq', docType: 'user-requirements',    label: 'User Requirements' },
];

function collectProjectDocsToUpload() {
    return PROJECT_DOC_INPUT_MAP
        .map(spec => {
            const input = document.getElementById(spec.inputId);
            const file = input?.files?.[0];
            return file ? { ...spec, file } : null;
        })
        .filter(Boolean);
}

async function uploadProjectDocument(projectId, spec) {
    const fd = new FormData();
    fd.append('file', spec.file);
    fd.append('docType', spec.docType);
    return ApiClient.uploadFile(`/projects/${projectId}/documents`, fd);
}

async function waitForProjectDocExtractions(projectId, docIds, { intervalMs = 3000, timeoutMs = 90000 } = {}) {
    if (!docIds.length) return [];
    const pending = new Set(docIds);
    const deadline = Date.now() + timeoutMs;
    let lastRows = [];
    while (pending.size && Date.now() < deadline) {
        await new Promise(r => setTimeout(r, intervalMs));
        try {
            lastRows = await ApiClient.get(`/projects/${projectId}/documents`);
        } catch (err) {
            console.warn('[ProjectDocs] poll failed; will retry', err);
            continue;
        }
        for (const row of lastRows) {
            if (pending.has(row.id) && row.extraction_status !== 'pending') {
                pending.delete(row.id);
            }
        }
    }
    return lastRows;
}

// The project form's primary button does double duty: create a new project or
// save edits to an existing one, depending on the current mode.
function submitProjectForm() {
    if (editingProjectId) return saveProjectEdit();
    return createProject();
}

// Returns the original clearance payload when the pathway requires it, or null
// if validation fails (shows inline errors). Returns {} when not required.
function collectOriginalClearanceFields(regulatory_pathway) {
    const requiresClearance = regulatory_pathway === 'design_change' || regulatory_pathway === 'capa';
    if (!requiresClearance) return {};

    const original_regulatory_pathway = elements.originalRegPathway.value;
    const original_device_name = elements.originalDeviceName.value.trim();
    const original_clearance_number = elements.originalClearanceNumber.value.trim();
    const original_clearance_date = elements.originalClearanceDate.value;

    let valid = true;
    const setErr = (id, msg) => {
        const el = document.getElementById(id);
        el.textContent = msg;
        el.style.display = msg ? 'block' : 'none';
        if (msg) valid = false;
    };

    setErr('originalRegPathwayError', original_regulatory_pathway ? '' : 'Required');
    setErr('originalDeviceNameError', original_device_name ? '' : 'Required');
    setErr('originalClearanceDateError', original_clearance_date ? '' : 'Required');

    const clearanceFormats = {
        '510k':    { re: /^K\d{6}$/,                   hint: 'e.g. K243785' },
        pma:       { re: /^P\d{6}(\/[SA]\d{3})?$/i,    hint: 'e.g. P160013 or P160013/S051' },
        de_novo:   { re: /^DEN\d{6}$/i,                 hint: 'e.g. DEN180042' },
    };
    const fmt = clearanceFormats[original_regulatory_pathway];
    if (!original_clearance_number) {
        setErr('originalClearanceNumberError', 'Required');
    } else if (fmt && !fmt.re.test(original_clearance_number)) {
        setErr('originalClearanceNumberError', `Invalid format - ${fmt.hint}`);
    } else {
        setErr('originalClearanceNumberError', '');
    }

    if (!valid) return null;
    return { original_regulatory_pathway, original_device_name, original_clearance_number, original_clearance_date };
}

async function createProject() {
    const name = elements.projectName.value.trim();
    const description = elements.projectDescription.value.trim();
    const regPathRadio = document.querySelector('input[name="projectRegPath"]:checked');
    const regulatory_pathway = regPathRadio ? regPathRadio.value : null;
    const use_environments = [...document.querySelectorAll('#projectEnvGrid input:checked')].map(c => c.value);

    if (!name) {
        showToast('Error', 'Project name is required', 'error');
        elements.projectName.focus();
        return;
    }

    if (!regulatory_pathway) {
        const errEl = document.getElementById('projectRegPathError');
        errEl.textContent = 'Please select a regulatory pathway.';
        errEl.style.display = 'block';
        return;
    }
    document.getElementById('projectRegPathError').style.display = 'none';

    const clearanceFields = collectOriginalClearanceFields(regulatory_pathway);
    if (clearanceFields === null) return;

    const docsToUpload = collectProjectDocsToUpload();
    const hasDocs = docsToUpload.length > 0;

    // Rebuild the progress bar each run so the step list reflects whether
    // documents need to be uploaded/extracted.
    const steps = hasDocs
        ? ['Validating', 'Creating', 'Uploading documents', 'Extracting', 'Complete']
        : ['Validating', 'Creating', 'Finalizing', 'Complete'];
    createProjectProgressBar = new ProgressBar(elements.createProjectProgress, {
        message: 'Creating project...',
        showPercentage: true,
        showSteps: true,
        steps,
    });

    createProjectProgressBar.reset();
    createProjectProgressBar.show();
    elements.createProjectBtn.disabled = true;

    try {
        // Step 1: Validating
        createProjectProgressBar.nextStep('Validating project details...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 2: Creating
        createProjectProgressBar.nextStep('Creating project...');
        const createResp = await ApiClient.post('/projects', {
            name,
            description,
            regulatory_pathway,
            use_environments,
            ...clearanceFields
        });
        const newProjectId = createResp?.project?.id;

        if (hasDocs && !newProjectId) {
            console.warn('[createProject] No project id returned; skipping document upload');
        }

        const uploadedDocIds = [];
        let ufmeaDocId = null;
        let ufmeaStillPending = false;

        if (hasDocs && newProjectId) {
            // Step 3: Uploading documents (one per file, non-fatal on failure)
            createProjectProgressBar.nextStep(`Uploading documents (0/${docsToUpload.length})...`);
            for (let i = 0; i < docsToUpload.length; i++) {
                const spec = docsToUpload[i];
                createProjectProgressBar.updateProgress(
                    40 + ((i / docsToUpload.length) * 20),
                    `Uploading ${spec.label} (${i + 1}/${docsToUpload.length})...`
                );
                try {
                    const row = await uploadProjectDocument(newProjectId, spec);
                    if (row?.id) {
                        uploadedDocIds.push(row.id);
                        if (spec.docType === 'ufmea') ufmeaDocId = row.id;
                    }
                } catch (err) {
                    console.error(`[createProject] Failed to upload ${spec.docType}`, err);
                    showToast(
                        `${spec.label} upload failed`,
                        err.apiMessage || 'Upload failed; the project was still created.',
                        'error'
                    );
                }
            }

            // Step 4: Extracting (poll until all uploaded docs settle)
            if (uploadedDocIds.length) {
                createProjectProgressBar.nextStep(
                    `Thank you for uploading your documents. I am getting onboarded to your project...`
                );
                const finalRows = await waitForProjectDocExtractions(newProjectId, uploadedDocIds);

                const ufmeaRow = ufmeaDocId ? finalRows.find(r => r.id === ufmeaDocId) : null;
                ufmeaStillPending = !!(ufmeaRow && ufmeaRow.extraction_status === 'pending');

                const failed = finalRows.filter(r =>
                    uploadedDocIds.includes(r.id) && r.extraction_status === 'failed'
                );
                const stillPending = finalRows.filter(r =>
                    uploadedDocIds.includes(r.id) && r.extraction_status === 'pending'
                );
                for (const r of failed) {
                    const spec = PROJECT_DOC_INPUT_MAP.find(s => s.docType === r.doc_type);
                    showToast(
                        `${spec?.label || r.doc_type} extraction failed`,
                        r.extraction_error || 'You can retry from the project page.',
                        'warning'
                    );
                }
                if (stillPending.length) {
                    showToast(
                        'Extraction still running',
                        `${stillPending.length} document(s) are still being processed in the background.`,
                        'info'
                    );
                }
            }
        } else {
            // Step 3: Finalizing (no docs path)
            createProjectProgressBar.nextStep('Finalizing setup...');
            await new Promise(resolve => setTimeout(resolve, 500));
        }

        // Final step: Complete
        createProjectProgressBar.complete('Project created successfully!');

        showToast('Success', 'Project created successfully', 'success');

        // Clear form and collapse
        resetProjectForm();
        document.getElementById('projectFormSection').style.display = 'none';

        // Reload projects
        await loadProjects();

        // The backend flags a description conflict on the project row when the
        // UFMEA extraction disagrees with a typed description. If extraction
        // finished within the wait, open the project so the banner shows now;
        // if it's still running, keep watching in the background (the durable
        // flag also re-surfaces the banner next time anyone opens the project).
        if (ufmeaDocId && newProjectId) {
            if (ufmeaStillPending) {
                watchUfmeaConflictInBackground(newProjectId, ufmeaDocId);
            } else {
                try {
                    const resp = await ApiClient.get(`/projects/${newProjectId}`);
                    if (resp?.project?.pending_extracted_description) {
                        await openProject(newProjectId);
                    }
                } catch (err) {
                    console.warn('[createProject] conflict check failed', err);
                }
            }
        }

    } catch (error) {
        console.error('Error creating project:', error);
        createProjectProgressBar.updateProgress(0, 'Project creation failed');
        createProjectProgressBar.hide();
        showToast('Error', error.apiMessage || 'Failed to create project', 'error');
    } finally {
        elements.createProjectBtn.disabled = false;
    }
}

async function openProject(projectId) {
    try {
        showLoading(true);

        const response = await ApiClient.get(`/projects/${projectId}`);
        const project = response.project || {};
        currentProject = project;
        currentStudy   = null;
        currentInvocation = null;

        // Update project header
        elements.projectNameTitle.textContent = project.name;
        elements.projectDescriptionContent.textContent = project.description || '';

        // Surface any unresolved UFMEA description conflict; drop a banner
        // left over from a previously viewed project. Not awaited — the
        // banner waits for a click and must not hold the loading spinner.
        document.getElementById('ufmeaDescReconcileBanner')?.remove();
        maybeShowDescriptionConflictBanner(project);

        // Load studies for this project
        await loadStudies(projectId);

        switchView('projectDetails');
        window.location.hash = `#project/${projectId}`;

    } catch (error) {
        console.error('Error loading project:', error);
        showToast('Error', 'Failed to load project details', 'error');
    } finally {
        showLoading(false);
    }
}


// ==================== EDIT PROJECT ====================

// Fetch the project and switch the main-view "Create New Project" form into
// edit mode, re-using the same fields. Triggered by the pencil on a project card.
async function editProject(projectId) {
    try {
        showLoading(true);
        const resp = await ApiClient.get(`/projects/${projectId}`);
        const project = resp?.project;
        if (!project) throw new Error('Project not found');
        enterProjectEditMode(project);
    } catch (err) {
        console.error('[editProject] failed', err);
        showToast('Error', 'Failed to load project for editing', 'error');
    } finally {
        showLoading(false);
    }
}

function enterProjectEditMode(project) {
    editingProjectId = project.id;

    elements.projectName.value = project.name || '';
    elements.projectDescription.value = project.description || '';

    document.querySelectorAll('input[name="projectRegPath"]').forEach(r => {
        r.checked = r.value === project.regulatory_pathway;
    });
    document.getElementById('projectRegPathError').style.display = 'none';

    const needsClearance = project.regulatory_pathway === 'design_change' || project.regulatory_pathway === 'capa';
    elements.originalClearanceSection.style.display = needsClearance ? '' : 'none';
    if (needsClearance) {
        elements.originalRegPathway.value = project.original_regulatory_pathway || '';
        elements.originalDeviceName.value = project.original_device_name || '';
        elements.originalClearanceNumber.value = project.original_clearance_number || '';
        elements.originalClearanceDate.value = project.original_clearance_date
            ? project.original_clearance_date.split('T')[0] : '';
    }

    const envs = project.use_environments || [];
    document.querySelectorAll('#projectEnvGrid input[type="checkbox"]').forEach(cb => {
        cb.checked = envs.includes(cb.value);
    });

    // Swap the form chrome into edit mode.
    document.getElementById('projectFormHeading').textContent = 'Edit Project';
    elements.createProjectBtn.innerHTML = '<i class="fas fa-save"></i> Save Changes';
    document.getElementById('cancelProjectEditBtn').style.display = '';
    if (elements.createProjectProgress) elements.createProjectProgress.style.display = 'none';

    // Show the project's existing documents in the doc cards.
    loadProjectDocsIntoForm(project.id);

    // Surface any unresolved UFMEA description conflict inline in the form.
    document.getElementById('ufmeaDescReconcileBanner')?.remove();
    maybeShowDescriptionConflictBanner(project);

    document.getElementById('projectFormSection').style.display = '';
    document.getElementById('projectFormSection').scrollIntoView({ behavior: 'smooth', block: 'start' });
    elements.projectName.focus();
}

function exitProjectEditMode() {
    editingProjectId = null;
    document.getElementById('ufmeaDescReconcileBanner')?.remove();
    resetProjectForm();
    document.getElementById('projectFormHeading').textContent = 'Create New Project';
    elements.createProjectBtn.innerHTML = '<i class="fas fa-plus"></i> Create Project';
    document.getElementById('cancelProjectEditBtn').style.display = 'none';
    document.getElementById('projectFormSection').style.display = 'none';
}

// Clears every field in the project form and resets the document cards. Shared
// by create-success cleanup and exiting edit mode.
function resetProjectForm() {
    elements.projectName.value = '';
    elements.projectDescription.value = '';
    const checkedRegPath = document.querySelector('input[name="projectRegPath"]:checked');
    if (checkedRegPath) checkedRegPath.checked = false;
    document.getElementById('projectRegPathError').style.display = 'none';
    elements.originalClearanceSection.style.display = 'none';
    elements.originalRegPathway.value = '';
    elements.originalDeviceName.value = '';
    elements.originalClearanceNumber.value = '';
    elements.originalClearanceDate.value = '';
    ['originalRegPathwayError', 'originalDeviceNameError', 'originalClearanceNumberError', 'originalClearanceDateError']
        .forEach(id => { document.getElementById(id).style.display = 'none'; });
    document.querySelectorAll('#projectEnvGrid input').forEach(c => { c.checked = false; });
    EDIT_PROJECT_DOC_TYPES.forEach(t => renderProjectDocCardStatus(t, null));
    document.querySelectorAll('.project-doc-input').forEach(input => { input.value = ''; });
}

const EDIT_PROJECT_DOC_TYPES = ['ufmea', 'system-requirements', 'user-requirements'];

// Locate the doc-card DOM for a given doc type using the create-form inputs.
function docCardElementsForType(docType) {
    const spec = PROJECT_DOC_INPUT_MAP.find(s => s.docType === docType);
    const input = spec ? document.getElementById(spec.inputId) : null;
    const card = input?.closest('.project-doc-card');
    return {
        card,
        filenameEl: card?.querySelector('.project-doc-filename'),
        badgeEl: card?.querySelector('.project-doc-badge'),
    };
}

function renderProjectDocCardStatus(docType, row) {
    const { card, filenameEl, badgeEl } = docCardElementsForType(docType);
    if (!card || !filenameEl || !badgeEl) return;

    // Remove any stale eye button before re-rendering.
    card.querySelector('.project-doc-eye-btn')?.remove();

    if (!row) {
        card.classList.remove('has-file');
        filenameEl.textContent = '';
        badgeEl.textContent = 'Optional';
        return;
    }

    card.classList.add('has-file');
    filenameEl.textContent = row.filename || '(uploaded)';
    const status = row.extraction_status;
    if (status === 'pending') badgeEl.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Extracting…';
    else if (status === 'success') badgeEl.textContent = '✓ Extracted';
    else if (status === 'failed')  badgeEl.textContent = '✗ Extraction failed';
    else                            badgeEl.textContent = 'Uploaded';

    if (AuthManager.getUser()?.role === 'system_admin' && status === 'success' && row.extracted_data) {
        const eyeBtn = document.createElement('button');
        eyeBtn.type = 'button';
        eyeBtn.className = 'project-doc-eye-btn';
        eyeBtn.title = 'View extracted data';
        eyeBtn.innerHTML = '<i class="fas fa-eye"></i>';
        eyeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            e.preventDefault();
            showExtractedDataModal(docType, row.extracted_data);
        });
        card.appendChild(eyeBtn);
    }
}

const DOC_TYPE_LABELS = {
    'ufmea': 'UFMEA',
    'system-requirements': 'SRS (System Requirements)',
    'user-requirements': 'URS (User Requirements)',
    'regulatory-snapshot': 'Regulatory Snapshot (DC analysis)',
};

function showExtractedDataModal(docType, data) {
    const label = DOC_TYPE_LABELS[docType] || docType;
    document.getElementById('extractedDataModalTitle').textContent = `Extracted Data — ${label}`;
    document.getElementById('extractedDataJson').textContent = JSON.stringify(data, null, 2);
    document.getElementById('extractedDataModal').style.display = 'block';
}

function closeExtractedDataModal() {
    document.getElementById('extractedDataModal').style.display = 'none';
}

async function loadProjectDocsIntoForm(projectId) {
    EDIT_PROJECT_DOC_TYPES.forEach(t => renderProjectDocCardStatus(t, null));
    try {
        const rows = await ApiClient.get(`/projects/${projectId}/documents`);
        const byType = new Map(rows.map(r => [r.doc_type, r]));
        EDIT_PROJECT_DOC_TYPES.forEach(t => renderProjectDocCardStatus(t, byType.get(t) || null));
    } catch (err) {
        console.error('[loadProjectDocsIntoForm] failed', err);
    }
}

// Change handler for the project-form doc inputs. In create mode it just
// reflects the chosen filename; in edit mode it uploads immediately.
function handleProjectDocInputChange(ev) {
    if (editingProjectId) {
        handleEditProjectDocUpload(ev);
        return;
    }
    const card = ev.target.closest('.project-doc-card');
    const label = card?.querySelector('.project-doc-filename');
    const file = ev.target.files?.[0];
    if (file) {
        if (label) label.textContent = file.name;
        card?.classList.add('has-file');
    } else {
        if (label) label.textContent = '';
        card?.classList.remove('has-file');
    }
}

async function handleEditProjectDocUpload(ev) {
    const input = ev.target;
    const spec = PROJECT_DOC_INPUT_MAP.find(s => s.inputId === input.id);
    const docType = spec?.docType;
    const file = input.files?.[0];
    if (!file || !docType || !editingProjectId) return;

    const { badgeEl } = docCardElementsForType(docType);
    const prevBadge = badgeEl?.innerHTML;
    if (badgeEl) badgeEl.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Uploading…';

    try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('docType', docType);
        const row = await ApiClient.uploadFile(`/projects/${editingProjectId}/documents`, fd);
        renderProjectDocCardStatus(docType, row);
        showToast('Uploaded', `${file.name} uploaded; extraction running in background.`, 'success');
        // Poll for the extraction to finish, then refresh + (UFMEA only) reconcile description.
        pollExtractionAndReconcile(editingProjectId, row.id, docType).catch((err) => {
            console.warn('[handleEditProjectDocUpload] poll/reconcile failed', err);
        });
    } catch (err) {
        console.error('[handleEditProjectDocUpload] failed', err);
        if (badgeEl) badgeEl.innerHTML = prevBadge || 'Optional';
        showToast('Upload failed', err.apiMessage || err.message || 'Upload failed', 'error');
    } finally {
        input.value = '';
    }
}

// After upload, watch the document row until extraction settles, refresh the
// edit-form doc cards, and — for UFMEA only — reconcile the extracted device
// description with the project's existing description.
async function pollExtractionAndReconcile(projectId, docId, docType, { intervalMs = 3000, timeoutMs = 90000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    let row = null;

    while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, intervalMs));
        let rows;
        try {
            rows = await ApiClient.get(`/projects/${projectId}/documents`);
        } catch (err) {
            console.warn('[pollExtractionAndReconcile] poll failed; will retry', err);
            continue;
        }
        // Refresh edit cards on each poll so the badge reflects the latest status.
        if (editingProjectId === projectId) {
            const byType = new Map(rows.map((r) => [r.doc_type, r]));
            EDIT_PROJECT_DOC_TYPES.forEach((t) => renderProjectDocCardStatus(t, byType.get(t) || null));
        }
        row = rows.find((r) => r.id === docId);
        if (row && row.extraction_status !== 'pending') break;
    }

    if (docType !== 'ufmea' || !row || row.extraction_status !== 'success') return;
    await reconcileExtractedDeviceDescription(projectId);
}

// After a UFMEA extraction settles, re-fetch the project: the backend either
// auto-populated an empty description (sync the UI) or recorded a conflict in
// pending_extracted_description (surface the reconcile banner).
async function reconcileExtractedDeviceDescription(projectId) {
    let project;
    try {
        const resp = await ApiClient.get(`/projects/${projectId}`);
        project = resp?.project;
    } catch (err) {
        console.warn('[reconcileExtractedDeviceDescription] failed to fetch project', err);
        return;
    }
    if (!project) return;

    syncProjectDescriptionUI(project);
    await maybeShowDescriptionConflictBanner(project);
}

// Reflect a fresh project row in whichever surface currently shows its
// description (project view header and/or the edit form).
function syncProjectDescriptionUI(project) {
    if (currentProject?.id === project.id) {
        currentProject = project;
        if (elements?.projectDescriptionContent) {
            elements.projectDescriptionContent.textContent = project.description || '';
        }
    }
    if (editingProjectId === project.id && project.description) {
        elements.projectDescription.value = project.description;
    }
}

// If the project carries an unresolved UFMEA description conflict, ask the
// user which description to keep. Both choices are persisted through
// POST /projects/:id/description-conflict, which clears the flag — so the
// question is asked exactly once no matter who answers it or when.
async function maybeShowDescriptionConflictBanner(project) {
    const projectId = project?.id;
    const extracted = (project?.pending_extracted_description || '').trim();
    if (!projectId || !extracted) return;

    // Only prompt while the user is editing or viewing this project; the
    // durable flag re-surfaces the banner on the next open otherwise.
    if (editingProjectId !== projectId && currentProject?.id !== projectId) return;

    const current = (project.description || '').trim();
    const useExtracted = await promptUfmeaDescriptionChoice(current, extracted);

    try {
        const resp = await ApiClient.post(`/projects/${projectId}/description-conflict`, {
            resolution: useExtracted ? 'use_extracted' : 'keep_current',
        });
        const updated = resp?.project;
        if (updated) syncProjectDescriptionUI(updated);
        if (useExtracted) {
            showToast('Description updated', 'Project description replaced with the UFMEA-extracted version.', 'success');
        }
    } catch (err) {
        console.error('[maybeShowDescriptionConflictBanner] resolve failed', err);
        showToast('Update failed', err.apiMessage || err.message || 'Could not resolve description conflict.', 'error');
    }
}

// Fire-and-forget watcher for a UFMEA extraction that outlived the create
// flow's progress bar. When it settles, shows the reconcile banner if the
// user is still on the project; if they navigated away, the backend flag
// brings the banner back on the next open.
async function watchUfmeaConflictInBackground(projectId, docId, { intervalMs = 5000, timeoutMs = 600000 } = {}) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, intervalMs));
        let rows;
        try {
            rows = await ApiClient.get(`/projects/${projectId}/documents`);
        } catch (err) {
            console.warn('[watchUfmeaConflictInBackground] poll failed; will retry', err);
            continue;
        }
        const row = rows.find(r => r.id === docId);
        if (!row) return; // superseded or deleted
        if (row.extraction_status === 'pending') continue;
        if (row.extraction_status === 'success') {
            await reconcileExtractedDeviceDescription(projectId);
        }
        return;
    }
}

// Inline replacement for confirm(). Inserts a banner right after the project
// description card and resolves with true (use extracted) or false (keep current).
// While editing on the main view, anchors to the form's description field.
function promptUfmeaDescriptionChoice(current, extracted) {
    return new Promise((resolve) => {
        const anchor = (editingProjectId
                ? elements?.projectDescription?.closest('.form-group')
                : null)
            || elements?.projectDescriptionContent
            || document.getElementById('projectDescriptionContent');
        if (!anchor) { resolve(false); return; }

        document.getElementById('ufmeaDescReconcileBanner')?.remove();

        const banner = document.createElement('div');
        banner.id = 'ufmeaDescReconcileBanner';
        banner.className = 'ufmea-desc-reconcile';
        banner.innerHTML = `
            <div class="ufmea-desc-reconcile-header">
                <i class="fas fa-file-import"></i>
                <strong>UFMEA extracted a different device description</strong>
            </div>
            <div class="ufmea-desc-reconcile-grid">
                <div class="ufmea-desc-reconcile-col">
                    <div class="ufmea-desc-reconcile-label">Current project description</div>
                    <div class="ufmea-desc-reconcile-text"></div>
                </div>
                <div class="ufmea-desc-reconcile-col">
                    <div class="ufmea-desc-reconcile-label">Extracted from UFMEA</div>
                    <div class="ufmea-desc-reconcile-text"></div>
                </div>
            </div>
            <div class="ufmea-desc-reconcile-actions">
                <button type="button" class="btn btn-outline btn-small" data-action="keep">Keep current</button>
                <button type="button" class="btn btn-primary btn-small" data-action="use">Use extracted</button>
            </div>
        `;
        const cols = banner.querySelectorAll('.ufmea-desc-reconcile-text');
        cols[0].textContent = current;
        cols[1].textContent = extracted;
        anchor.insertAdjacentElement('afterend', banner);

        const finish = (useExtracted) => {
            banner.remove();
            resolve(useExtracted);
        };
        banner.querySelector('[data-action="use"]').addEventListener('click', () => finish(true));
        banner.querySelector('[data-action="keep"]').addEventListener('click', () => finish(false));
    });
}

async function saveProjectEdit() {
    if (!editingProjectId) return;

    const projectId = editingProjectId;
    const name = elements.projectName.value.trim();
    const description = elements.projectDescription.value.trim();
    const regPathRadio = document.querySelector('input[name="projectRegPath"]:checked');
    const regulatory_pathway = regPathRadio ? regPathRadio.value : null;
    const use_environments = [...document.querySelectorAll('#projectEnvGrid input:checked')].map(cb => cb.value);
    const pathwayErrEl = document.getElementById('projectRegPathError');
    pathwayErrEl.style.display = 'none';

    if (!name) {
        showToast('Error', 'Project name is required', 'error');
        elements.projectName.focus();
        return;
    }
    if (!regulatory_pathway) {
        pathwayErrEl.textContent = 'Please select a regulatory pathway.';
        pathwayErrEl.style.display = 'block';
        return;
    }

    const clearanceFields = collectOriginalClearanceFields(regulatory_pathway);
    if (clearanceFields === null) return;

    const btn = elements.createProjectBtn;
    btn.disabled = true;
    try {
        const response = await ApiClient.put(`/projects/${projectId}`, {
            name, description, regulatory_pathway, use_environments, ...clearanceFields
        });
        const updated = response.project;
        // Keep the project-details header in sync if it's the same project.
        if (updated && currentProject?.id === projectId) {
            currentProject = updated;
            elements.projectNameTitle.textContent = updated.name;
            elements.projectDescriptionContent.textContent = updated.description || '';
        }
        exitProjectEditMode();
        showToast('Success', 'Project updated', 'success');
        loadProjects();
    } catch (err) {
        showToast('Error', err.apiMessage || err.message || 'Failed to save changes.', 'error');
    } finally {
        btn.disabled = false;
    }
}

// ==================== EDIT STUDY ====================

function openEditStudyForm() {
    if (!currentStudy) return;
    document.getElementById('editStudyName').value = currentStudy.name || '';
    document.getElementById('editStudyError').style.display = 'none';
    document.getElementById('editStudyForm').style.display = 'block';
    document.getElementById('editStudyBtn').style.display = 'none';
    document.getElementById('editStudyName').focus();
}

function closeEditStudyForm() {
    document.getElementById('editStudyForm').style.display = 'none';
    document.getElementById('editStudyBtn').style.display = '';
}

async function saveStudyEdit() {
    const name = document.getElementById('editStudyName').value.trim();
    const errEl = document.getElementById('editStudyError');
    errEl.style.display = 'none';

    if (!name) { errEl.textContent = 'Study name is required.'; errEl.style.display = 'block'; return; }

    const btn = document.getElementById('saveEditStudyBtn');
    btn.disabled = true;
    try {
        const response = await ApiClient.put(`/studies/${currentStudy.id}`, { name });
        currentStudy = response.study || { ...currentStudy, name };
        document.getElementById('studyNameTitle').innerHTML = `<i class="fas fa-microscope"></i> ${escapeHtml(currentStudy.name)}`;
        closeEditStudyForm();
        showToast('Success', 'Study updated', 'success');
        loadStudies(currentProject.id);
    } catch (err) {
        errEl.textContent = err.message || 'Failed to save changes.';
        errEl.style.display = 'block';
    } finally {
        btn.disabled = false;
    }
}

// ==================== STUDY MANAGEMENT ====================

async function loadStudies(projectId) {
    const container = document.getElementById('studiesList');
    if (!container) return;
    container.innerHTML = '<div class="loading">Loading studies...</div>';
    try {
        const response = await ApiClient.get(`/projects/${projectId}/studies`);
        displayStudies(response.studies || []);
    } catch (err) {
        console.error('Error loading studies:', err);
        container.innerHTML = '<div class="error">Failed to load studies.</div>';
    }
}

function displayStudies(studies) {
    const container = document.getElementById('studiesList');
    if (!container) return;

    if (studies.length === 0) {
        container.innerHTML = `
            <div class="no-files">
                <i class="fas fa-microscope" style="font-size:3rem;color:#ddd;margin-bottom:16px;"></i>
                <p>No studies yet. Click <strong>New Study</strong> to create one.</p>
            </div>`;
        return;
    }

    const isSystemAdmin = AuthManager.getUser()?.role === 'system_admin';
    container.innerHTML = studies.map(study => {
        const deleteBtn = isSystemAdmin ? `
            <button class="project-delete-btn" title="Delete study" onclick="event.stopPropagation(); deleteStudy(${study.id}, ${jsArg(study.name)})">
                <i class="fas fa-trash-alt"></i>
            </button>` : '';
        return `
        <div class="project-card" onclick="openStudy(${study.id})">
            <div class="project-card-header">
                <h3><i class="fas fa-microscope" style="margin-right:8px;"></i>${escapeHtml(study.name)}</h3>
                ${deleteBtn}
            </div>
            <div class="project-meta">
                <span>Created: ${formatDate(study.created_at)}</span>
            </div>
        </div>`;
    }).join('');
}

async function createStudy() {
    const nameInput = document.getElementById('studyNameInput');
    const errEl     = document.getElementById('addStudyError');
    const name      = nameInput ? nameInput.value.trim() : '';

    if (!name) {
        if (errEl) { errEl.textContent = 'Study name is required.'; errEl.style.display = 'block'; }
        return;
    }
    if (errEl) errEl.style.display = 'none';

    try {
        const btn = document.getElementById('saveStudyBtn');
        if (btn) btn.disabled = true;

        await ApiClient.post(`/projects/${currentProject.id}/studies`, { name });

        // Reset and hide the form, reload studies
        if (nameInput) nameInput.value = '';
        document.getElementById('addStudyForm').style.display = 'none';
        await loadStudies(currentProject.id);
        showToast('Success', 'Study created', 'success');
    } catch (err) {
        console.error('Error creating study:', err);
        if (errEl) { errEl.textContent = err.message || 'Failed to create study.'; errEl.style.display = 'block'; }
    } finally {
        const btn = document.getElementById('saveStudyBtn');
        if (btn) btn.disabled = false;
    }
}

async function deleteStudy(studyId, studyName) {
    if (!confirm(`Are you sure you want to delete "${studyName}"?\n\nThis will permanently delete the study and all its files and data.`)) {
        return;
    }

    try {
        showLoading(true);
        await ApiClient.delete(`/studies/${studyId}`);
        showToast('Success', `Study "${studyName}" deleted successfully`, 'success');
        await loadStudies(currentProject.id);
    } catch (error) {
        console.error('Error deleting study:', error);
        showToast('Error', error.message || 'Failed to delete study', 'error');
    } finally {
        showLoading(false);
    }
}

async function openStudy(studyId) {
    try {
        showLoading(true);

        const response = await ApiClient.get(`/studies/${studyId}`);
        currentStudy = response.study || { id: studyId };
        currentInvocation = null;

        const titleEl = document.getElementById('studyNameTitle');
        if (titleEl) titleEl.innerHTML = `<i class="fas fa-microscope"></i> ${escapeHtml(currentStudy.name)}`;

        await loadToolInvocations(studyId);

        switchView('studyDetail');
        window.location.hash = `#study/${studyId}`;

    } catch (err) {
        console.error('Error opening study:', err);
        showToast('Error', 'Failed to load study', 'error');
    } finally {
        showLoading(false);
    }
}

// ==================== TOOL INVOCATION MANAGEMENT ====================

async function loadToolInvocations(studyId) {
    const container = document.getElementById('invocationsList');
    if (!container) return;
    container.innerHTML = '<div class="loading">Loading tools...</div>';
    try {
        const response = await ApiClient.get(`/studies/${studyId}/tool-invocations`);
        displayToolInvocations(response.invocations || []);
    } catch (err) {
        console.error('Error loading tool invocations:', err);
        container.innerHTML = '<div class="error">Failed to load tools.</div>';
    }
}

const TOOL_LABELS = {
    hf_advisor:          { icon: '🔬', label: 'HF Advisor' },
    hf_usability_report: { icon: '📊', label: 'Usability Report' },
    design_change:       { icon: '🔄', label: 'Design Change' },
};

function displayToolInvocations(invocations) {
    const container = document.getElementById('invocationsList');
    if (!container) return;

    if (invocations.length === 0) {
        container.innerHTML = `
            <div class="no-files">
                <i class="fas fa-tools" style="font-size:3rem;color:#ddd;margin-bottom:16px;"></i>
                <p>No tool runs yet. Use the grid above to launch a tool.</p>
            </div>`;
        return;
    }

    const isSystemAdmin = AuthManager.getUser()?.role === 'system_admin';
    container.innerHTML = invocations.map(inv => {
        const meta = TOOL_LABELS[inv.tool_type] || { icon: '🔧', label: inv.tool_type };
        const statusBadge = getInvocationStatusBadge(inv);
        const deleteBtn = isSystemAdmin ? `
            <button class="project-delete-btn" title="Delete invocation" onclick="event.stopPropagation(); deleteInvocation(${inv.id}, ${jsArg(inv.name)})">
                <i class="fas fa-trash-alt"></i>
            </button>` : '';
        return `
            <div class="project-card invocation-card" onclick="openInvocation(${inv.id}, '${inv.tool_type}')">
                <div class="project-card-header">
                    <h3>${meta.icon} ${escapeHtml(inv.name)}</h3>
                    ${deleteBtn}
                </div>
                <div class="project-meta">
                    ${statusBadge}
                    <span>Created: ${formatDate(inv.created_at)}</span>
                </div>
            </div>`;
    }).join('');
}

function getInvocationStatusBadge(inv) {
    if (inv.tool_type === 'hf_advisor') {
        const status = inv.hf_status;
        if (!status) return '<span class="badge badge-neutral">Not started</span>';
        if (status === 'complete') return '<span class="badge badge-success">Complete</span>';
        if (status === 'running' || status === 'pending') return '<span class="badge badge-info">Running…</span>';
        if (status === 'error' && inv.hf_has_result) return '<span class="badge badge-success">Complete</span>';
        if (status === 'error') return '<span class="badge badge-error">Error</span>';
    }
    if (inv.tool_type === 'design_change') {
        const status = inv.dc_status;
        if (!status) return '<span class="badge badge-neutral">Not started</span>';
        if (status === 'complete') return '<span class="badge badge-success">Complete</span>';
    }
    if (inv.tool_type === 'hf_usability_report') {
        const status = inv.report_generation_status;
        if (!status || status === 'not_started') return '<span class="badge badge-neutral">Not started</span>';
        if (status === 'completed') return '<span class="badge badge-success">Complete</span>';
        if (status === 'processing') return '<span class="badge badge-info">Generating…</span>';
        if (status === 'failed') return '<span class="badge badge-error">Error</span>';
    }
    return '';
}

async function deleteInvocation(invocationId, invocationName) {
    if (!confirm(`Are you sure you want to delete "${invocationName}"?\n\nThis will permanently delete the tool run and all its data.`)) {
        return;
    }

    try {
        showLoading(true);
        await ApiClient.delete(`/tool-invocations/${invocationId}`);
        showToast('Success', `"${invocationName}" deleted successfully`, 'success');
        await loadToolInvocations(currentStudy.id);
    } catch (error) {
        console.error('Error deleting invocation:', error);
        showToast('Error', error.message || 'Failed to delete tool run', 'error');
    } finally {
        showLoading(false);
    }
}

async function openInvocation(invocationId, toolType) {
    if (toolType === 'hf_advisor') {
        // Set study context BEFORE init so back button picks it up
        hfState.studyId      = currentStudy?.id || null;
        hfState.invocationId = invocationId;

        switchView('hfAdvisor');
        window.location.hash = '#hf-advisor';
        hfAdvisorInit();

        // Load the invocation — the draft analysis is always linked at creation time
        try {
            const res = await ApiClient.get(`/tool-invocations/${invocationId}`);
            if (res.invocation?.name) {
                const el = document.getElementById('hfAdvisorNameTitle');
                if (el) el.textContent = res.invocation.name;
            }
            if (res.invocation?.hf_analysis_id) {
                hfLoadAnalysis(res.invocation.hf_analysis_id);
            } else {
                hfResetWizard();
            }
        } catch (err) {
            hfResetWizard();
        }
    } else if (toolType === 'design_change') {
        dcState.studyId           = currentStudy?.id || null;
        dcState.invocationId      = invocationId;
        dcState.currentAnalysisId = null;

        switchView('designChange');
        window.location.hash = '#dc-analyzer';
        dcAnalyzerInit();
        dcResetWizard();

        try {
            const res = await ApiClient.get(`/tool-invocations/${invocationId}`);
            if (dcState.invocationId !== invocationId) return; // stale fetch — user switched away
            const inv = res.invocation;
            if (inv?.name) {
                const el = document.getElementById('dcNameTitle');
                if (el) el.textContent = inv.name;
            }
            if (inv?.dc_analysis_id) dcState.currentAnalysisId = inv.dc_analysis_id;

            if (inv?.dc_status === 'complete' && inv?.dc_result) {
                const result = typeof inv.dc_result === 'string' ? JSON.parse(inv.dc_result) : inv.dc_result;
                await dcRenderResults(result);
                dcShowPage('results');
            } else if (inv?.dc_analysis_id && inv?.dc_status === 'pending') {
                const result = await dcPollAnalysis(inv.dc_analysis_id);
                if (dcState.invocationId !== invocationId) return;
                await dcRenderResults(result);
                dcShowPage('results');
            } else if (inv?.dc_analysis_id && inv?.dc_status === 'draft') {
                // Restore saved draft inputs from the DB record
                const { analysis } = await ApiClient.get(`/dc-analyzer/analyses/${inv.dc_analysis_id}`);
                if (dcState.invocationId !== invocationId) return;
                if (analysis) dcRestoreDraftFromRecord(analysis);
            }
        } catch (err) {
            console.error('Failed to load DC analysis:', err);
        }
    } else if (toolType === 'hf_usability_report') {
        await openUsabilityReportInvocation(invocationId);
    }
}

async function launchTool(toolType) {
    if (!currentStudy) return;
    try {
        showLoading(true);

        // Create the tool invocation
        const response = await ApiClient.post(`/studies/${currentStudy.id}/tool-invocations`, {
            tool_type: toolType,
        });
        const invocation = response.invocation;

        await openInvocation(invocation.id, toolType);

    } catch (err) {
        console.error('Error creating tool invocation:', err);
        showToast('Error', 'Failed to create tool invocation', 'error');
    } finally {
        showLoading(false);
    }
}

// Open an HF Usability Report invocation (files + categories + report generation)
async function openUsabilityReportInvocation(toolInvocationId) {
    try {
        showLoading(true);

        // Resolve the hf_usability_report_invocation_id from the tool invocation
        const toolInvRes = await ApiClient.get(`/tool-invocations/${toolInvocationId}`);
        const toolInv = toolInvRes.invocation;
        const reportInvId = toolInv?.hf_usability_report_invocation_id;
        if (!reportInvId) throw new Error('No usability report linked to this invocation');

        const response = await ApiClient.get(`/usability-reports/${reportInvId}`);
        const inv = response.invocation || { id: reportInvId };
        currentInvocation = { ...inv, toolInvocationId };

        const titleEl = document.getElementById('invocationNameTitle');
        if (titleEl) titleEl.innerHTML = `<i class="fas fa-file-alt"></i> ${escapeHtml(toolInv.name || 'Usability Report')}`;

        // Load files scoped to this invocation
        await loadInvocationFiles(reportInvId);

        // Load categories scoped to this invocation
        const questionCount = await loadInvocationCategories(reportInvId);

        // Display existing analyses
        displayExistingAnalysis(inv);
        displayExistingUsabilityReport(inv);

        // Reset CSV import state so stale preview data from a previous invocation is cleared
        resetCsvImport();
        const inlineCsvImport = document.getElementById('inlineCsvImport');
        if (inlineCsvImport) inlineCsvImport.style.display = 'none';

        // Default to Categories & Questions tab when no questions exist yet;
        // otherwise show Files & Reports first.
        const defaultTab = questionCount > 0 ? 'invocationFilesTab' : 'invocationCategoriesTab';
        document.querySelectorAll('.invocation-tab-btn').forEach(b => b.classList.toggle('active', b.dataset.tab === defaultTab));
        document.querySelectorAll('.invocation-tab-content').forEach(c => c.classList.toggle('active', c.id === defaultTab));

        switchView('usabilityReport');
        window.location.hash = `#invocation/${toolInvocationId}`;

    } catch (err) {
        console.error('Error opening usability report invocation:', err);
        showToast('Error', 'Failed to load usability report', 'error');
    } finally {
        showLoading(false);
    }
}

async function loadInvocationFiles(invocationId) {
    try {
        const response = await ApiClient.get(`/usability-reports/${invocationId}/files`);
        loadProjectFiles(response.files || []);
    } catch (err) {
        console.error('Error loading invocation files:', err);
    }
}

async function loadInvocationCategories(invocationId) {
    try {
        const response = await ApiClient.get(`/usability-reports/${invocationId}/categories`);
        const cats = response.categories || [];
        displayCategories(cats);
        const questionCount = await countQuestionsAcrossCategories(cats);
        updateUploadAvailability(questionCount > 0);
        return questionCount;
    } catch (err) {
        console.error('Error loading invocation categories:', err);
        updateUploadAvailability(false);
        return 0;
    }
}

async function countQuestionsAcrossCategories(categories) {
    if (!categories || categories.length === 0) return 0;
    const counts = await Promise.all(categories.map(async (c) => {
        try {
            const res = await ApiClient.get(`/categories/${c.id}/questions`);
            return (res.questions || []).length;
        } catch {
            return 0;
        }
    }));
    return counts.reduce((a, b) => a + b, 0);
}

// Enable/disable the file upload area based on whether any questions exist
function updateUploadAvailability(hasQuestions) {
    const uploadArea = elements.uploadArea;
    if (!uploadArea) return;
    uploadArea.classList.toggle('disabled', !hasQuestions);
    const hint = uploadArea.querySelector('.upload-disabled-hint');
    if (hint) hint.style.display = hasQuestions ? 'none' : 'block';
    const prompt = uploadArea.querySelector('.upload-prompt');
    if (prompt) prompt.style.display = hasQuestions ? 'block' : 'none';
}

// Admin Functions
function setupAdmin() {
    // Tab switching
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            const tabName = e.target.dataset.tab;
            switchAdminTab(tabName);
        });
    });

    // Add user button
    document.getElementById('addUserBtn')?.addEventListener('click', showAddUserModal);

    // User modal controls
    document.getElementById('closeUserModal')?.addEventListener('click', closeUserModal);
    document.getElementById('cancelUserBtn')?.addEventListener('click', closeUserModal);
    document.getElementById('userForm')?.addEventListener('submit', handleUserFormSubmit);
}

function switchAdminTab(tabName) {
    // Update tab buttons
    document.querySelectorAll('.tab-btn').forEach(btn => {
        btn.classList.toggle('active', btn.dataset.tab === tabName);
    });

    // Update tab content
    document.querySelectorAll('.tab-content').forEach(content => {
        content.classList.toggle('active', content.id === `${tabName}Tab`);
    });

    // Load tab content
    if (tabName === 'users') {
        loadAdminUsers();
    } else if (tabName === 'templates') {
        loadAdminTemplates();
    } else if (tabName === 'agentPrompts') {
        loadAgentPrompts();
    } else if (tabName === 'regulatoryRegistry') {
        loadRegulatoryRegistry();
    } else if (tabName === 'tenants') {
        loadAdminTenants();
    }
}

// User Management Functions
async function loadAdminUsers() {
    const container = document.getElementById('adminUsersList');

    try {
        container.innerHTML = '<div class="loading">Loading users...</div>';

        // Get the checkbox state
        const showInactive = document.getElementById('showInactiveUsers')?.checked || false;
        const queryParams = showInactive ? '?includeInactive=true' : '';

        const response = await ApiClient.get(`/admin/users${queryParams}`);
        const users = response.users || [];

        if (users.length === 0) {
            container.innerHTML = '<div class="no-files">No users available</div>';
            return;
        }

        const currentUserId = AuthManager.getUser().id;

        container.innerHTML = users.map(user => {
            const isCurrentUser = user.id === currentUserId;
            const statusBadge = user.active
                ? '<span class="badge badge-success">Active</span>'
                : '<span class="badge badge-danger">Inactive</span>';
            const roleBadge = user.role === 'system_admin'
                ? '<span class="badge badge-warning">System Admin</span>'
                : user.role === 'admin'
                ? '<span class="badge badge-primary">Admin</span>'
                : '<span class="badge badge-secondary">User</span>';
            const lastLogin = user.last_login
                ? formatDate(user.last_login)
                : 'Never';

            return `
                <div class="admin-item user-item">
                    <div class="admin-item-content">
                        <div class="admin-item-title">
                            <i class="fas fa-user"></i> ${escapeHtml(user.username)}
                            ${isCurrentUser ? '<span class="badge badge-info">You</span>' : ''}
                        </div>
                        <div class="admin-item-meta">
                            ${escapeHtml(user.email)} •
                            ${roleBadge} •
                            ${statusBadge} •
                            Last login: ${lastLogin}
                        </div>
                        <div class="admin-item-date">
                            Created: ${formatDate(user.created_at)}
                        </div>
                    </div>
                    <div class="admin-item-actions">
                        <button class="btn btn-small btn-outline" onclick="editUser(${user.id})">
                            <i class="fas fa-edit"></i> Edit
                        </button>
                        ${!isCurrentUser ? `
                            <button class="btn btn-small btn-danger" onclick="deleteUser(${user.id}, ${jsArg(user.username)})">
                                <i class="fas fa-trash"></i> Delete
                            </button>
                        ` : ''}
                    </div>
                </div>
            `;
        }).join('');

    } catch (error) {
        console.error('Error loading admin users:', error);
        container.innerHTML = '<div class="error">Failed to load users</div>';
        showToast('Error', 'Failed to load users', 'error');
    }
}

function showAddUserModal() {
    document.getElementById('userModalTitle').textContent = 'Add New User';
    document.getElementById('userId').value = '';
    document.getElementById('userUsername').value = '';
    document.getElementById('userEmail').value = '';
    document.getElementById('userPassword').value = '';
    document.getElementById('userPassword').required = true;
    document.getElementById('passwordGroup').style.display = 'block';
    document.getElementById('userRole').value = 'user';
    document.getElementById('userActive').checked = true;
    document.getElementById('userFormError').style.display = 'none';
    document.getElementById('userModal').style.display = 'block';
}

async function editUser(userId) {
    try {
        // Get all users (including inactive) and find the one we're editing
        const response = await ApiClient.get('/admin/users?includeInactive=true');
        const users = response.users || [];
        const user = users.find(u => u.id === userId);

        if (!user) {
            showToast('Error', 'User not found', 'error');
            return;
        }

        // Populate form
        document.getElementById('userModalTitle').textContent = 'Edit User';
        document.getElementById('userId').value = user.id;
        document.getElementById('userUsername').value = user.username;
        document.getElementById('userEmail').value = user.email;
        document.getElementById('userPassword').value = '';
        document.getElementById('userPassword').required = false;
        document.getElementById('passwordGroup').style.display = 'none'; // Hide password field for edit
        document.getElementById('userRole').value = user.role;
        document.getElementById('userActive').checked = user.active;
        document.getElementById('userFormError').style.display = 'none';
        document.getElementById('userModal').style.display = 'block';

    } catch (error) {
        console.error('Error loading user:', error);
        showToast('Error', 'Failed to load user details', 'error');
    }
}

function closeUserModal() {
    document.getElementById('userModal').style.display = 'none';
    document.getElementById('userForm').reset();
}

async function handleUserFormSubmit(e) {
    e.preventDefault();

    const userId = document.getElementById('userId').value;
    const username = document.getElementById('userUsername').value.trim();
    const email = document.getElementById('userEmail').value.trim();
    const password = document.getElementById('userPassword').value;
    const role = document.getElementById('userRole').value;
    const active = document.getElementById('userActive').checked;

    const errorDiv = document.getElementById('userFormError');
    errorDiv.style.display = 'none';

    // Validation
    if (!username || !email || !role) {
        errorDiv.textContent = 'Please fill in all required fields';
        errorDiv.style.display = 'block';
        return;
    }

    if (username.length < 3 || username.length > 50) {
        errorDiv.textContent = 'Username must be between 3 and 50 characters';
        errorDiv.style.display = 'block';
        return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        errorDiv.textContent = 'Username can only contain letters, numbers, hyphens, and underscores';
        errorDiv.style.display = 'block';
        return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        errorDiv.textContent = 'Please enter a valid email address';
        errorDiv.style.display = 'block';
        return;
    }

    // For new users, password is required
    if (!userId && (!password || password.length < 8)) {
        errorDiv.textContent = 'Password must be at least 8 characters long';
        errorDiv.style.display = 'block';
        return;
    }

    const saveBtn = document.getElementById('saveUserBtn');
    const originalBtnText = saveBtn.innerHTML;
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving...';

    try {
        if (userId) {
            // Update existing user
            const updateData = {
                username,
                email,
                role,
                active
            };

            await ApiClient.request(`/admin/users/${userId}`, {
                method: 'PUT',
                body: JSON.stringify(updateData)
            });

            showToast('Success', 'User updated successfully', 'success');
        } else {
            // Create new user
            const createData = {
                username,
                email,
                password,
                role,
                active
            };

            await ApiClient.post('/admin/users', createData);

            showToast('Success', 'User created successfully', 'success');
        }

        closeUserModal();
        loadAdminUsers();

    } catch (error) {
        console.error('Error saving user:', error);
        let errorMessage = error.message || 'Failed to save user';

        // Extract the actual error message from "HTTP 403: message" format
        if (errorMessage.includes('HTTP')) {
            const parts = errorMessage.split(':');
            if (parts.length > 1) {
                // Join all parts after the first colon (in case message contains colons)
                errorMessage = parts.slice(1).join(':').trim();
            }
        }

        errorDiv.textContent = errorMessage;
        errorDiv.style.display = 'block';
    } finally {
        saveBtn.disabled = false;
        saveBtn.innerHTML = originalBtnText;
    }
}

async function deleteUser(userId, username) {
    if (!confirm(`Are you sure you want to delete user "${username}"?\n\nThis will deactivate the user account.`)) {
        return;
    }

    try {
        showLoading(true);

        await ApiClient.delete(`/admin/users/${userId}`);

        showToast('Success', `User "${username}" deleted successfully`, 'success');
        loadAdminUsers();

    } catch (error) {
        console.error('Error deleting user:', error);
        const errorMessage = error.message || 'Failed to delete user';
        showToast('Error', errorMessage.includes('HTTP') ? errorMessage.split(':')[1].trim() : errorMessage, 'error');
    } finally {
        showLoading(false);
    }
}

async function loadAdminTemplates() {
    const container = document.getElementById('adminTemplatesList');

    try {
        container.innerHTML = '<div class="loading">Loading templates...</div>';

        const response = await ApiClient.get('/templates');
        const templates = response.templates || [];

        // Separate templates by type
        const usabilityTemplates = templates.filter(t => t.template_type === 'usability_report');
        const otherTemplates = templates.filter(t => t.template_type !== 'usability_report');

        let html = `
            <!-- Upload Form -->
            <div class="upload-template-section" style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
                <h3 style="margin-top: 0;">Upload Usability Report Template</h3>
                <form id="uploadTemplateForm" style="display: flex; gap: 10px; flex-wrap: wrap; align-items: end;">
                    <div style="flex: 1; min-width: 200px;">
                        <label for="templateName" style="display: block; margin-bottom: 5px; font-weight: 500;">Template Name</label>
                        <input type="text" id="templateName" name="name" required
                               placeholder="e.g., Usability Report Template"
                               style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                    <div style="flex: 1; min-width: 200px;">
                        <label for="templateFile" style="display: block; margin-bottom: 5px; font-weight: 500;">Select .docx File</label>
                        <input type="file" id="templateFile" name="file"
                               accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                               required
                               style="width: 100%; padding: 8px; border: 1px solid #ddd; border-radius: 4px;">
                    </div>
                    <button type="submit" class="btn btn-primary" style="margin-bottom: 0;">
                        <i class="fas fa-upload"></i> Upload Template
                    </button>
                </form>
                <small style="display: block; margin-top: 10px; color: #666;">
                    <i class="fas fa-info-circle"></i> Only .docx files are supported. Max file size: 5MB. Template will be validated on upload.
                </small>
            </div>

            <!-- Placeholder Help -->
            <div class="template-help-section" style="background: #e7f3ff; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
                <h4 style="margin-top: 0; color: #0066cc;">
                    <i class="fas fa-question-circle"></i> Template Placeholders Guide
                </h4>
                <p style="margin: 10px 0;">Use these placeholders in your Word document:</p>
                <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
                    <thead>
                        <tr style="background: #d0e7ff;">
                            <th style="padding: 8px; text-align: left; border: 1px solid #b3d9ff;">Placeholder</th>
                            <th style="padding: 8px; text-align: left; border: 1px solid #b3d9ff;">Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{project_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Project name</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{generated_date}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Report generation date</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{study_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Study name</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{generated_by}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Name of the user who generated the report</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{device_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Generic device name from the HF Advisor intake form</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{device_description}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Device description from the project</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{use_environment}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Use environments from the project (comma-separated)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{intended_use}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Intended use statement from the URS</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{intended_users}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Intended user roles from the URS user groups</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{intended_training}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Training assumptions from the URS</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#use_scenarios}...{/use_scenarios}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for UFMEA use scenarios (one table row per scenario)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{task_number}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Task number (1, 2, 3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{title}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Use scenario title from the UFMEA</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{description}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Use scenario description from the UFMEA</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#critical_tasks}...{/critical_tasks}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for UFMEA critical tasks (one table row per task)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{task_number}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Critical task number (1, 2, 3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{ufmea_line}</code></td><td style="padding: 6px; border: 1px solid #ddd;">UFMEA line / item number</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{task_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Critical task name (use step) from the UFMEA</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{task_description}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Critical task description (failure mode) from the UFMEA</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#use_errors}...{/use_errors}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for relevant use errors (MAUDE FDA signals)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{use_error_number}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Use error number (1, 2, 3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{use_error}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Use error finding from MAUDE</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{consequence}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Consequence / study-design implication of the use error</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{severity}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Signal severity (Critical, Warning)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{mdr}</code></td><td style="padding: 6px; border: 1px solid #ddd;">MAUDE MDR report number(s) — the risk documentation reference</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{brand_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Device brand name from the MAUDE event</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#ufmea_matches}...{/ufmea_matches}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for UFMEA failure modes matching this use error (up to 3, or one NONE entry)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{ufmea_line_id}</code></td><td style="padding: 6px; border: 1px solid #ddd;">UFMEA line ID of the matching failure mode (empty when no match)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{ufmea_use_error}</code></td><td style="padding: 6px; border: 1px solid #ddd;">UFMEA use error or failure mode text, or "NONE (no matching line)"</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#participants}...{/participants}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for participants (one table row per planned participant)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{professional_background}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Professional background from the HF Advisor user group</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{experience_level}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Experience mix from the HF Advisor user group (e.g. "1/3/2" = 0–2 yrs / 2–10 yrs / 10+ yrs)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#questions}...{/questions}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for each question</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{question_number}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Question index (1, 2, 3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{question_text}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Question text</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{summary}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Summary of the question</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#quotes}...{/quotes}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for participant quotes</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{participant_name}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Participant label (P1, P2, P3...)</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{participant_quote}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Quote from participant</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{#recommendations}...{/recommendations}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Repeating section for recommendations</td></tr>
                        <tr><td style="padding: 6px; border: 1px solid #ddd;"><code>{recommendation_text}</code></td><td style="padding: 6px; border: 1px solid #ddd;">Recommendation text</td></tr>
                    </tbody>
                </table>
                <div style="margin-top: 15px; padding: 12px; background: #fff3cd; border-left: 4px solid #ffc107; border-radius: 4px;">
                    <strong style="color: #856404;">⚠️ Important: Avoid Word Formatting Issues</strong>
                    <ul style="margin: 8px 0 0 20px; color: #856404; font-size: 13px; line-height: 1.6;">
                        <li><strong>Copy placeholders</strong> from this table instead of typing them manually</li>
                        <li>After creating your template, select all text (Ctrl+A) and <strong>clear formatting</strong> (Ctrl+Spacebar, then Ctrl+Q)</li>
                        <li>If you get "duplicate tag" errors, copy text to Notepad and paste back into a new Word document</li>
                        <li>Use <strong>underscores</strong> in placeholders: <code>{participant_name}</code> not <code>{participant name}</code></li>
                    </ul>
                </div>
            </div>
        `;

        // Display Usability Report Templates
        if (usabilityTemplates.length > 0) {
            html += '<h3><i class="fas fa-file-word"></i> Usability Report Templates</h3>';
            html += usabilityTemplates.map(template => `
                <div class="admin-item ${template.is_active ? 'active-template' : ''}" style="border-left: 4px solid ${template.is_active ? '#28a745' : '#ddd'};">
                    <div class="admin-item-content">
                        <div class="admin-item-title">
                            ${escapeHtml(template.name)}
                            ${template.is_active ? '<span class="badge" style="background: #28a745; color: white; padding: 2px 8px; border-radius: 12px; font-size: 12px; margin-left: 8px;">Active</span>' : ''}
                        </div>
                        <div class="admin-item-meta">
                            <span style="color: #0066cc; font-weight: 500;">Usability Report</span> •
                            Size: ${formatFileSize(template.file_size || 0)} •
                            Created: ${formatDate(template.created_at)}
                        </div>
                    </div>
                    <div class="admin-item-actions">
                        ${!template.is_active ? `
                            <button class="btn btn-small btn-primary" onclick="activateTemplate(${template.id})">
                                <i class="fas fa-check"></i> Activate
                            </button>
                        ` : ''}
                        <button class="btn btn-small btn-outline" onclick="downloadTemplate(${template.id}, ${jsArg(template.name)})">
                            <i class="fas fa-download"></i> Download
                        </button>
                        <button class="btn btn-small btn-danger" onclick="deleteTemplate(${template.id})">
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            `).join('');
        }

        // Display Other Templates (Legacy)
        if (otherTemplates.length > 0) {
            html += '<h3 style="margin-top: 30px;"><i class="fas fa-file-alt"></i> Text Templates (Legacy)</h3>';
            html += otherTemplates.map(template => `
                <div class="admin-item">
                    <div class="admin-item-content">
                        <div class="admin-item-title">${escapeHtml(template.name)}</div>
                        <div class="admin-item-meta">Category: ${escapeHtml(template.category || 'General')} • Created: ${formatDate(template.created_at)}</div>
                    </div>
                    <div class="admin-item-actions">
                        <button class="btn btn-small btn-outline" onclick="viewTemplate(${jsArg(template.name)}, ${jsArg(template.content)})">
                            <i class="fas fa-eye"></i> View
                        </button>
                        <button class="btn btn-small btn-danger" onclick="deleteTemplate(${template.id})">
                            <i class="fas fa-trash"></i> Delete
                        </button>
                    </div>
                </div>
            `).join('');
        }

        if (templates.length === 0) {
            html += '<div class="no-files">No templates available</div>';
        }

        container.innerHTML = html;

        // Attach form submit handler
        const form = document.getElementById('uploadTemplateForm');
        if (form) {
            form.addEventListener('submit', handleTemplateUpload);
        }

    } catch (error) {
        console.error('Error loading admin templates:', error);
        container.innerHTML = '<div class="error">Failed to load templates</div>';
    }
}

// Template Upload Handler
async function handleTemplateUpload(event) {
    event.preventDefault();

    const formData = new FormData(event.target);
    const file = formData.get('file');
    const name = formData.get('name');

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.docx')) {
        showToast('Error', 'Only .docx files are allowed', 'error');
        return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
        showToast('Error', 'File size must be less than 5MB', 'error');
        return;
    }

    // Validate name
    if (!name || name.trim().length < 3) {
        showToast('Error', 'Template name must be at least 3 characters', 'error');
        return;
    }

    try {
        showToast('Uploading', 'Uploading and validating template...', 'info');

        const response = await ApiClient.uploadFile('/admin/templates/upload', formData);

        showToast('Success', response.message || 'Template uploaded successfully', 'success');

        // Reload templates
        await loadAdminTemplates();
    } catch (error) {
        console.error('Template upload error:', error);

        // Clean up error message - remove "HTTP 400:" prefix for better UX
        let errorMessage = error.message || 'Failed to upload template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// Activate Template
async function activateTemplate(templateId) {
    if (!confirm('Activate this template? This will deactivate the current active template.')) {
        return;
    }

    try {
        const response = await ApiClient.put(`/admin/templates/${templateId}/activate`);
        showToast('Success', response.message || 'Template activated successfully', 'success');
        await loadAdminTemplates();
    } catch (error) {
        console.error('Error activating template:', error);

        // Clean up error message
        let errorMessage = error.message || 'Failed to activate template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// Download Template
async function downloadTemplate(templateId, templateName) {
    try {
        // Use ApiClient.downloadFile() method - same as report downloads
        await ApiClient.downloadFile(`/admin/templates/${templateId}/download`, `${templateName}.docx`);
        showToast('Success', 'Template downloaded', 'success');
    } catch (error) {
        console.error('Download error:', error);

        // Clean up error message
        let errorMessage = error.message || 'Failed to download template';
        const httpPrefixMatch = errorMessage.match(/^HTTP \d+:\s*/);
        if (httpPrefixMatch) {
            errorMessage = errorMessage.substring(httpPrefixMatch[0].length);
        }

        showToast('Error', errorMessage, 'error');
    }
}

// ==============================
// Agent Prompt Management Functions (system_admin only)
// ==============================

const AGENT_TYPE_LABELS = {
    extractor: { name: 'Extractor Agent', icon: 'fa-file-import', description: 'Extracts structured data from participant feedback documents.' },
    supervisor: { name: 'Extraction Supervisor', icon: 'fa-check-double', description: 'Evaluates quality of data extraction output.' },
    report_generator: { name: 'Report Generator', icon: 'fa-file-word', description: 'Synthesizes participant responses into report insights.' },
    report_quality_supervisor: { name: 'Report Quality Supervisor', icon: 'fa-clipboard-check', description: 'Evaluates quality of generated usability reports.' },
    dc_analysis: { name: 'Design Change Analysis', icon: 'fa-drafting-compass', description: 'Analyzes proposed design changes and assesses regulatory pathway requirements for FDA-regulated medical devices.' },
    question_analyzer: { name: 'Question Analyzer', icon: 'fa-spell-check', description: 'Evaluates study questions for bias, scope, jargon, double-barreling, redundancy, and ordering effects before import.' },
    question_analyzer_inter: { name: 'Question Analyzer (Inter)', icon: 'fa-link', description: 'Evaluates relationships and dependencies between questions for logical flow, redundancy, and ordering effects across the full question set.' },
    ufmea_device_extractor: { name: 'UFMEA Device Extractor', icon: 'fa-microchip', description: 'Extracts device classification, predicate devices, and intended use from UFMEA documents.' },
    system_requirements_predicate_extractor: { name: 'System Requirements Extractor', icon: 'fa-list-alt', description: 'Extracts predicate devices and HF-relevant requirements (UI, task-triggering, error detection, user-dependent performance, accessibility) from System Requirements documents.' },
    user_requirements_scenario_extractor: { name: 'User Requirements (URS) Extractor', icon: 'fa-file-contract', description: 'Extracts structured data across 7 HF sections from a User Requirements Specification (URS) document.' },
    hf_advisor_overview: { name: 'HF Advisor — Tab 1 (Overview)', icon: 'fa-clipboard-list', description: 'HF study overview: study tasks, simulation fidelity, data capture, session duration, protocol deviations, regulatory documentation, ethics.' },
    hf_advisor_user_groups: { name: 'HF Advisor — Tab 2 (User Groups)', icon: 'fa-users', description: 'Derives UG-NN user groups with FDA six-factor distinctions, screener criteria, and rationale.' },
    hf_advisor_use_scenarios: { name: 'HF Advisor — Tab 3 (Use Scenarios)', icon: 'fa-tasks', description: 'Derives TS-NN use scenarios with risk level, stress conditions, HF observer notes, and traceability.' },
    hf_advisor_fda_signals: { name: 'HF Advisor — Tab 5 (FDA Signals)', icon: 'fa-broadcast-tower', description: 'Surfaces external MAUDE / 510(k) / PubMed / Market signals influencing study design.' },
    hf_advisor_regulatory_rationale: { name: 'HF Advisor — Tab 6 (Regulatory Rationale)', icon: 'fa-balance-scale', description: 'Submission-ready regulatory backbone. Uses {{USER_GROUPS}} / {{USE_SCENARIOS}} placeholders substituted at runtime.' },
    pubmed_extract: { name: 'PubMed Search Term Extractor', icon: 'fa-search', description: 'Haiku prompt that distills a raw device description into a clean 8-12-word PubMed search term. Use {{DEVICE_DESCRIPTION}} as the placeholder.' },
    regulatory_section_detector: { name: 'Regulatory Section Detector', icon: 'fa-file-pdf', description: 'Finds section boundaries (line number + heading) in a regulatory PDF uploaded to the registry. The API slices the verbatim text itself; the model never rewrites clause bodies.' }
};

let agentPromptsData = null;
let agentPromptsTenants = [];

async function loadAgentPrompts() {
    const container = document.getElementById('agentPromptsList');
    container.innerHTML = '<div class="loading">Loading agent prompts...</div>';

    try {
        const response = await ApiClient.get('/system/agent-prompts');
        agentPromptsData = response.prompts || {};
        agentPromptsTenants = response.tenants || [];
        displayAgentPrompts();
    } catch (error) {
        console.error('Error loading agent prompts:', error);
        container.innerHTML = '<div class="error">Failed to load agent prompts. System admin access required.</div>';
    }
}

// ── Regulatory reference registry (own admin tab, system_admin only) ────────
// The registry is curated via database migrations; system_admin can also
// edit a reference's metadata and its verbatim clauses inline here.

async function loadRegulatoryRegistry() {
    const container = document.getElementById('regulatoryRegistryList');
    if (!container) return;
    try {
        const response = await ApiClient.get('/regulatory-references');
        displayRegulatoryRegistry(response.references || []);
    } catch (error) {
        console.error('Error loading regulatory references:', error);
        container.innerHTML = '<div class="error">Failed to load regulatory references.</div>';
    }
}

function displayRegulatoryRegistry(references) {
    const container = document.getElementById('regulatoryRegistryList');
    if (!references.length) {
        container.innerHTML = '<div class="no-files">No regulatory references seeded. Run migration 046.</div>';
        return;
    }

    // Every isAdmin usage below gates a mutating control (upload, edit) —
    // curation is dev-only (see requireDevEnvironment on the API side).
    const isAdmin = AuthManager.getUser()?.role === 'system_admin' && isDevEnvironment();

    const statusBadge = (status) => status === 'current'
        ? '<span class="badge badge-primary">current</span>'
        : `<span class="badge badge-secondary">${escapeHtml(status)}</span>`;

    const contentTypeBadge = (ct) => ct === 'verbatim'
        ? '<span class="badge badge-success" title="Verbatim public-domain text">Verbatim</span>'
        : '<span class="badge badge-secondary" title="Paraphrased summary">Summary</span>';

    const REF_TYPE_INFO = {
        standard: 'A formal technical standard (e.g. ISO, IEC) — typically copyrighted, so clause summaries here must be paraphrased, never verbatim.',
        guidance: 'Non-binding regulatory guidance (e.g. FDA guidance document) — recommendations, not a legally enforceable requirement.',
        regulation: 'A binding regulation (e.g. 21 CFR) — a legally enforceable requirement.',
        database: 'A live external data source queried at runtime — citations are record identifiers, not clause entries.',
        pattern_library: 'A curated library of recurring patterns rather than a formal reference document.',
        literature: 'Published literature queried at runtime — citations are literature identifiers (e.g. PMIDs), not clause entries.',
    };
    const refTypeBadge = (refType) => {
        const label = refType.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
        const def = REF_TYPE_INFO[refType] || '';
        return `<span class="badge badge-secondary" title="${escapeHtml(def)}">${escapeHtml(label)}</span>`;
    };

    container.innerHTML = references.map((ref) => {
        const clauses = ref.clauses || [];
        const clauseTable = clauses.length
            ? `<details>
                <summary style="cursor:pointer; font-weight:600; margin:0.5rem 0;">${clauses.length} clause${clauses.length !== 1 ? 's' : ''}</summary>
                <table class="preview-table" style="font-size:0.85rem;">
                    <thead><tr><th>Clause</th><th>Title</th><th>Summary</th><th>Tags</th><th>Type</th>${isAdmin ? '<th></th>' : ''}</tr></thead>
                    <tbody>
                        ${clauses.map((c, i) => {
                            const rowId = `${ref.id}-${i}`;
                            const editable = isAdmin;
                            const viewRow = `
                            <tr id="reg-clause-view-${rowId}">
                                <td style="white-space:nowrap;">${escapeHtml(c.displayLabel)}</td>
                                <td>${escapeHtml(c.title)}</td>
                                <td>${escapeHtml(c.summary)}</td>
                                <td>${(c.tags || []).map((t) => `<span class="badge badge-secondary">${escapeHtml(t)}</span>`).join(' ')}</td>
                                <td>${contentTypeBadge(c.contentType)}</td>
                                ${isAdmin ? `<td style="white-space:nowrap;">${editable ? `<button class="btn btn-outline btn-sm" onclick="regulatoryToggleClauseEdit('${rowId}', true)" title="Edit clause"><i class="fas fa-pen"></i></button> <button class="btn btn-outline btn-sm" onclick="regulatoryDeleteClause(${jsArg(c.clauseId)})" title="Delete clause"><i class="fas fa-trash"></i></button>` : ''}</td>` : ''}
                            </tr>`;
                            const editRow = editable ? `
                            <tr id="reg-clause-edit-${rowId}" style="display:none;" data-clause-id="${escapeHtml(c.clauseId)}">
                                <td style="white-space:nowrap;vertical-align:top;">
                                    <input type="text" class="form-input reg-edit-displaylabel" maxlength="80" value="${escapeHtml(c.displayLabel)}" style="width:100%;font-size:0.85rem;">
                                    <div class="prompt-meta" style="margin-top:0.25rem;">${escapeHtml(c.clauseId)}</div>
                                </td>
                                <td colspan="4">
                                    <input type="text" class="form-input reg-edit-title" maxlength="255" value="${escapeHtml(c.title)}" placeholder="Title" style="width:100%;margin-bottom:0.25rem;font-size:0.85rem;">
                                    <textarea class="form-input reg-edit-summary" style="width:100%;min-height:80px;margin-bottom:0.25rem;font-size:0.85rem;">${escapeHtml(c.summary)}</textarea>
                                    <div class="reg-edit-tags" style="display:flex;flex-wrap:wrap;gap:0.1rem 0.75rem;font-size:0.8rem;margin-bottom:0.25rem;">
                                        ${REGULATORY_TAGS.map((t) => `<label style="display:flex;align-items:center;gap:0.25rem;white-space:nowrap;"><input type="checkbox" value="${t}" ${(c.tags || []).includes(t) ? 'checked' : ''}>${t}</label>`).join('')}
                                    </div>
                                    <input type="number" class="form-input reg-edit-order" min="0" value="${c.sortOrder ?? 0}" placeholder="Sort order" style="width:160px;font-size:0.85rem;">
                                </td>
                                <td style="vertical-align:top;">
                                    <button class="btn btn-primary btn-sm" onclick="regulatorySaveClauseEdit('${rowId}', this)"><i class="fas fa-save"></i> Save</button>
                                    <button class="btn btn-outline btn-sm" style="margin-top:0.25rem;" onclick="regulatoryToggleClauseEdit('${rowId}', false)">Cancel</button>
                                </td>
                            </tr>` : '';
                            return viewRow + editRow;
                        }).join('')}
                    </tbody>
                </table>
               </details>`
            : '<p class="prompt-meta">Data source — cited via record identifiers (MDR ids, K-numbers, PMIDs), no clause entries.</p>';

        const pathwayRules = ref.pathway_tags || [];
        const pathwayPanel = clauses.length
            ? `<details>
                <summary style="cursor:pointer; font-weight:600; margin:0.5rem 0;" title="Which clause tags of this reference the Design Change Analyzer uses, per original regulatory pathway">Pathway rules</summary>
                <table class="preview-table" style="font-size:0.85rem;">
                    <thead><tr><th>Original pathway</th><th>Included clause tags</th>${isAdmin ? '<th></th>' : ''}</tr></thead>
                    <tbody>
                        ${REGULATORY_PATHWAYS.map((pw) => {
                            const rowId = `${ref.id}-${pw}`;
                            const rule = pathwayRules.find((r) => r.pathway === pw);
                            const viewRow = `
                            <tr id="reg-pathway-view-${rowId}">
                                <td style="white-space:nowrap;"><strong>${pw}</strong></td>
                                <td>${rule ? (rule.tags || []).map((t) => `<span class="badge badge-secondary">${escapeHtml(t)}</span>`).join(' ') : '<span class="prompt-meta">Not included for this pathway</span>'}</td>
                                ${isAdmin ? `<td style="white-space:nowrap;"><button class="btn btn-outline btn-sm" onclick="regulatoryTogglePathwayEdit('${rowId}', true)" title="Edit pathway rule"><i class="fas fa-pen"></i></button>${rule ? ` <button class="btn btn-outline btn-sm" onclick="regulatoryDeletePathwayTags(${ref.id}, '${pw}')" title="Remove pathway rule"><i class="fas fa-trash"></i></button>` : ''}</td>` : ''}
                            </tr>`;
                            const editRow = isAdmin ? `
                            <tr id="reg-pathway-edit-${rowId}" style="display:none;" data-ref-id="${ref.id}" data-pathway="${pw}">
                                <td style="white-space:nowrap;vertical-align:top;"><strong>${pw}</strong></td>
                                <td>
                                    <div class="reg-edit-tags" style="display:flex;flex-wrap:wrap;gap:0.1rem 0.75rem;font-size:0.8rem;">
                                        ${REGULATORY_TAGS.map((t) => `<label style="display:flex;align-items:center;gap:0.25rem;white-space:nowrap;"><input type="checkbox" value="${t}" ${(rule?.tags || []).includes(t) ? 'checked' : ''}>${t}</label>`).join('')}
                                    </div>
                                </td>
                                <td style="vertical-align:top;">
                                    <button class="btn btn-primary btn-sm" onclick="regulatorySavePathwayTags('${rowId}', this)"><i class="fas fa-save"></i> Save</button>
                                    <button class="btn btn-outline btn-sm" style="margin-top:0.25rem;" onclick="regulatoryTogglePathwayEdit('${rowId}', false)">Cancel</button>
                                </td>
                            </tr>` : '';
                            return viewRow + editRow;
                        }).join('')}
                    </tbody>
                </table>
               </details>`
            : '';

        const uploadBtn = isAdmin
            ? `<button class="btn btn-outline btn-sm" style="margin-top:0.5rem;"
                 onclick="regulatoryStartUpload(${ref.id}, this)">
                 <i class="fas fa-upload"></i> Add sections from PDF
               </button>
               <button class="btn btn-outline btn-sm" style="margin-top:0.5rem;margin-left:0.5rem;"
                 onclick="regulatoryToggleAddClause(${ref.id})">
                 <i class="fas fa-plus"></i> Add clause manually
               </button>
               <div id="reg-upload-panel-${ref.id}" style="display:none; margin-top:0.75rem;"></div>
               <div id="reg-add-clause-panel-${ref.id}" style="display:none; margin-top:0.75rem; border:1px solid var(--border-color,#ddd); border-radius:6px; padding:1rem;">
                   <div style="display:flex;gap:0.5rem;flex-wrap:wrap;margin-bottom:0.25rem;">
                       <input type="text" class="form-input reg-add-clauseid" maxlength="60" placeholder="Clause ID (e.g. ${escapeHtml(ref.ref_key)}:5.1)" style="width:220px;">
                       <input type="text" class="form-input reg-add-displaylabel" maxlength="80" placeholder="Display label" style="width:220px;">
                       <input type="number" class="form-input reg-add-order" min="0" placeholder="Sort order" style="width:160px;">
                   </div>
                   <input type="text" class="form-input reg-add-title" maxlength="255" placeholder="Title" style="width:100%;margin-bottom:0.25rem;">
                   <textarea class="form-input reg-add-summary" placeholder="Summary" style="width:100%;min-height:80px;margin-bottom:0.25rem;"></textarea>
                   <div class="reg-add-tags" style="display:flex;flex-wrap:wrap;gap:0.1rem 0.75rem;font-size:0.8rem;margin-bottom:0.5rem;">
                       ${REGULATORY_TAGS.map((t) => `<label style="display:flex;align-items:center;gap:0.25rem;white-space:nowrap;"><input type="checkbox" value="${t}">${t}</label>`).join('')}
                   </div>
                   <button class="btn btn-primary btn-sm" onclick="regulatorySaveNewClause(${ref.id}, this)"><i class="fas fa-save"></i> Save</button>
                   <button class="btn btn-outline btn-sm" onclick="document.getElementById('reg-add-clause-panel-${ref.id}').style.display='none';">Cancel</button>
               </div>`
            : '';

        const effectiveDateInput = ref.effective_date ? String(ref.effective_date).slice(0, 10) : '';
        const editBtn = isAdmin
            ? `<button class="btn btn-outline btn-sm" onclick="regulatoryToggleRefEdit(${ref.id}, true)" title="Edit reference"><i class="fas fa-pen"></i></button>
               <button class="btn btn-outline btn-sm" onclick="regulatoryDeleteReference(${ref.id}, ${jsArg(ref.ref_key)})" title="Delete reference"><i class="fas fa-trash"></i></button>`
            : '';
        const editForm = isAdmin ? `
            <div id="reg-ref-edit-${ref.id}" style="display:none; margin-top:0.5rem;">
                <input type="text" class="form-input reg-ref-edit-title" maxlength="255" value="${escapeHtml(ref.title)}" placeholder="Title" style="width:100%;margin-bottom:0.25rem;">
                <input type="text" class="form-input reg-ref-edit-edition" maxlength="60" value="${escapeHtml(ref.edition)}" placeholder="Edition" style="width:200px;margin-bottom:0.25rem;">
                <select class="form-input reg-ref-edit-status" style="width:160px;margin-bottom:0.25rem;">
                    ${['current', 'superseded', 'withdrawn'].map((s) => `<option value="${s}" ${ref.status === s ? 'selected' : ''}>${s}</option>`).join('')}
                </select>
                <input type="date" class="form-input reg-ref-edit-effdate" value="${effectiveDateInput}" style="width:160px;margin-bottom:0.25rem;">
                <input type="text" class="form-input reg-ref-edit-url" maxlength="500" value="${escapeHtml(ref.source_url || '')}" placeholder="Source URL" style="width:100%;margin-bottom:0.25rem;">
                <textarea class="form-input reg-ref-edit-notes" placeholder="Notes" style="width:100%;min-height:60px;margin-bottom:0.25rem;">${escapeHtml(ref.notes || '')}</textarea>
                <button class="btn btn-primary btn-sm" onclick="regulatorySaveRefEdit(${ref.id}, this)"><i class="fas fa-save"></i> Save</button>
                <button class="btn btn-outline btn-sm" onclick="regulatoryToggleRefEdit(${ref.id}, false)">Cancel</button>
            </div>` : '';

        return `
            <div class="prompt-card">
                <div id="reg-ref-view-${ref.id}">
                    <div class="prompt-card-header">
                        <h3>${escapeHtml(ref.title)}</h3>
                        <span class="badge badge-secondary">${escapeHtml(ref.ref_key)}</span>
                        ${refTypeBadge(ref.ref_type)}
                        ${editBtn}
                    </div>
                    <div class="prompt-meta">
                        Edition: <strong>${escapeHtml(ref.edition)}</strong> · ${statusBadge(ref.status)}
                        ${ref.effective_date ? ` · Effective: ${new Date(ref.effective_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' })}` : ''}
                        ${ref.source_url ? ` · <a href="${escapeHtml(ref.source_url)}" target="_blank" rel="noopener noreferrer">Source</a>` : ''}
                    </div>
                    ${ref.notes ? `<p class="prompt-description">${escapeHtml(ref.notes)}</p>` : ''}
                </div>
                ${editForm}
                ${clauseTable}
                ${pathwayPanel}
                ${uploadBtn}
            </div>`;
    }).join('');
}

async function regulatoryExportSeed(btn) {
    btn.disabled = true;
    const originalHtml = btn.innerHTML;
    btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Exporting…';

    try {
        const response = await fetch(`${API_BASE_URL}/regulatory-references/export-seed`, {
            headers: { Authorization: `Bearer ${AuthManager.getToken()}` },
        });
        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Export failed');
        }

        const blob = await response.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'regulatory_content.sql';
        a.click();
        URL.revokeObjectURL(url);

        showToast(
            'Success',
            'Seed downloaded — save it to database/seeds/regulatory_content.sql and commit',
            'success',
            'Upsert-only — does NOT delete references/clauses removed since the last export.'
        );
    } catch (err) {
        showToast('Error', err.message || 'Export failed', 'error');
    } finally {
        btn.disabled = false;
        btn.innerHTML = originalHtml;
    }
}

const REGULATORY_PATHWAYS = ['510k', 'pma', 'de_novo'];
const REGULATORY_TAGS = [
    'user_groups', 'use_environments', 'use_scenarios', 'risk',
    'training', 'participants', 'study_design',
    'validation', 'documentation', 'fda_signals', 'design_change',
    '510k_designchange', 'pma_designchange',
];

async function regulatoryStartUpload(referenceId, btn) {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async () => {
        const file = input.files[0];
        if (!file) return;
        btn.disabled = true;
        btn.textContent = 'Extracting…';
        try {
            const formData = new FormData();
            formData.append('pdf', file);
            const res = await fetch(`${API_BASE_URL}/regulatory-references/${referenceId}/document`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${AuthManager.getToken()}` },
                body: formData,
            });
            if (!res.ok) throw new Error((await res.json()).error || res.statusText);
            const { sections } = await res.json();
            regulatoryRenderSectionPanel(referenceId, sections, btn);
        } catch (err) {
            showToast('Error', `PDF extraction failed: ${err.message}`, 'error');
            btn.disabled = false;
            btn.innerHTML = '<i class="fas fa-upload"></i> Add sections from PDF';
        }
    };
    input.click();
}

function regulatoryRenderSectionPanel(referenceId, sections, triggerBtn) {
    const panel = document.getElementById(`reg-upload-panel-${referenceId}`);
    if (!panel) return;

    const refKeyMatch = triggerBtn.closest('.prompt-card')?.querySelector('.prompt-card-header .badge')?.textContent || '';

    const rows = sections.map((s) => {
        const sectionNumber = s.heading.split(' ')[0];
        return `
        <tr>
            <td><input type="checkbox" class="reg-sec-check" data-idx="${s.index}" checked></td>
            <td><input type="text" class="form-input reg-sec-heading" data-idx="${s.index}" value="${escapeHtml(s.heading)}" maxlength="255" style="width:100%;font-size:0.85rem;"></td>
            <td><input type="text" class="form-input reg-sec-displaylabel" data-idx="${s.index}"
                       value="${escapeHtml(refKeyMatch + ' §' + sectionNumber)}" maxlength="80"
                       style="width:100%;font-size:0.85rem;"></td>
            <td><input type="text" class="form-input reg-sec-clauseid" data-idx="${s.index}"
                       value="${escapeHtml(refKeyMatch + ':' + sectionNumber)}" maxlength="60"
                       style="width:100%;font-size:0.85rem;"></td>
            <td>
                <div class="reg-sec-tags" data-idx="${s.index}" style="display:flex;flex-direction:column;gap:0.1rem;font-size:0.8rem;">
                    ${REGULATORY_TAGS.map((t) => `<label style="display:flex;align-items:center;gap:0.25rem;white-space:nowrap;"><input type="checkbox" value="${t}">${t}</label>`).join('')}
                </div>
            </td>
            <td><input type="number" class="form-input reg-sec-order" min="0" data-idx="${s.index}" value="${s.index * 10}" style="width:100px;font-size:0.85rem;"></td>
        </tr>
        <tr><td colspan="6">
            <details><summary style="font-size:0.8rem;cursor:pointer;">Edit summary text</summary>
                <textarea class="form-input reg-sec-text" data-idx="${s.index}" style="width:100%;font-size:0.75rem;min-height:120px;margin-top:0.25rem;">${escapeHtml(s.text)}</textarea>
            </details>
        </td></tr>
    `;
    }).join('');

    panel.innerHTML = `
        <div style="border:1px solid var(--border-color,#ddd);border-radius:6px;padding:1rem;">
            <h4 style="margin:0 0 0.5rem;">Review extracted sections (${sections.length})</h4>
            <div style="overflow-x:auto;">
            <table class="preview-table" style="font-size:0.85rem;">
                <thead><tr><th>✓</th><th>Heading / Title</th><th>Display Label</th><th>Clause ID</th><th>Tags (multi-select)</th><th>Order</th></tr></thead>
                <tbody>${rows}</tbody>
            </table>
            </div>
            <button class="btn btn-primary btn-sm" style="margin-top:0.75rem;"
                    onclick="regulatorySaveSections(${referenceId})">
                <i class="fas fa-save"></i> Save selected sections
            </button>
            <button class="btn btn-outline btn-sm" style="margin-top:0.75rem;margin-left:0.5rem;"
                    onclick="document.getElementById('reg-upload-panel-${referenceId}').style.display='none';">
                Cancel
            </button>
        </div>`;
    panel.style.display = '';
}

async function regulatorySaveSections(referenceId) {
    const panel = document.getElementById(`reg-upload-panel-${referenceId}`);
    const checks = panel.querySelectorAll('.reg-sec-check:checked');
    const clauses = Array.from(checks).map((chk) => {
        const idx = chk.dataset.idx;
        const heading = panel.querySelector(`.reg-sec-heading[data-idx="${idx}"]`).value.trim();
        const displayLabel = panel.querySelector(`.reg-sec-displaylabel[data-idx="${idx}"]`).value.trim();
        const clauseId = panel.querySelector(`.reg-sec-clauseid[data-idx="${idx}"]`).value.trim();
        const tags = Array.from(panel.querySelectorAll(`.reg-sec-tags[data-idx="${idx}"] input:checked`)).map((cb) => cb.value);
        const sortOrder = parseInt(panel.querySelector(`.reg-sec-order[data-idx="${idx}"]`).value, 10) || 0;
        const summary = panel.querySelector(`.reg-sec-text[data-idx="${idx}"]`)?.value || '';
        return { clause_id: clauseId, display_label: displayLabel, title: heading, summary, tags, sort_order: sortOrder };
    });

    if (!clauses.length) { showToast('Error', 'Select at least one section.', 'error'); return; }

    try {
        const res = await ApiClient.post(`/regulatory-references/${referenceId}/clauses`, { clauses });
        const skippedNote = res.skipped ? ` (${res.skipped} already existed and were skipped)` : '';
        showToast('Success', `Saved ${res.created} clause${res.created !== 1 ? 's' : ''}.${skippedNote}`, 'success');
        panel.style.display = 'none';
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Save failed: ${err.message}`, 'error');
    }
}

function regulatoryToggleNewRef(showForm = true) {
    const form = document.getElementById('regulatoryNewRefForm');
    if (!form) return;
    form.style.display = showForm ? '' : 'none';
    if (showForm) {
        ['regNewRefKey', 'regNewRefTitle', 'regNewRefEdition', 'regNewRefEffDate', 'regNewRefUrl', 'regNewRefNotes'].forEach((id) => {
            document.getElementById(id).value = '';
        });
        document.getElementById('regNewRefType').value = 'standard';
        document.getElementById('regNewRefStatus').value = 'current';
    }
}

async function regulatorySaveNewRef(btn) {
    const ref_key = document.getElementById('regNewRefKey').value.trim();
    const ref_type = document.getElementById('regNewRefType').value;
    const status = document.getElementById('regNewRefStatus').value;
    const title = document.getElementById('regNewRefTitle').value.trim();
    const edition = document.getElementById('regNewRefEdition').value.trim();
    const effective_date = document.getElementById('regNewRefEffDate').value || null;
    const source_url = document.getElementById('regNewRefUrl').value.trim() || null;
    const notes = document.getElementById('regNewRefNotes').value.trim() || null;

    if (!ref_key || !title || !edition) {
        showToast('Error', 'ref_key, title, and edition are required.', 'error');
        return;
    }

    btn.disabled = true;
    try {
        await ApiClient.post('/regulatory-references', { ref_key, ref_type, title, edition, status, effective_date, source_url, notes });
        showToast('Success', 'Reference created.', 'success');
        regulatoryToggleNewRef(false);
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Create failed: ${err.message}`, 'error');
    } finally {
        btn.disabled = false;
    }
}

function regulatoryToggleAddClause(refId) {
    const panel = document.getElementById(`reg-add-clause-panel-${refId}`);
    if (!panel) return;
    const showing = panel.style.display !== 'none';
    if (!showing) {
        panel.querySelectorAll('input[type="text"], input[type="number"], textarea').forEach((el) => { el.value = ''; });
        panel.querySelectorAll('input[type="checkbox"]').forEach((el) => { el.checked = false; });
    }
    panel.style.display = showing ? 'none' : '';
}

async function regulatorySaveNewClause(referenceId, btn) {
    const panel = document.getElementById(`reg-add-clause-panel-${referenceId}`);
    if (!panel) return;

    const clause_id = panel.querySelector('.reg-add-clauseid').value.trim();
    const display_label = panel.querySelector('.reg-add-displaylabel').value.trim();
    const title = panel.querySelector('.reg-add-title').value.trim();
    const summary = panel.querySelector('.reg-add-summary').value.trim();
    const sort_order = parseInt(panel.querySelector('.reg-add-order').value, 10) || 0;
    const tags = Array.from(panel.querySelectorAll('.reg-add-tags input:checked')).map((cb) => cb.value);

    if (!clause_id || !display_label || !title || !summary) {
        showToast('Error', 'Clause ID, display label, title, and summary are required.', 'error');
        return;
    }

    btn.disabled = true;
    try {
        const res = await ApiClient.post(`/regulatory-references/${referenceId}/clauses`, {
            clauses: [{ clause_id, display_label, title, summary, tags, sort_order }],
        });
        if (res.skipped) {
            showToast('Error', 'A clause with that ID already exists.', 'error');
            return;
        }
        showToast('Success', 'Clause added.', 'success');
        panel.style.display = 'none';
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Save failed: ${err.message}`, 'error');
    } finally {
        btn.disabled = false;
    }
}

async function regulatoryDeleteClause(clauseId) {
    if (!confirm(`Delete clause ${clauseId}? This cannot be undone.`)) return;
    try {
        await ApiClient.delete(`/regulatory-references/clauses/${encodeURIComponent(clauseId)}`);
        showToast('Success', 'Clause deleted.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Delete failed: ${err.message}`, 'error');
    }
}

async function regulatoryDeleteReference(refId, refKey) {
    if (!confirm(`Delete reference ${refKey}? This also deletes all of its clauses and cannot be undone.`)) return;
    try {
        await ApiClient.delete(`/regulatory-references/${refId}`);
        showToast('Success', 'Reference deleted.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Delete failed: ${err.message}`, 'error');
    }
}

function regulatoryToggleRefEdit(refId, showEdit) {
    const viewDiv = document.getElementById(`reg-ref-view-${refId}`);
    const editDiv = document.getElementById(`reg-ref-edit-${refId}`);
    if (!viewDiv || !editDiv) return;
    viewDiv.style.display = showEdit ? 'none' : '';
    editDiv.style.display = showEdit ? '' : 'none';
}

async function regulatorySaveRefEdit(refId, btn) {
    const editDiv = document.getElementById(`reg-ref-edit-${refId}`);
    if (!editDiv) return;

    const title = editDiv.querySelector('.reg-ref-edit-title').value.trim();
    const edition = editDiv.querySelector('.reg-ref-edit-edition').value.trim();
    const status = editDiv.querySelector('.reg-ref-edit-status').value;
    const effective_date = editDiv.querySelector('.reg-ref-edit-effdate').value || null;
    const source_url = editDiv.querySelector('.reg-ref-edit-url').value.trim() || null;
    const notes = editDiv.querySelector('.reg-ref-edit-notes').value.trim() || null;

    if (!title || !edition) {
        showToast('Error', 'Title and edition are required.', 'error');
        return;
    }

    btn.disabled = true;
    try {
        await ApiClient.patch(`/regulatory-references/${refId}`, { title, edition, status, effective_date, source_url, notes });
        showToast('Success', 'Reference updated.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Save failed: ${err.message}`, 'error');
        btn.disabled = false;
    }
}

function regulatoryToggleClauseEdit(rowId, showEdit) {
    const viewRow = document.getElementById(`reg-clause-view-${rowId}`);
    const editRow = document.getElementById(`reg-clause-edit-${rowId}`);
    if (!viewRow || !editRow) return;
    viewRow.style.display = showEdit ? 'none' : '';
    editRow.style.display = showEdit ? '' : 'none';
}

async function regulatorySaveClauseEdit(rowId, btn) {
    const editRow = document.getElementById(`reg-clause-edit-${rowId}`);
    if (!editRow) return;
    const clauseId = editRow.dataset.clauseId;

    const displayLabel = editRow.querySelector('.reg-edit-displaylabel').value.trim();
    const title = editRow.querySelector('.reg-edit-title').value.trim();
    const summary = editRow.querySelector('.reg-edit-summary').value.trim();
    const tags = Array.from(editRow.querySelectorAll('.reg-edit-tags input:checked')).map((cb) => cb.value);
    const sortOrder = parseInt(editRow.querySelector('.reg-edit-order').value, 10) || 0;

    btn.disabled = true;
    try {
        await ApiClient.patch(`/regulatory-references/clauses/${encodeURIComponent(clauseId)}`, {
            display_label: displayLabel, title, summary, tags, sort_order: sortOrder,
        });
        showToast('Success', 'Clause updated.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Save failed: ${err.message}`, 'error');
        btn.disabled = false;
    }
}

function regulatoryTogglePathwayEdit(rowId, showEdit) {
    const viewRow = document.getElementById(`reg-pathway-view-${rowId}`);
    const editRow = document.getElementById(`reg-pathway-edit-${rowId}`);
    if (!viewRow || !editRow) return;
    viewRow.style.display = showEdit ? 'none' : '';
    editRow.style.display = showEdit ? '' : 'none';
}

async function regulatorySavePathwayTags(rowId, btn) {
    const editRow = document.getElementById(`reg-pathway-edit-${rowId}`);
    if (!editRow) return;
    const tags = Array.from(editRow.querySelectorAll('.reg-edit-tags input:checked')).map((cb) => cb.value);
    if (!tags.length) {
        showToast('Error', 'Select at least one tag, or use the delete button to exclude this reference for the pathway.', 'error');
        return;
    }
    btn.disabled = true;
    try {
        await ApiClient.put(`/regulatory-references/${editRow.dataset.refId}/pathway-tags/${editRow.dataset.pathway}`, { tags });
        showToast('Success', 'Pathway rule saved.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Save failed: ${err.message}`, 'error');
        btn.disabled = false;
    }
}

async function regulatoryDeletePathwayTags(refId, pathway) {
    if (!confirm(`Remove the ${pathway} rule? This reference will contribute no clauses for ${pathway} analyses.`)) return;
    try {
        await ApiClient.delete(`/regulatory-references/${refId}/pathway-tags/${pathway}`);
        showToast('Success', 'Pathway rule removed.', 'success');
        loadRegulatoryRegistry();
    } catch (err) {
        showToast('Error', `Delete failed: ${err.message}`, 'error');
    }
}

window.showExtractedDataModal = showExtractedDataModal;
window.regulatoryTogglePathwayEdit = regulatoryTogglePathwayEdit;
window.regulatorySavePathwayTags = regulatorySavePathwayTags;
window.regulatoryDeletePathwayTags = regulatoryDeletePathwayTags;
window.regulatoryExportSeed = regulatoryExportSeed;
window.regulatoryToggleNewRef = regulatoryToggleNewRef;
window.regulatorySaveNewRef = regulatorySaveNewRef;
window.regulatoryStartUpload = regulatoryStartUpload;
window.regulatorySaveSections = regulatorySaveSections;
window.regulatoryToggleClauseEdit = regulatoryToggleClauseEdit;
window.regulatorySaveClauseEdit = regulatorySaveClauseEdit;
window.regulatoryDeleteClause = regulatoryDeleteClause;
window.regulatoryToggleAddClause = regulatoryToggleAddClause;
window.regulatorySaveNewClause = regulatorySaveNewClause;
window.regulatoryToggleRefEdit = regulatoryToggleRefEdit;
window.regulatorySaveRefEdit = regulatorySaveRefEdit;
window.regulatoryDeleteReference = regulatoryDeleteReference;

function displayAgentPrompts() {
    const container = document.getElementById('agentPromptsList');
    const agentTypes = Object.keys(AGENT_TYPE_LABELS);

    const html = agentTypes.map(agentType => {
        const label = AGENT_TYPE_LABELS[agentType];
        const data = agentPromptsData[agentType] || { default: null, overrides: [] };
        const defaultPrompt = data.default;
        const overrides = data.overrides || [];

        return `
            <div class="prompt-card" data-agent-type="${agentType}">
                <div class="prompt-card-header">
                    <h3><i class="fas ${label.icon}"></i> ${escapeHtml(label.name)}</h3>
                    <span class="badge badge-secondary">${escapeHtml(agentType)}</span>
                </div>
                <p class="prompt-description">${escapeHtml(label.description)}</p>

                <div class="prompt-default-section">
                    <h4>System Default</h4>
                    <textarea class="prompt-textarea" id="prompt-default-${agentType}" rows="12">${defaultPrompt ? escapeHtml(defaultPrompt.prompt_text) : ''}</textarea>
                    <div class="prompt-meta">
                        ${defaultPrompt ? `v${defaultPrompt.version} | Updated: ${formatDate(defaultPrompt.updated_at)}` : 'No default set'}
                    </div>
                    <button class="btn btn-primary btn-sm" onclick="saveDefaultPrompt('${agentType}')">
                        <i class="fas fa-save"></i> Save Default
                    </button>
                </div>

                <div class="prompt-overrides-section">
                    <div class="prompt-overrides-header">
                        <h4>Tenant Overrides (${overrides.length})</h4>
                        <button class="btn btn-outline btn-sm" onclick="showAddOverrideForm('${agentType}')">
                            <i class="fas fa-plus"></i> Add Override
                        </button>
                    </div>

                    <div id="override-form-${agentType}" class="override-form" style="display: none;">
                        <div class="form-group">
                            <label>Select Tenant</label>
                            <select id="override-tenant-${agentType}" class="form-input">
                                <option value="">-- Select tenant --</option>
                                ${agentPromptsTenants.map(t => `<option value="${t.id}">${escapeHtml(t.name)} (${escapeHtml(t.slug)})</option>`).join('')}
                            </select>
                        </div>
                        <div class="form-group">
                            <label>Override Prompt</label>
                            <textarea class="prompt-textarea" id="override-text-${agentType}" rows="10" placeholder="Enter tenant-specific prompt..."></textarea>
                        </div>
                        <div class="prompt-actions">
                            <button class="btn btn-primary btn-sm" onclick="saveTenantOverride('${agentType}')">
                                <i class="fas fa-save"></i> Save Override
                            </button>
                            <button class="btn btn-outline btn-sm" onclick="hideAddOverrideForm('${agentType}')">Cancel</button>
                        </div>
                    </div>

                    ${overrides.length > 0 ? overrides.map(override => `
                        <div class="override-item">
                            <div class="override-item-header">
                                <span class="badge badge-primary">${escapeHtml(override.tenant_name || 'Tenant ' + override.tenant_id)}</span>
                                <span class="prompt-meta">v${override.version} | Updated: ${formatDate(override.updated_at)}</span>
                            </div>
                            <textarea class="prompt-textarea" id="override-edit-${agentType}-${override.tenant_id}" rows="8">${escapeHtml(override.prompt_text)}</textarea>
                            <div class="prompt-actions">
                                <button class="btn btn-primary btn-sm" onclick="updateTenantOverride('${agentType}', ${override.tenant_id})">
                                    <i class="fas fa-save"></i> Update
                                </button>
                                <button class="btn btn-danger btn-sm" onclick="removeTenantOverride('${agentType}', ${override.tenant_id})">
                                    <i class="fas fa-trash"></i> Remove Override
                                </button>
                            </div>
                        </div>
                    `).join('') : '<div class="no-files">No tenant overrides. All tenants use the system default.</div>'}
                </div>
            </div>
        `;
    }).join('');

    container.innerHTML = html;
}

function showAddOverrideForm(agentType) {
    document.getElementById(`override-form-${agentType}`).style.display = 'block';
}

function hideAddOverrideForm(agentType) {
    document.getElementById(`override-form-${agentType}`).style.display = 'none';
}

async function saveDefaultPrompt(agentType) {
    const textarea = document.getElementById(`prompt-default-${agentType}`);
    const promptText = textarea.value.trim();

    if (!promptText) {
        showToast('Error', 'Prompt text cannot be empty', 'error');
        return;
    }

    try {
        await ApiClient.put(`/system/agent-prompts/${agentType}/default`, {
            prompt_text: promptText
        });
        showToast('Success', 'Default prompt updated successfully', 'success');
        await loadAgentPrompts();
    } catch (error) {
        console.error('Error saving default prompt:', error);
        showToast('Error', error.message || 'Failed to save default prompt', 'error');
    }
}

async function saveTenantOverride(agentType) {
    const tenantSelect = document.getElementById(`override-tenant-${agentType}`);
    const textarea = document.getElementById(`override-text-${agentType}`);
    const tenantId = tenantSelect.value;
    const promptText = textarea.value.trim();

    if (!tenantId) {
        showToast('Error', 'Please select a tenant', 'error');
        return;
    }

    if (!promptText) {
        showToast('Error', 'Prompt text cannot be empty', 'error');
        return;
    }

    try {
        await ApiClient.put(`/system/agent-prompts/${agentType}/tenant/${tenantId}`, {
            prompt_text: promptText
        });
        showToast('Success', 'Tenant override saved successfully', 'success');
        hideAddOverrideForm(agentType);
        await loadAgentPrompts();
    } catch (error) {
        console.error('Error saving tenant override:', error);
        showToast('Error', error.message || 'Failed to save tenant override', 'error');
    }
}

async function updateTenantOverride(agentType, tenantId) {
    const textarea = document.getElementById(`override-edit-${agentType}-${tenantId}`);
    const promptText = textarea.value.trim();

    if (!promptText) {
        showToast('Error', 'Prompt text cannot be empty', 'error');
        return;
    }

    try {
        await ApiClient.put(`/system/agent-prompts/${agentType}/tenant/${tenantId}`, {
            prompt_text: promptText
        });
        showToast('Success', 'Tenant override updated successfully', 'success');
        await loadAgentPrompts();
    } catch (error) {
        console.error('Error updating tenant override:', error);
        showToast('Error', error.message || 'Failed to update tenant override', 'error');
    }
}

async function removeTenantOverride(agentType, tenantId) {
    if (!confirm('Remove this tenant override? The tenant will revert to the system default prompt.')) {
        return;
    }

    try {
        await ApiClient.delete(`/system/agent-prompts/${agentType}/tenant/${tenantId}`);
        showToast('Success', 'Tenant override removed', 'success');
        await loadAgentPrompts();
    } catch (error) {
        console.error('Error removing tenant override:', error);
        showToast('Error', error.message || 'Failed to remove tenant override', 'error');
    }
}

// Expose agent prompt functions globally for inline onclick handlers
window.showAddOverrideForm = showAddOverrideForm;
window.hideAddOverrideForm = hideAddOverrideForm;
window.saveDefaultPrompt = saveDefaultPrompt;
window.saveTenantOverride = saveTenantOverride;
window.updateTenantOverride = updateTenantOverride;
window.removeTenantOverride = removeTenantOverride;

// ==============================
// Tenant Management Functions (system_admin only)
// ==============================

async function loadAdminTenants() {
    const container = document.getElementById('adminTenantsList');

    try {
        container.innerHTML = '<div class="loading">Loading tenants...</div>';

        const showInactive = document.getElementById('showInactiveTenants')?.checked || false;
        const queryParams = showInactive ? '?includeInactive=true' : '';

        const response = await ApiClient.get(`/admin/tenants${queryParams}`);
        const tenants = response.tenants || [];

        if (tenants.length === 0) {
            container.innerHTML = '<div class="no-files">No tenants available</div>';
            return;
        }

        container.innerHTML = tenants.map(tenant => {
            const statusBadge = tenant.active
                ? '<span class="badge badge-success">Active</span>'
                : '<span class="badge badge-danger">Inactive</span>';
            const passwordBadge = tenant.allow_password_login
                ? '<span class="badge badge-secondary">Password</span>'
                : '';
            const ssoBadge = tenant.default_identity_provider
                ? `<span class="badge badge-info">SSO: ${escapeHtml(tenant.default_identity_provider)}</span>`
                : '';

            return `
                <div class="admin-item tenant-item">
                    <div class="admin-item-content">
                        <div class="admin-item-title">
                            <i class="fas fa-building"></i> ${escapeHtml(tenant.name)}
                        </div>
                        <div class="admin-item-meta">
                            Slug: <code>${escapeHtml(tenant.slug)}</code> •
                            ${statusBadge}
                            ${passwordBadge}
                            ${ssoBadge}
                        </div>
                        <div class="admin-item-meta">
                            Users: ${tenant.user_count || 0} active
                            ${tenant.total_user_count > tenant.user_count ? ` (${tenant.total_user_count} total)` : ''}
                        </div>
                        ${tenant.description ? `<div class="admin-item-description">${escapeHtml(tenant.description)}</div>` : ''}
                        <div class="admin-item-date">
                            Created: ${formatDate(tenant.created_at)}
                        </div>
                    </div>
                    <div class="admin-item-actions">
                        <button class="btn btn-small btn-outline" onclick="editTenant(${tenant.id})">
                            <i class="fas fa-edit"></i> Edit
                        </button>
                        ${tenant.active ? `
                            <button class="btn btn-small btn-danger" onclick="deleteTenant(${tenant.id}, ${jsArg(tenant.name)})">
                                <i class="fas fa-trash"></i> Deactivate
                            </button>
                        ` : ''}
                    </div>
                </div>
            `;
        }).join('');

    } catch (error) {
        console.error('Error loading tenants:', error);
        container.innerHTML = '<div class="error">Failed to load tenants</div>';
        showToast('Error', 'Failed to load tenants', 'error');
    }
}

function showAddTenantModal() {
    document.getElementById('tenantModalTitle').textContent = 'Add New Tenant';
    document.getElementById('tenantId').value = '';
    document.getElementById('tenantName').value = '';
    document.getElementById('tenantSlug').value = '';
    document.getElementById('tenantDescription').value = '';
    document.getElementById('tenantActive').checked = true;
    document.getElementById('tenantAllowPasswordLogin').checked = true;
    document.getElementById('tenantDefaultIdp').value = '';
    document.getElementById('tenantFormError').style.display = 'none';
    document.getElementById('tenantModal').style.display = 'block';
}

async function editTenant(tenantId) {
    try {
        const response = await ApiClient.get(`/admin/tenants/${tenantId}`);
        const tenant = response.tenant;

        if (!tenant) {
            showToast('Error', 'Tenant not found', 'error');
            return;
        }

        document.getElementById('tenantModalTitle').textContent = 'Edit Tenant';
        document.getElementById('tenantId').value = tenant.id;
        document.getElementById('tenantName').value = tenant.name || '';
        document.getElementById('tenantSlug').value = tenant.slug || '';
        document.getElementById('tenantDescription').value = tenant.description || '';
        document.getElementById('tenantActive').checked = tenant.active !== false;
        document.getElementById('tenantAllowPasswordLogin').checked = tenant.allow_password_login !== false;
        document.getElementById('tenantDefaultIdp').value = tenant.default_identity_provider || '';
        document.getElementById('tenantFormError').style.display = 'none';
        document.getElementById('tenantModal').style.display = 'block';

    } catch (error) {
        console.error('Error loading tenant:', error);
        showToast('Error', 'Failed to load tenant details', 'error');
    }
}

function closeTenantModal() {
    document.getElementById('tenantModal').style.display = 'none';
}

async function handleTenantFormSubmit(e) {
    e.preventDefault();

    const tenantId = document.getElementById('tenantId').value;
    const isEdit = !!tenantId;

    const data = {
        name: document.getElementById('tenantName').value.trim(),
        slug: document.getElementById('tenantSlug').value.trim().toLowerCase(),
        description: document.getElementById('tenantDescription').value.trim() || null,
        active: document.getElementById('tenantActive').checked,
        allow_password_login: document.getElementById('tenantAllowPasswordLogin').checked,
        default_identity_provider: document.getElementById('tenantDefaultIdp').value.trim() || null
    };

    // Basic validation
    if (!data.name || !data.slug) {
        document.getElementById('tenantFormError').textContent = 'Name and slug are required';
        document.getElementById('tenantFormError').style.display = 'block';
        return;
    }

    // Validate slug format
    if (!/^[a-z0-9-]+$/.test(data.slug)) {
        document.getElementById('tenantFormError').textContent = 'Slug must contain only lowercase letters, numbers, and hyphens';
        document.getElementById('tenantFormError').style.display = 'block';
        return;
    }

    const saveBtn = document.getElementById('saveTenantBtn');
    saveBtn.disabled = true;
    saveBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving...';

    try {
        if (isEdit) {
            await ApiClient.put(`/admin/tenants/${tenantId}`, data);
            showToast('Success', 'Tenant updated successfully', 'success');
        } else {
            await ApiClient.post('/admin/tenants', data);
            showToast('Success', 'Tenant created successfully', 'success');
        }

        closeTenantModal();
        loadAdminTenants();

    } catch (error) {
        console.error('Error saving tenant:', error);
        let errorMessage = error.message || 'Failed to save tenant';

        if (errorMessage.includes('slug already exists')) {
            errorMessage = 'A tenant with this slug already exists';
        }

        document.getElementById('tenantFormError').textContent = errorMessage;
        document.getElementById('tenantFormError').style.display = 'block';
    } finally {
        saveBtn.disabled = false;
        saveBtn.innerHTML = '<i class="fas fa-save"></i> Save Tenant';
    }
}

async function deleteTenant(tenantId, tenantName) {
    if (!confirm(`Are you sure you want to deactivate "${tenantName}"?\n\nUsers in this tenant will no longer be able to log in.`)) {
        return;
    }

    try {
        showLoading(true);

        await ApiClient.delete(`/admin/tenants/${tenantId}`);

        showToast('Success', `Tenant "${tenantName}" deactivated successfully`, 'success');
        loadAdminTenants();

    } catch (error) {
        console.error('Error deactivating tenant:', error);
        const errorMessage = error.message || 'Failed to deactivate tenant';
        showToast('Error', errorMessage, 'error');
    } finally {
        showLoading(false);
    }
}

// Setup tenant modal controls
function setupTenantAdmin() {
    document.getElementById('addTenantBtn')?.addEventListener('click', showAddTenantModal);
    document.getElementById('closeTenantModal')?.addEventListener('click', closeTenantModal);
    document.getElementById('cancelTenantBtn')?.addEventListener('click', closeTenantModal);
    document.getElementById('tenantForm')?.addEventListener('submit', handleTenantFormSubmit);
}

// Expose tenant functions globally for inline onclick handlers
window.loadAdminTenants = loadAdminTenants;
window.editTenant = editTenant;
window.deleteTenant = deleteTenant;

// Modal Functions
function setupModals() {
    // Admin button - switch to admin view
    elements.adminBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Admin button clicked, switching to admin view');
        switchView('admin');
        switchAdminTab('users'); // Load users tab by default
        window.location.hash = '#admin';
    });

    // Back to main from admin
    elements.backToMainBtn?.addEventListener('click', (e) => {
        e.preventDefault();
        console.log('Back button clicked, switching to main view');
        switchView('main');
        window.location.hash = '';
    });

    // Close modals when clicking outside
    window.addEventListener('click', (e) => {
        if (e.target.classList.contains('modal')) {
            e.target.style.display = 'none';
        }
    });

    document.getElementById('closeExtractedDataModal')?.addEventListener('click', closeExtractedDataModal);
}

// Utility function to escape HTML
function escapeHtml(text) {
    // Quotes too: results are interpolated into attribute values.
    return String(text ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

// A JS string literal safe inside an inline on*="..." handler. The browser
// HTML-decodes the attribute before running it, so escapeHtml alone is not enough.
function jsArg(value) {
    return escapeHtml(JSON.stringify(String(value ?? '')));
}

function highlightQuery(text, query) {
    const escaped = escapeHtml(text);
    if (!query) return escaped;
    const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    return escaped.replace(re, m => `<strong>${m}</strong>`);
}

/**
 * Escape text then turn Q1/Q2/... references into clickable links that scroll
 * to the corresponding question row in the preview table.
 */
function linkifyQuestionRefs(text) {
    return escapeHtml(text).replace(/\bQ(\d+)\b/g, (match, n) =>
        `<a href="javascript:void(0)" class="q-ref-link" onclick="scrollToQuestion(${n})">${match}</a>`
    );
}

function scrollToQuestion(n) {
    const row = document.getElementById(`q-row-${n}`);
    if (!row) return;
    row.closest('tr').scrollIntoView({ behavior: 'smooth', block: 'center' });
    const label = row.querySelector('.q-num-label');
    if (label) {
        label.classList.add('q-num-highlight');
        setTimeout(() => label.classList.remove('q-num-highlight'), 1800);
    }
}

function inlineMarkdown(text) {
    return escapeHtml(text)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*]+?)\*/g,  '<em>$1</em>');
}

/**
 * Pre-processes AI prose that lacks markdown structure but uses inline
 * ALL-CAPS LABEL: cues (e.g. "STUDY TYPE: ..."  or "SESSION STRUCTURE: ...")
 * or long run-on paragraphs. Splits such text into markdown-style sections
 * so formatStudyNotesHtml can render them with headings and paragraphs.
 */
function normalizeNarrativeMarkdown(text) {
    if (!text) return '';

    // If the text already uses markdown headings or bullets or has real line breaks
    // with short-line headings, leave it alone.
    const hasStructure = /(^|\n)\s*(#{1,3}\s|[-*•]\s|\d+[.)]\s)/.test(text)
        || /\n\s*\n/.test(text);
    if (hasStructure) return text;

    // Insert a newline before ALL-CAPS LABELS followed by a colon (at least 2 words
    // or 6+ chars, ending in ":"). Example: "STUDY TYPE: ...", "SESSION STRUCTURE: ..."
    let out = text.replace(/\s+((?:[A-Z][A-Z0-9/&()-]*\s+){0,3}[A-Z][A-Z0-9/&()-]{2,}(?:\s+[A-Z][A-Z0-9/&()-]*){0,4}):\s+/g,
        (match, label) => `\n\n## ${label}\n`);

    // If nothing matched and the paragraph is long, split into paragraphs every
    // ~3 sentences. A sentence ends at . ! or ? followed by whitespace + a capital
    // letter — this avoids splitting on "Section 5.1", "IEC 62366-1:2015", "K152770",
    // decimals, and mid-sentence abbreviations.
    if (!/\n/.test(out) && out.length > 400) {
        const sentenceEnd = /([.!?])(["')\]]?)\s+(?=[A-Z])/g;
        const sentences = [];
        let lastIdx = 0;
        let match;
        while ((match = sentenceEnd.exec(out)) !== null) {
            sentences.push(out.slice(lastIdx, match.index + match[0].length).trim());
            lastIdx = match.index + match[0].length;
        }
        if (lastIdx < out.length) sentences.push(out.slice(lastIdx).trim());

        const grouped = [];
        for (let i = 0; i < sentences.length; i += 3) {
            grouped.push(sentences.slice(i, i + 3).join(' ').trim());
        }
        out = grouped.filter(Boolean).join('\n\n');
    }

    return out.trim();
}

/**
 * Converts the free-text studyDesignNotes blob into structured HTML.
 * Detects headings (markdown ## / bold **text** / short lines ending in ":"),
 * bullet lists, numbered lists, and regular paragraphs.
 */
function formatStudyNotesHtml(text) {
    if (!text) return '';
    const lines = normalizeNarrativeMarkdown(text).split('\n');
    const parts = [];
    let inList = false;
    let listType = null;

    const closeList = () => {
        if (inList) { parts.push(`</${listType}>`); inList = false; listType = null; }
    };

    for (const rawLine of lines) {
        const trimmed = rawLine.trim();

        if (!trimmed) { closeList(); continue; }

        // Markdown headings (#, ##, ###)
        const mdHeading = trimmed.match(/^(#{1,3})\s+(.+)/);
        if (mdHeading) {
            closeList();
            parts.push(`<h4 class="hf-notes-heading">${escapeHtml(mdHeading[2])}</h4>`);
            continue;
        }

        // Bold-only line: **text** or **text:**
        const boldHeading = trimmed.match(/^\*\*([^*]+)\*\*:?\s*$/);
        if (boldHeading) {
            closeList();
            parts.push(`<h4 class="hf-notes-heading">${escapeHtml(boldHeading[1])}</h4>`);
            continue;
        }

        // Short line ending in ":" (e.g. "Session Structure:") → heading
        if (trimmed.endsWith(':') && trimmed.length < 80 && !/^[-•*\d]/.test(trimmed)) {
            closeList();
            parts.push(`<h4 class="hf-notes-heading">${escapeHtml(trimmed)}</h4>`);
            continue;
        }

        // Bullet point
        const bullet = trimmed.match(/^[-•*]\s+(.+)/);
        if (bullet) {
            if (listType !== 'ul') { closeList(); parts.push('<ul class="hf-notes-list">'); inList = true; listType = 'ul'; }
            parts.push(`<li>${inlineMarkdown(bullet[1])}</li>`);
            continue;
        }

        // Numbered list
        const numbered = trimmed.match(/^\d+[.)]\s+(.+)/);
        if (numbered) {
            if (listType !== 'ol') { closeList(); parts.push('<ol class="hf-notes-list">'); inList = true; listType = 'ol'; }
            parts.push(`<li>${inlineMarkdown(numbered[1])}</li>`);
            continue;
        }

        // Regular paragraph
        closeList();
        parts.push(`<p class="hf-notes-para">${inlineMarkdown(trimmed)}</p>`);
    }
    closeList();
    return parts.join('\n');
}

// Copy to clipboard function
function copyToClipboard(text) {
    navigator.clipboard.writeText(text).then(() => {
        showToast('Success', 'Copied to clipboard', 'success');
    }).catch(() => {
        showToast('Error', 'Failed to copy to clipboard', 'error');
    });
}

// Format JSON content for human-readable display
function formatSummaryContent(content) {
    if (!content) return '<div class="no-summary">No summary available.</div>';

    // Try to detect and parse JSON
    let data;
    try {
        // Check if content looks like JSON (starts with { or [)
        const trimmedContent = content.trim();
        if (trimmedContent.startsWith('{') || trimmedContent.startsWith('[')) {
            data = JSON.parse(trimmedContent);
        } else {
            // Not JSON, return as formatted text
            return `<div class="summary-text">${escapeHtml(content).replace(/\n/g, '<br>')}</div>`;
        }
    } catch (e) {
        // Not valid JSON, return as formatted text
        return `<div class="summary-text">${escapeHtml(content).replace(/\n/g, '<br>')}</div>`;
    }

    // Format the JSON object in a human-readable way
    function formatValue(value, indent = 0) {
        if (value === null || value === undefined) {
            return '<span class="summary-null">—</span>';
        }

        if (typeof value === 'object' && !Array.isArray(value)) {
            let html = '';

            // Add visual container for nested objects
            if (indent > 0) {
                html += `<div class="summary-object-container" style="margin-left: ${indent * 20}px;">`;
            }

            for (const [key, val] of Object.entries(value)) {
                const formattedKey = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());

                html += `<div class="summary-field">`;
                html += `<strong class="summary-key">${escapeHtml(formattedKey)}:</strong> `;

                if (typeof val === 'object' && val !== null) {
                    html += '<br>' + formatValue(val, indent + 1);
                } else {
                    html += formatValue(val, indent);
                }
                html += '</div>';
            }

            if (indent > 0) {
                html += '</div>';
            }

            return html;
        }

        if (Array.isArray(value)) {
            if (value.length === 0) {
                return '<span class="summary-empty">None</span>';
            }

            let html = '<div class="summary-list">';
            value.forEach((item) => {
                if (typeof item === 'object' && item !== null) {
                    html += `<div class="summary-list-item" style="margin-left: ${(indent + 1) * 20}px; margin-top: 8px;">`;
                    html += formatValue(item, indent + 1);
                    html += '</div>';
                } else {
                    html += `<div class="summary-list-item" style="margin-left: ${(indent + 1) * 20}px;">`;
                    html += `<span class="summary-bullet">•</span> ${escapeHtml(String(item))}`;
                    html += '</div>';
                }
            });
            html += '</div>';
            return html;
        }

        if (typeof value === 'boolean') {
            return `<span class="summary-boolean">${value ? 'Yes' : 'No'}</span>`;
        }

        if (typeof value === 'number') {
            return `<span class="summary-number">${value}</span>`;
        }

        // String value
        return `<span class="summary-value">${escapeHtml(String(value))}</span>`;
    }

    return '<div class="summary-formatted">' + formatValue(data) + '</div>';
}

// View Management Functions
function switchView(viewName) {
    console.log('Switching view to:', viewName);
    console.log('Available views:', Object.keys(views));

    // Leaving the main view abandons any in-progress project edit.
    if (viewName !== 'main' && editingProjectId) {
        exitProjectEditMode();
    }

    Object.keys(views).forEach(key => {
        const view = views[key];
        if (view) {
            if (key === viewName) {
                view.style.display = 'block';
                console.log(`Showing view: ${key}`);
            } else {
                view.style.display = 'none';
            }
        } else {
            console.warn(`View element not found: ${key}`);
        }
    });
    currentView = viewName;
}

function goBackToMain() {
    stopSummaryPolling(); // Stop polling when leaving project
    switchView('main');
    loadProjects();
    window.location.hash = '';
    currentProject = null;
}

function goBackToProject() {
    if (currentProject) {
        switchView('projectDetails');
        window.location.hash = `#project/${currentProject.id}`;
    } else {
        goBackToMain();
    }
}

function goBackToUsabilityReport() {
    if (currentInvocation?.toolInvocationId) {
        openUsabilityReportInvocation(currentInvocation.toolInvocationId);
    } else {
        goBackToProject();
    }
}

// Project Details Functions
function loadProjectFiles(files) {
    // Filter out generated reports (Excel Analysis and Usability Reports)
    // These are displayed separately in their own sections
    const userUploadedFiles = files.filter(file => {
        const s3Key = file.s3_key || '';

        // Only include files stored in the /files/ directory
        // Generated reports are stored in /excel/ and /usability-reports/ directories
        return s3Key.includes('/files/');
    });

    elements.filesCount.textContent = `${userUploadedFiles.length} file${userUploadedFiles.length !== 1 ? 's' : ''}`;

    if (userUploadedFiles.length === 0) {
        elements.filesList.innerHTML = '<div class="no-files">No files uploaded yet</div>';
        elements.generateExcelAnalysisBtn.disabled = true;
        elements.generateUsabilityReportBtn.disabled = true;
        return;
    }

    // Store file summaries in a global object for easy access
    window.fileSummaries = {};

    elements.filesList.innerHTML = userUploadedFiles.map(file => {
        // Store the full summary
        window.fileSummaries[file.id] = {
            name: file.original_name,
            summary: file.ai_summary || 'No summary available'
        };
        
        return `
            <div class="file-item" data-file-id="${file.id}">
                <div class="file-info">
                    <div class="file-header">
                        <div class="participant-label-container" onclick="editParticipantLabel(${file.id}, ${jsArg(file.participant_label || '')})">
                            <span class="participant-label editable" data-file-id="${file.id}" data-current-label="${escapeHtml(file.participant_label || '')}">
                                ${file.participant_label || 'No label'}
                            </span>
                            <i class="fas fa-edit participant-edit-icon"></i>
                        </div>
                        <div class="file-name">
                            <i class="fas fa-file"></i>
                            ${escapeHtml(file.original_name)}
                        </div>
                    </div>
                    <div class="file-meta">
                        ${formatFileSize(file.file_size)} • Uploaded ${formatDate(file.upload_date)}
                    </div>
                    ${file.ai_summary ? `
                        <div class="file-summary">
                            <strong>Summary:</strong> ${escapeHtml(file.ai_summary.substring(0, 150))}${file.ai_summary.length > 150 ? '...' : ''}
                        </div>
                    ` : ''}
                </div>
                <div class="file-actions">
                    <button class="btn btn-small btn-outline" onclick="viewFileSummary(${file.id})">
                        <i class="fas fa-eye"></i> View Summary
                    </button>
                    <button class="btn btn-small btn-danger" onclick="deleteFile(${file.id})">
                        <i class="fas fa-trash"></i> Delete
                    </button>
                </div>
            </div>
        `;
    }).join('');

    elements.generateExcelAnalysisBtn.disabled = false;
    elements.generateUsabilityReportBtn.disabled = false;
}

// File Upload Functions
function setupFileUpload() {
    const isUploadDisabled = () => elements.uploadArea?.classList.contains('disabled');

    // Click to upload
    elements.uploadArea?.addEventListener('click', () => {
        if (isUploadDisabled()) return;
        elements.fileInput.click();
    });

    // File input change
    elements.fileInput?.addEventListener('change', (e) => {
        handleFileUpload(Array.from(e.target.files));
    });

    // Drag and drop
    elements.uploadArea?.addEventListener('dragenter', (e) => {
        e.preventDefault();
        if (isUploadDisabled()) return;
        elements.uploadArea.classList.add('drag-over');
    });

    elements.uploadArea?.addEventListener('dragover', (e) => {
        e.preventDefault();
        if (isUploadDisabled()) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'copy';
        elements.uploadArea.classList.add('drag-over');
    });

    elements.uploadArea?.addEventListener('dragleave', (e) => {
        // Only remove the highlight when leaving the upload area entirely,
        // not when moving between its child elements.
        if (!elements.uploadArea.contains(e.relatedTarget)) {
            elements.uploadArea.classList.remove('drag-over');
        }
    });

    elements.uploadArea?.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        elements.uploadArea.classList.remove('drag-over');
        if (isUploadDisabled()) return;

        const files = Array.from(e.dataTransfer.files);
        if (files.length === 0) return;
        handleFileUpload(files);
    });
}

// Progress bar instances
let uploadProgressBar = null;
let reportProgressBar = null;
let createProjectProgressBar = null;

async function handleFileUpload(files) {
    if (!currentInvocation || files.length === 0) {
        return;
    }

    // Initialize upload progress bar
    if (!uploadProgressBar) {
        uploadProgressBar = new ProgressBar(elements.uploadProgress, {
            message: 'Preparing upload...',
            showPercentage: true,
            showSteps: true,
            steps: ['Upload', 'Processing', 'Complete']
        });
    }

    uploadProgressBar.reset();
    uploadProgressBar.show();

    try {
        // Step 1: Upload files
        uploadProgressBar.nextStep('Uploading files...');

        const uploadedFiles = [];

        for (let i = 0; i < files.length; i++) {
            const file = files[i];

            // Update progress for current file
            const fileProgress = ((i / files.length) * 25); // 0-25% for upload phase
            uploadProgressBar.updateProgress(fileProgress, `Uploading ${file.name} (${i + 1}/${files.length})`);

            // Create form data
            const formData = new FormData();
            formData.append('file', file);

            // Upload file using direct fetch with timeout and better error handling
            const url = `${API_BASE_URL}/usability-reports/${currentInvocation.id}/upload`;
            const token = AuthManager.getToken();

            console.log(`[Upload] Uploading file ${i + 1}/${files.length}: ${file.name}`);

            // Create an AbortController for timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout

            try {
                const response = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    body: formData,
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                console.log(`[Upload] Response status: ${response.status}`);

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`[Upload] Error response:`, errorText);
                    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                console.log(`[Upload] File uploaded successfully:`, result);
                uploadedFiles.push(result.file);
            } catch (fetchError) {
                clearTimeout(timeoutId);

                if (fetchError.name === 'AbortError') {
                    console.error(`[Upload] Request timed out after 2 minutes`);
                    throw new Error(`Upload timed out. The file may be too large or the server is not responding.`);
                }

                console.error(`[Upload] Fetch error:`, fetchError);
                throw fetchError;
            }
        }

        // Check if files are being processed asynchronously (status 202)
        const isAsyncProcessing = uploadedFiles.some(f => f.processing_status === 'processing');

        if (isAsyncProcessing) {
            // Step 2: Processing (async AI extraction and summarization)
            uploadProgressBar.nextStep('Processing files with AI...');

            // Poll for file processing status
            await pollFileProcessingStatus(uploadedFiles, uploadProgressBar);

            // Reload files for the current invocation
            await loadInvocationFiles(currentInvocation.id);

            showToast('Success', `${files.length} file${files.length !== 1 ? 's' : ''} processed successfully!`, 'success');

        } else {
            // Legacy synchronous processing - files already complete
            uploadProgressBar.nextStep('Processing complete...');
            await new Promise(resolve => setTimeout(resolve, 500));

            uploadProgressBar.complete(`${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully!`);

            showToast('Success', `${files.length} file${files.length !== 1 ? 's' : ''} uploaded successfully`, 'success');

            // Reload files for the current invocation
            await loadInvocationFiles(currentInvocation.id);
        }

    } catch (error) {
        console.error('Error uploading files:', error);
        uploadProgressBar.updateProgress(0, 'Upload failed');
        uploadProgressBar.hide();
        showToast('Error', 'Failed to upload files', 'error');
    } finally {
        elements.fileInput.value = '';
    }
}

/**
 * Poll file processing status for async Step Functions workflow
 * Updates progress bar based on processing status
 */
async function pollFileProcessingStatus(files, progressBar) {
    const maxAttempts = 120; // 2 minutes max (120 * 1 second)
    let attempt = 0;
    let delay = 1000; // Start with 1 second
    const maxDelay = 5000; // Max 5 seconds between polls

    const fileIds = files.filter(f => f.processing_status === 'processing').map(f => f.id);

    while (attempt < maxAttempts && fileIds.length > 0) {
        attempt++;

        // Poll each file's status
        const statusChecks = await Promise.all(
            fileIds.map(async (fileId) => {
                try {
                    const response = await ApiClient.get(`/files/${fileId}/status`);
                    return response;
                } catch (error) {
                    console.error(`Error checking status for file ${fileId}:`, error);
                    return null;
                }
            })
        );

        // Check statuses
        let allCompleted = true;
        let anyFailed = false;
        let anyNeedingReview = false;
        let totalQualityScore = 0;
        let completedCount = 0;

        for (const status of statusChecks) {
            if (!status) continue;

            if (status.status === 'completed') {
                completedCount++;
                totalQualityScore += status.qualityScore || 0;
            } else if (status.status === 'processing') {
                allCompleted = false;
                // Update progress bar with processing message
                progressBar.updateProgress(50, 'Processing (may take several minutes)...');
            } else if (status.status === 'failed') {
                anyFailed = true;
            } else if (status.status === 'pending_review') {
                anyNeedingReview = true;
            }
        }

        if (allCompleted) {
            // All files completed successfully
            const avgQualityScore = completedCount > 0 ? Math.round(totalQualityScore / completedCount) : 0;

            // Step 3: Complete
            progressBar.complete(`Processing complete! Quality score: ${avgQualityScore}/100`);
            return;
        }

        if (anyFailed) {
            progressBar.updateProgress(0, 'Processing failed');
            progressBar.hide();
            throw new Error('File processing failed');
        }

        if (anyNeedingReview) {
            progressBar.nextStep('Requires human review...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            progressBar.complete('Queued for human review');
            return;
        }

        // Wait before next poll (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, delay));
        delay = Math.min(delay * 1.2, maxDelay); // Increase delay up to max
    }

    // Timeout reached
    if (attempt >= maxAttempts) {
        progressBar.updateProgress(0, 'Processing timeout - please refresh to check status');
        await new Promise(resolve => setTimeout(resolve, 3000));
        progressBar.hide();
    }
}

// Summary Generation Functions
function displayExistingAnalysis(project) {
    if (project.excel_analysis_filename && project.excel_analysis_generated_at) {
        // Show the existing analysis section
        elements.existingExcelAnalysis.style.display = 'block';

        // Update the filename
        elements.existingExcelAnalysis.querySelector('.analysis-filename').textContent = project.excel_analysis_filename;

        // Format and display the generation date
        const generatedDate = new Date(project.excel_analysis_generated_at);
        const formattedDate = generatedDate.toLocaleString();
        elements.existingExcelAnalysis.querySelector('.analysis-date').textContent = `Generated: ${formattedDate}`;

        // Update the generate button text
        elements.generateExcelAnalysisBtn.innerHTML = '<i class="fas fa-sync"></i> Regenerate Excel Analysis';

    } else {
        // Hide the existing analysis section
        elements.existingExcelAnalysis.style.display = 'none';

        // Reset the generate button text
        elements.generateExcelAnalysisBtn.innerHTML = '<i class="fas fa-magic"></i> Generate Excel Analysis';
    }
}

async function downloadExistingAnalysis() {
    if (!currentInvocation) return;

    try {
        await ApiClient.downloadFile(
            `/usability-reports/${currentInvocation.id}/download-excel-analysis`,
            currentInvocation.excel_analysis_filename
        );
        showToast('Success', 'Excel analysis downloaded successfully', 'success');
    } catch (error) {
        console.error('Error downloading existing analysis:', error);
        showToast('Error', 'Failed to download analysis file', 'error');
    }
}

function displayExistingUsabilityReport(project) {
    if (project.usability_report_filename && project.usability_report_generated_at) {
        // Show the existing report section
        elements.existingUsabilityReport.style.display = 'block';

        // Update the filename
        elements.existingUsabilityReport.querySelector('.report-filename').textContent = project.usability_report_filename;

        // Format and display the generation date
        const generatedDate = new Date(project.usability_report_generated_at);
        const formattedDate = generatedDate.toLocaleString();
        elements.existingUsabilityReport.querySelector('.report-date').textContent = `Generated: ${formattedDate}`;

        // Update the generate button text
        elements.generateUsabilityReportBtn.innerHTML = '<i class="fas fa-sync"></i> Regenerate Usability Report';

    } else {
        // Hide the existing report section
        elements.existingUsabilityReport.style.display = 'none';

        // Reset the generate button text
        elements.generateUsabilityReportBtn.innerHTML = '<i class="fas fa-file-alt"></i> Generate Usability Report';
    }
}

async function downloadExistingReport() {
    if (!currentInvocation) return;

    try {
        await ApiClient.downloadFile(
            `/usability-reports/${currentInvocation.id}/download-usability-report`,
            currentInvocation.usability_report_filename
        );
        showToast('Success', 'Usability report downloaded successfully', 'success');
    } catch (error) {
        console.error('Error downloading existing report:', error);
        showToast('Error', 'Failed to download report file', 'error');
    }
}

// Usability report progress bar instance
let usabilityProgressBar = null;

async function generateUsabilityReport() {
    if (!currentInvocation) return;

    // Initialize usability progress bar
    if (!usabilityProgressBar) {
        usabilityProgressBar = new ProgressBar(elements.reportProgress, {
            message: 'Preparing report...',
            showPercentage: true,
            showSteps: true,
            steps: ['Initializing', 'Generating content', 'Finalizing', 'Complete']
        });
    }

    usabilityProgressBar.reset();
    usabilityProgressBar.show();
    elements.generateUsabilityReportBtn.disabled = true;

    try {
        // Step 1: Trigger Step Functions workflow
        usabilityProgressBar.nextStep('Initializing report generation...');
        await ApiClient.post(`/usability-reports/${currentInvocation.id}/generate-usability-report`, {});

        // Step 2: Poll for completion
        usabilityProgressBar.nextStep('Generating content (may take 2-3 minutes)...');
        await pollInvocationReportStatus(currentInvocation.id, usabilityProgressBar);

        // Reload invocation to get updated report info
        await openUsabilityReportInvocation(currentInvocation.toolInvocationId);

        showToast('Success', 'Usability report generated successfully', 'success');

    } catch (error) {
        console.error('Error generating usability report:', error);
        usabilityProgressBar.updateProgress(0, 'Report generation failed');
        usabilityProgressBar.hide();
        showToast('Error', 'Failed to generate usability report', 'error');
    } finally {
        elements.generateUsabilityReportBtn.disabled = false;
    }
}

/**
 * Poll report generation status until complete, failed, or timeout
 */
async function pollReportGenerationStatus(projectId, progressBar) {
    const maxAttempts = 180; // 3 minutes max (180 * 1 second)
    let attempt = 0;
    let delay = 2000; // Start with 2 seconds (report generation is slower)
    const maxDelay = 5000; // Max 5 seconds between polls

    while (attempt < maxAttempts) {
        attempt++;

        try {
            const status = await ApiClient.get(`/projects/${projectId}/report-generation-status`);

            console.log(`Report status poll ${attempt}:`, status);

            // Check status
            if (status.status === 'completed') {
                // Step 3: Quality check complete
                progressBar.nextStep(`Quality check complete (Score: ${status.qualityScore || 'N/A'})...`);
                await new Promise(resolve => setTimeout(resolve, 500));

                // Step 4: Complete
                progressBar.complete(`Report complete! Quality score: ${status.qualityScore || 'N/A'}/100`);
                return;
            } else if (status.status === 'processing') {
                // Update progress bar with iteration info
                const iterationMsg = status.iterations > 0
                    ? ` (Iteration ${status.iterations}/3)`
                    : '';
                progressBar.updateProgress(50, `Generating content ${iterationMsg}...`);
            } else if (status.status === 'failed') {
                progressBar.updateProgress(0, 'Report generation failed');
                progressBar.hide();
                throw new Error('Report generation failed');
            } else if (status.status === 'pending_review') {
                progressBar.nextStep('Requires human review...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                progressBar.complete('Queued for human review');
                return;
            }

            // Wait before next poll (exponential backoff)
            await new Promise(resolve => setTimeout(resolve, delay));
            delay = Math.min(delay * 1.1, maxDelay); // Gradual increase

        } catch (error) {
            console.error(`Error checking report status (attempt ${attempt}):`, error);

            // If it's a 404 or network error, continue polling
            if (attempt < maxAttempts - 10) {
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            } else {
                throw error; // Throw on last few attempts
            }
        }
    }

    // Timeout reached
    progressBar.updateProgress(0, 'Report generation timeout - please check status later');
    await new Promise(resolve => setTimeout(resolve, 3000));
    progressBar.hide();
    throw new Error('Report generation timeout');
}

async function pollInvocationReportStatus(invocationId, progressBar) {
    const maxAttempts = 180;
    let attempt = 0;
    let delay = 2000;
    const maxDelay = 5000;

    while (attempt < maxAttempts) {
        attempt++;

        try {
            const status = await ApiClient.get(`/usability-reports/${invocationId}/report-generation-status`);

            if (status.status === 'completed') {
                progressBar.nextStep(`Quality check complete (Score: ${status.qualityScore || 'N/A'})...`);
                await new Promise(resolve => setTimeout(resolve, 500));
                progressBar.complete(`Report complete! Quality score: ${status.qualityScore || 'N/A'}/100`);
                return;
            } else if (status.status === 'processing') {
                const iterationMsg = status.iterations > 0 ? ` (Iteration ${status.iterations}/3)` : '';
                progressBar.updateProgress(50, `Generating content${iterationMsg}...`);
            } else if (status.status === 'failed') {
                progressBar.updateProgress(0, 'Report generation failed');
                progressBar.hide();
                throw new Error('Report generation failed');
            } else if (status.status === 'pending_review') {
                progressBar.nextStep('Requires human review...');
                await new Promise(resolve => setTimeout(resolve, 2000));
                progressBar.complete('Queued for human review');
                return;
            }

            await new Promise(resolve => setTimeout(resolve, delay));
            delay = Math.min(delay * 1.1, maxDelay);

        } catch (error) {
            if (attempt < maxAttempts - 10) {
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
            } else {
                throw error;
            }
        }
    }

    progressBar.updateProgress(0, 'Report generation timeout - please check status later');
    await new Promise(resolve => setTimeout(resolve, 3000));
    progressBar.hide();
    throw new Error('Report generation timeout');
}

async function generateExcelAnalysis() {
    if (!currentInvocation) return;

    // Initialize analysis progress bar
    if (!reportProgressBar) {
        reportProgressBar = new ProgressBar(elements.summaryProgress, {
            message: 'Preparing analysis...',
            showPercentage: true,
            showSteps: true,
            steps: ['Analyzing files', 'Generating Excel', 'Finalizing', 'Complete']
        });
    }

    reportProgressBar.reset();
    reportProgressBar.show();
    elements.generateExcelAnalysisBtn.disabled = true;
    elements.summaryResult.style.display = 'none';

    try {
        // Step 1: Analyzing files
        reportProgressBar.nextStep('Analyzing invocation files...');
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Step 2: Generating Excel
        reportProgressBar.nextStep('Generating Excel analysis...');
        await ApiClient.post(`/usability-reports/${currentInvocation.id}/generate-excel`, {});

        // Step 3: Finalizing
        reportProgressBar.nextStep('Finalizing analysis...');
        await new Promise(resolve => setTimeout(resolve, 500));

        // Step 4: Complete
        reportProgressBar.complete('Excel analysis generated successfully!');

        // Reload invocation to get updated analysis info
        await openUsabilityReportInvocation(currentInvocation.toolInvocationId);

        showToast('Success', 'Excel analysis generated successfully', 'success');

    } catch (error) {
        console.error('Error generating analysis:', error);
        reportProgressBar.updateProgress(0, 'Analysis failed');
        reportProgressBar.hide();
        showToast('Error', 'Failed to generate analysis', 'error');
    } finally {
        elements.generateExcelAnalysisBtn.disabled = false;
    }
}

// Hash-based routing
function handleHashChange() {
    const hash = window.location.hash;

    if (hash === '#hf-advisor') {
        if (currentView !== 'hfAdvisor') {
            switchView('hfAdvisor');
            hfAdvisorInit();
        }
    } else if (hash === '#dc-analyzer') {
        if (currentView !== 'designChange') {
            switchView('designChange');
            dcAnalyzerInit();
        }
    } else if (hash === '#admin') {
        if (currentView !== 'admin') {
            switchView('admin');
            switchAdminTab('users');
        }
    } else if (hash.startsWith('#study/')) {
        // Study detail URL: #study/456
        const studyId = parseInt(hash.split('/')[1]);
        if (studyId && studyId !== currentStudy?.id) {
            openStudy(studyId);
        } else if (currentView !== 'studyDetail') {
            switchView('studyDetail');
        }
    } else if (hash.startsWith('#invocation/')) {
        // Usability report invocation URL: #invocation/789
        const invocationId = parseInt(hash.split('/')[1]);
        if (invocationId && invocationId !== currentInvocation?.id) {
            openUsabilityReportInvocation(invocationId);
        } else if (currentView !== 'usabilityReport') {
            switchView('usabilityReport');
        }
    } else if (hash.startsWith('#project/')) {
        const hashParts = hash.split('/');
        const projectId = parseInt(hashParts[1]);

        if (hashParts.length >= 4 && hashParts[2] === 'file') {
            // File summary URL: #project/123/file/456
            const fileId = parseInt(hashParts[3]);
            if (!currentProject || currentProject.id !== projectId) {
                openProject(projectId).then(() => {
                    if (window.fileSummaries?.[fileId]) viewFileSummary(fileId);
                });
            } else if (window.fileSummaries?.[fileId]) {
                viewFileSummary(fileId);
            }
        } else {
            // Project details URL: #project/123
            if (projectId && projectId !== currentProject?.id) {
                openProject(projectId);
            } else if (currentView !== 'projectDetails') {
                switchView('projectDetails');
            }
        }
    } else {
        // No hash or unrecognized hash - go to main
        if (currentView !== 'main') {
            goBackToMain();
        }
    }
}

// ==================== CATEGORY & QUESTION MANAGEMENT ====================

// Load categories for a project
async function loadCategories(projectId) {
    try {
        const response = await ApiClient.get(`/projects/${projectId}/categories`);
        const categories = response.categories || [];
        displayCategories(categories);
    } catch (error) {
        console.error('Error loading categories:', error);
        elements.categoriesList.innerHTML = '<div class="no-categories"><i class="fas fa-folder-open"></i><p>Failed to load categories</p></div>';
    }
}

// Display categories in the UI
function displayCategories(categories) {
    if (!categories || categories.length === 0) {
        elements.categoriesList.innerHTML = `
            <div class="no-categories">
                <i class="fas fa-folder-open"></i>
                <p>No categories yet. Click "Add Category" to create one.</p>
            </div>
        `;
        return;
    }

    elements.categoriesList.innerHTML = categories.map(category => `
        <div class="category-item" data-category-id="${category.id}">
            <div class="category-header">
                <div class="category-info">
                    <div class="category-name">
                        <span class="category-order-badge" title="Drag to reorder">${category.display_order}</span>
                        ${escapeHtml(category.name)}
                    </div>
                    ${category.description ? `<div class="category-description">${escapeHtml(category.description)}</div>` : ''}
                </div>
                <div class="category-actions">
                    <button class="btn btn-icon btn-outline" onclick="editCategory(${category.id})" title="Edit category">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-outline" onclick="deleteCategory(${category.id})" title="Delete category">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
            <div class="category-questions" id="questions-${category.id}">
                <div class="loading">Loading questions...</div>
            </div>
        </div>
    `).join('');

    // Load questions for each category
    categories.forEach(category => {
        loadCategoryQuestions(category.id);
    });
}

// Load questions for a specific category
async function loadCategoryQuestions(categoryId) {
    try {
        const response = await ApiClient.get(`/categories/${categoryId}/questions`);
        const questions = response.questions || [];
        displayCategoryQuestions(categoryId, questions);
    } catch (error) {
        console.error('Error loading questions:', error);
        document.getElementById(`questions-${categoryId}`).innerHTML = '<div class="no-questions">Failed to load questions</div>';
    }
}

// Display questions for a category
function displayCategoryQuestions(categoryId, questions) {
    const container = document.getElementById(`questions-${categoryId}`);

    if (!questions || questions.length === 0) {
        container.innerHTML = `
            <div class="category-questions-header">
                <h5>Questions (0)</h5>
                <button class="btn btn-small btn-primary" onclick="addQuestionToCategory(${categoryId})">
                    <i class="fas fa-plus"></i> Add Question
                </button>
            </div>
            <div class="no-questions">No questions in this category</div>
        `;
        return;
    }

    container.innerHTML = `
        <div class="category-questions-header">
            <h5>Questions (${questions.length})</h5>
            <button class="btn btn-small btn-primary" onclick="addQuestionToCategory(${categoryId})">
                <i class="fas fa-plus"></i> Add Question
            </button>
        </div>
        ${questions.map(q => `
            <div class="question-item">
                <div class="question-text">${escapeHtml(q.question)}</div>
                <div class="question-actions">
                    <button class="btn btn-icon btn-outline" onclick="editQuestion(${q.id}, ${categoryId})" title="Edit question">
                        <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-outline" onclick="deleteQuestionFromCategory(${q.id})" title="Delete question">
                        <i class="fas fa-trash"></i>
                    </button>
                </div>
            </div>
        `).join('')}
    `;
}

// Update the category name char counter and warning state
function updateCategoryNameCounter() {
    const counter = elements.categoryNameCounter;
    const input = elements.categoryName;
    if (!counter || !input) return;
    const max = parseInt(input.getAttribute('maxlength'), 10) || 100;
    const len = input.value.length;
    counter.textContent = `${len} / ${max}`;
    counter.classList.toggle('at-limit', len >= max);
    counter.classList.toggle('near-limit', len >= max * 0.9 && len < max);
}

// Show add category inline form
function showAddCategoryModal() {
    elements.categoryModalTitle.textContent = 'Add New Category';
    elements.categoryId.value = '';
    elements.categoryProjectId.value = currentProject?.id || '';
    elements.categoryName.value = '';
    elements.categoryDescription.value = '';
    elements.categoryFormError.style.display = 'none';
    document.getElementById('inlineCsvImport').style.display = 'none';
    if (elements.categoryQuestionModal) elements.categoryQuestionModal.style.display = 'none';
    elements.inlineCategoryForm.style.display = 'block';
    updateCategoryNameCounter();
    elements.categoryName.focus();
}

// Edit category
async function editCategory(categoryId) {
    try {
        // Fetch categories from the correct endpoint based on context
        let categories;
        if (currentInvocation?.id) {
            const response = await ApiClient.get(`/usability-reports/${currentInvocation.id}/categories`);
            categories = response.categories || [];
        } else {
            const response = await ApiClient.get(`/projects/${currentProject.id}/categories`);
            categories = response.categories || [];
        }
        const category = categories.find(c => c.id === categoryId);

        if (!category) {
            showToast('Error', 'Category not found', 'error');
            return;
        }

        elements.categoryModalTitle.textContent = 'Edit Category';
        elements.categoryId.value = category.id;
        elements.categoryProjectId.value = category.project_id;
        elements.categoryName.value = category.name;
        elements.categoryDescription.value = category.description || '';
        elements.categoryFormError.style.display = 'none';
        document.getElementById('inlineCsvImport').style.display = 'none';
        elements.inlineCategoryForm.style.display = 'block';
        updateCategoryNameCounter();
        elements.categoryName.focus();
    } catch (error) {
        console.error('Error loading category:', error);
        showToast('Error', 'Failed to load category details', 'error');
    }
}

// Delete category
async function deleteCategory(categoryId) {
    if (!confirm('Are you sure you want to delete this category? All questions in this category will also be deleted.')) {
        return;
    }

    try {
        await ApiClient.delete(`/categories/${categoryId}`);
        showToast('Success', 'Category deleted successfully', 'success');
        if (currentInvocation?.id) {
            await loadInvocationCategories(currentInvocation.id);
        } else {
            await loadCategories(currentProject.id);
        }
    } catch (error) {
        console.error('Error deleting category:', error);
        showToast('Error', 'Failed to delete category', 'error');
    }
}

// Handle category form submission
async function handleCategoryFormSubmit(e) {
    e.preventDefault();

    const categoryId = elements.categoryId.value;
    const projectId = elements.categoryProjectId.value;
    const name = elements.categoryName.value.trim();
    const description = elements.categoryDescription.value.trim();
    const displayOrder = 0;

    if (!name) {
        elements.categoryFormError.textContent = 'Category name is required';
        elements.categoryFormError.style.display = 'block';
        return;
    }

    try {
        if (categoryId) {
            // Update existing category
            await ApiClient.put(`/categories/${categoryId}`, {
                name,
                description,
                display_order: displayOrder
            });
            showToast('Success', 'Category updated successfully', 'success');
        } else if (currentInvocation?.id) {
            // Create category scoped to usability report invocation
            await ApiClient.post(`/usability-reports/${currentInvocation.id}/categories`, {
                name,
                description,
                display_order: displayOrder
            });
            showToast('Success', 'Category created successfully', 'success');
        } else {
            // Create project-scoped category
            await ApiClient.post(`/projects/${projectId}/categories`, {
                name,
                description,
                display_order: displayOrder
            });
            showToast('Success', 'Category created successfully', 'success');
        }

        elements.inlineCategoryForm.style.display = 'none';
        if (currentInvocation?.id) {
            await loadInvocationCategories(currentInvocation.id);
        } else {
            await loadCategories(currentProject.id);
        }
    } catch (error) {
        console.error('Error saving category:', error);
        elements.categoryFormError.textContent = error.message || 'Failed to save category';
        elements.categoryFormError.style.display = 'block';
    }
}

// Add question to category
function addQuestionToCategory(categoryId) {
    elements.categoryQuestionModalTitle.textContent = 'Add Question';
    elements.questionId.value = '';
    elements.questionCategoryId.value = categoryId;
    elements.questionText.value = '';
    elements.questionFormError.style.display = 'none';
    elements.inlineCategoryForm.style.display = 'none';
    document.getElementById('inlineCsvImport').style.display = 'none';
    elements.categoryQuestionModal.style.display = 'block';
    elements.questionText.focus();
}

// Edit question
async function editQuestion(questionId, categoryId) {
    try {
        const response = await ApiClient.get(`/categories/${categoryId}/questions`);
        const questions = response.questions || [];
        const question = questions.find(q => q.id === questionId);

        if (!question) {
            showToast('Error', 'Question not found', 'error');
            return;
        }

        elements.categoryQuestionModalTitle.textContent = 'Edit Question';
        elements.questionId.value = question.id;
        elements.questionCategoryId.value = categoryId;
        elements.questionText.value = question.question;
        elements.questionFormError.style.display = 'none';
        elements.inlineCategoryForm.style.display = 'none';
        document.getElementById('inlineCsvImport').style.display = 'none';
        elements.categoryQuestionModal.style.display = 'block';
        elements.questionText.focus();
    } catch (error) {
        console.error('Error loading question:', error);
        showToast('Error', 'Failed to load question details', 'error');
    }
}

// Delete question from category
async function deleteQuestionFromCategory(questionId) {
    if (!confirm('Are you sure you want to delete this question?')) {
        return;
    }

    try {
        await ApiClient.delete(`/questions/${questionId}`);
        showToast('Success', 'Question deleted successfully', 'success');
        if (currentInvocation?.id) {
            await loadInvocationCategories(currentInvocation.id);
        } else {
            await loadCategories(currentProject.id);
        }
    } catch (error) {
        console.error('Error deleting question:', error);
        showToast('Error', 'Failed to delete question', 'error');
    }
}

// Handle question form submission
async function handleQuestionFormSubmit(e) {
    e.preventDefault();

    const questionId = elements.questionId.value;
    const categoryId = elements.questionCategoryId.value;
    const questionText = elements.questionText.value.trim();

    if (!questionText) {
        elements.questionFormError.textContent = 'Question text is required';
        elements.questionFormError.style.display = 'block';
        return;
    }

    try {
        if (questionId) {
            // Update existing question
            await ApiClient.put(`/questions/${questionId}`, {
                question: questionText
            });
            showToast('Success', 'Question updated successfully', 'success');
        } else {
            // Create new question
            await ApiClient.post(`/categories/${categoryId}/questions`, {
                question: questionText
            });
            showToast('Success', 'Question created successfully', 'success');
        }

        elements.categoryQuestionModal.style.display = 'none';
        if (currentInvocation?.id) {
            await loadInvocationCategories(currentInvocation.id);
        } else {
            await loadCategories(currentProject.id);
        }
    } catch (error) {
        console.error('Error saving question:', error);
        elements.questionFormError.textContent = error.message || 'Failed to save question';
        elements.questionFormError.style.display = 'block';
    }
}

// Close category modal
function closeCategoryModal() {
    elements.inlineCategoryForm.style.display = 'none';
}

// Close question modal
function closeCategoryQuestionModal() {
    elements.categoryQuestionModal.style.display = 'none';
}

// Make functions globally available
window.editCategory = editCategory;
window.deleteCategory = deleteCategory;
window.addQuestionToCategory = addQuestionToCategory;
window.editQuestion = editQuestion;
window.deleteQuestionFromCategory = deleteQuestionFromCategory;

// OAuth callback and tenant selection helpers
function handleOAuthCallback() {
    const hash = window.location.hash;

    // Handle error params from query string (Cognito errors)
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('error');
    if (oauthError) {
        window.history.replaceState(null, '', window.location.pathname);
        showLoginError(`Authentication failed: ${oauthError}`);
        return false;
    }

    // Handle successful OAuth callback (token in hash)
    if (hash.includes('token=')) {
        const params = new URLSearchParams(hash.substring(1));
        const token = params.get('token');
        const userJson = params.get('user');

        if (token && userJson) {
            try {
                const user = JSON.parse(decodeURIComponent(userJson));
                AuthManager.saveToken(decodeURIComponent(token));
                AuthManager.saveUser(user);
                window.history.replaceState(null, '', window.location.pathname);
                return true;
            } catch (e) {
                console.error('Failed to parse OAuth callback data:', e);
            }
        }
    }

    // Handle new user needing tenant selection
    if (hash.includes('needs_tenant=true')) {
        const params = new URLSearchParams(hash.substring(1));
        const pendingId = params.get('pending_id');
        const email = params.get('email');
        const name = params.get('name');
        window.history.replaceState(null, '', window.location.pathname);
        showTenantSelection(pendingId, email, name);
        return 'tenant_selection';
    }

    return false;
}

async function showTenantSelection(pendingId) {
    showLoginView();

    // Load available tenants
    try {
        const response = await fetch(`${API_BASE_URL}/tenants`);
        if (!response.ok) throw new Error('Failed to load organizations');
        const data = await response.json();

        const select = elements.tenantSelect;
        select.innerHTML = '<option value="">-- Select Organization --</option>';
        (data.tenants || data).forEach(tenant => {
            if (tenant.active) {
                const option = document.createElement('option');
                option.value = tenant.id;
                option.textContent = tenant.name;
                select.appendChild(option);
            }
        });
    } catch (e) {
        console.error('Failed to load tenants:', e);
    }

    // Show the modal
    elements.tenantSelectModal.style.display = 'flex';

    // Store pending ID for the confirm handler
    elements.confirmTenantBtn.onclick = async () => {
        const tenantId = elements.tenantSelect.value;
        if (!tenantId) {
            elements.tenantSelectError.textContent = 'Please select an organization';
            elements.tenantSelectError.style.display = 'block';
            return;
        }

        elements.confirmTenantBtn.disabled = true;
        elements.confirmTenantBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Setting up...';

        try {
            const response = await fetch(`${API_BASE_URL}/auth/oauth/tenant-select`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pendingId, tenantId: parseInt(tenantId) })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Registration failed');
            }

            const data = await response.json();
            AuthManager.saveToken(data.token);
            AuthManager.saveUser(data.user);
            elements.tenantSelectModal.style.display = 'none';
            showAppView();
            showToast('Success', 'Account created successfully', 'success');
        } catch (error) {
            elements.tenantSelectError.textContent = error.message;
            elements.tenantSelectError.style.display = 'block';
        } finally {
            elements.confirmTenantBtn.disabled = false;
            elements.confirmTenantBtn.innerHTML = '<i class="fas fa-check"></i> Continue';
        }
    };
}

const OAUTH_CONFIG_CACHE_KEY = 'oauthConfigCache';

function applyOAuthConfig(config) {
    // Email/password is always the default screen.
    if (elements.oauthLoginSection) elements.oauthLoginSection.style.display = 'none';
    if (elements.localLoginSection) elements.localLoginSection.style.display = 'block';
}

function getCachedOAuthConfig() {
    try {
        const cached = localStorage.getItem(OAUTH_CONFIG_CACHE_KEY);
        return cached ? JSON.parse(cached) : null;
    } catch (e) {
        return null;
    }
}

function setCachedOAuthConfig(config) {
    try {
        // Only cache the global config (no email-specific configs)
        localStorage.setItem(OAUTH_CONFIG_CACHE_KEY, JSON.stringify(config));
    } catch (e) {
        // Ignore storage errors
    }
}

async function checkOAuthConfig(email = null) {
    try {
        const url = email
            ? `${API_BASE_URL}/auth/oauth/config?email=${encodeURIComponent(email)}`
            : `${API_BASE_URL}/auth/oauth/config`;
        const response = await fetch(url);
        if (response.ok) {
            const config = await response.json();
            if (!email) setCachedOAuthConfig(config);
            return config;
        }
    } catch (e) {
        // OAuth not available — fall through to cache or default
    }
    // Use cached config if available, otherwise default to password login
    return getCachedOAuthConfig() || { enabled: false, allowPasswordLogin: true };
}

// Initialize app
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // Handle OAuth callback first (before auth check)
        const oauthResult = handleOAuthCallback();

        if (oauthResult === true) {
            // Successfully authenticated via OAuth
            showAppView();
            showToast('Success', 'Logged in successfully', 'success');
        } else if (oauthResult === 'tenant_selection') {
            // Tenant selection flow in progress, don't change view
        } else if (AuthManager.isAuthenticated()) {
            showAppView();
        } else {
            // Check whether a token exists but is expired — give a clear reason
            if (AuthManager.getToken() && AuthManager.isTokenExpired()) {
                AuthManager.removeToken();
                AuthManager.removeUser();
                showLoginView();
                showToast('Session ended', 'Your session expired. Please log in again.', 'info');
            } else {
                showLoginView();
            }

            // Apply cached config immediately to avoid flicker on next load
            const cachedConfig = getCachedOAuthConfig();
            if (cachedConfig) applyOAuthConfig(cachedConfig);

            // Fetch fresh config and update UI if it differs
            const oauthConfig = await checkOAuthConfig();
            applyOAuthConfig(oauthConfig);
        }

        // Track current email for OAuth flow
        let currentLoginEmail = null;

        // Email lookup form - first step of OAuth flow
        elements.emailLookupForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = elements.workEmail?.value.trim();
            if (!email) return;

            currentLoginEmail = email;
            elements.continueBtn.disabled = true;
            elements.continueBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Looking up...';
            hideLoginError();

            try {
                const [result, config] = await Promise.all([
                    AuthManager.lookupEmail(email),
                    checkOAuthConfig(email)
                ]);

                if (result.found && result.identityProvider) {
                    // Known tenant with specific IdP - show SSO button
                    elements.providerMessage.textContent = `Sign in to ${result.tenantName}`;
                    elements.ssoButtonText.textContent = `Continue with ${result.tenantName}`;
                    elements.ssoSignInBtn.style.display = 'block';
                    elements.ssoSignInBtn.dataset.provider = result.identityProvider;
                    elements.googleSignInBtn.style.display = 'none';
                    elements.microsoftSignInBtn.style.display = 'none';
                } else if (result.found) {
                    // Known tenant but no specific IdP - show generic options
                    elements.providerMessage.textContent = `Sign in to ${result.tenantName}`;
                    elements.ssoSignInBtn.style.display = 'none';
                    elements.googleSignInBtn.style.display = 'block';
                    elements.microsoftSignInBtn.style.display = 'block';
                } else {
                    // Unknown domain - show all options
                    elements.providerMessage.textContent = 'Choose your sign-in method';
                    elements.ssoSignInBtn.style.display = 'none';
                    elements.googleSignInBtn.style.display = 'block';
                    elements.microsoftSignInBtn.style.display = 'block';
                }

                // Show password login option if allowed for this tenant
                if (elements.passwordLoginOption) {
                    if (config.allowPasswordLogin) {
                        elements.passwordLoginOption.style.display = 'block';
                        // Pre-fill the email in the password form
                        if (elements.loginEmail) {
                            elements.loginEmail.value = email;
                        }
                    } else {
                        elements.passwordLoginOption.style.display = 'none';
                    }
                }

                // Show provider buttons
                elements.emailEntryStep.style.display = 'none';
                elements.providerButtonsStep.style.display = 'block';

            } catch (error) {
                showLoginError('Failed to look up email. Please try again.');
            } finally {
                elements.continueBtn.disabled = false;
                elements.continueBtn.innerHTML = '<i class="fas fa-arrow-right"></i> Continue';
            }
        });

        // Toggle: switch from SSO section to plain email/password
        elements.switchToPasswordBtn?.addEventListener('click', () => {
            if (elements.oauthLoginSection) elements.oauthLoginSection.style.display = 'none';
            if (elements.localLoginSection) elements.localLoginSection.style.display = 'block';
            hideLoginError();
            elements.loginEmail?.focus();
        });

        // Toggle: switch from plain email/password back to SSO section
        elements.switchToSsoBtn?.addEventListener('click', () => {
            if (elements.localLoginSection) elements.localLoginSection.style.display = 'none';
            if (elements.oauthLoginSection) elements.oauthLoginSection.style.display = 'flex';
            // Reset SSO section back to email entry step
            if (elements.emailEntryStep) elements.emailEntryStep.style.display = 'block';
            if (elements.providerButtonsStep) elements.providerButtonsStep.style.display = 'none';
            hideLoginError();
            elements.workEmail?.focus();
        });

        // Back button to return to email entry
        elements.backToEmailBtn?.addEventListener('click', () => {
            elements.emailEntryStep.style.display = 'block';
            elements.providerButtonsStep.style.display = 'none';
            if (elements.passwordLoginOption) {
                elements.passwordLoginOption.style.display = 'none';
            }
            if (elements.providerPasswordInput) {
                elements.providerPasswordInput.value = '';
            }
            currentLoginEmail = null;
        });

        // SSO button for tenant-specific IdP
        elements.ssoSignInBtn?.addEventListener('click', async () => {
            try {
                const provider = elements.ssoSignInBtn.dataset.provider;
                await AuthManager.initiateOAuth({ email: currentLoginEmail, provider });
            } catch (error) {
                showLoginError('Failed to start sign-in. Please try again.');
            }
        });

        // Setup OAuth button listeners
        elements.googleSignInBtn?.addEventListener('click', async () => {
            try {
                await AuthManager.initiateOAuth({ email: currentLoginEmail, provider: 'Google' });
            } catch (error) {
                showLoginError('Failed to start sign-in. Please try again.');
            }
        });

        elements.microsoftSignInBtn?.addEventListener('click', async () => {
            try {
                await AuthManager.initiateOAuth({ email: currentLoginEmail, provider: 'Microsoft' });
            } catch (error) {
                showLoginError('Failed to start sign-in. Please try again.');
            }
        });

        // Password login from provider step (when tenant allows password login)
        elements.providerPasswordForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = currentLoginEmail;
            const password = elements.providerPasswordInput?.value;

            if (!email || !password) {
                showLoginError('Please enter your password');
                return;
            }

            elements.providerPasswordBtn.disabled = true;
            elements.providerPasswordBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Signing In...';
            hideLoginError();

            try {
                await AuthManager.login(email, password);
                showAppView();
                showToast('Success', 'Logged in successfully', 'success');
            } catch (error) {
                showLoginError(error.message || 'Invalid credentials');
            } finally {
                elements.providerPasswordBtn.disabled = false;
                elements.providerPasswordBtn.innerHTML = '<i class="fas fa-key"></i> Sign in with Password';
            }
        });

        // Setup fallback email/password authentication event listeners
        elements.loginForm?.addEventListener('submit', async (e) => {
            e.preventDefault();
            const email = elements.loginEmail.value.trim();
            const password = elements.loginPassword.value;

            if (!email || !password) {
                showLoginError('Please enter both email and password');
                return;
            }

            elements.loginBtn.disabled = true;
            elements.loginBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Signing In...';
            hideLoginError();

            try {
                await AuthManager.login(email, password);
                showAppView();
                showToast('Success', 'Logged in successfully', 'success');
            } catch (error) {
                showLoginError(error.message);
            } finally {
                elements.loginBtn.disabled = false;
                elements.loginBtn.innerHTML = '<i class="fas fa-sign-in-alt"></i> Sign In';
            }
        });

        elements.logoutBtn?.addEventListener('click', () => {
            AuthManager.logout();
            showToast('Info', 'Logged out successfully', 'info');
        });

        // Show/hide original clearance fields when Design Change or CAPA is selected
        elements.projectRegPathGrid?.addEventListener('change', (e) => {
            if (!e.target.matches('input[name="projectRegPath"]')) return;
            const needsClearance = e.target.value === 'design_change' || e.target.value === 'capa';
            elements.originalClearanceSection.style.display = needsClearance ? '' : 'none';
        });

        // Setup event listeners
        elements.createProjectBtn?.addEventListener('click', submitProjectForm);
        document.getElementById('cancelProjectEditBtn')?.addEventListener('click', exitProjectEditMode);
        document.getElementById('newProjectBtn')?.addEventListener('click', () => {
            const section = document.getElementById('projectFormSection');
            section.style.display = '';
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
            elements.projectName?.focus();
        });
        document.querySelectorAll('.project-doc-input').forEach(input => {
            input.addEventListener('change', handleProjectDocInputChange);
        });
        elements.refreshProjectsBtn?.addEventListener('click', loadProjects);
        elements.projectsSearch?.addEventListener('input', renderProjectsList);
        elements.backBtn?.addEventListener('click', goBackToMain);
        elements.backToReportBtn?.addEventListener('click', goBackToUsabilityReport);
        elements.generateExcelAnalysisBtn?.addEventListener('click', generateExcelAnalysis);
        elements.downloadExistingAnalysisBtn?.addEventListener('click', downloadExistingAnalysis);
        elements.generateUsabilityReportBtn?.addEventListener('click', generateUsabilityReport);
        elements.downloadExistingReportBtn?.addEventListener('click', downloadExistingReport);

        // Edit study inline form
        document.getElementById('editStudyBtn')?.addEventListener('click', openEditStudyForm);
        document.getElementById('cancelEditStudyBtn')?.addEventListener('click', closeEditStudyForm);
        document.getElementById('saveEditStudyBtn')?.addEventListener('click', saveStudyEdit);

        // Study management
        document.getElementById('addStudyBtn')?.addEventListener('click', () => {
            document.getElementById('addStudyForm').style.display = 'block';
            document.getElementById('studyNameInput')?.focus();
        });
        document.getElementById('cancelStudyBtn')?.addEventListener('click', () => {
            document.getElementById('addStudyForm').style.display = 'none';
            document.getElementById('addStudyError').style.display = 'none';
            document.getElementById('studyNameInput').value = '';
        });
        document.getElementById('saveStudyBtn')?.addEventListener('click', createStudy);
        document.getElementById('studyNameInput')?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') createStudy();
        });

        // Study detail back button
        document.getElementById('backToProjectBtn2')?.addEventListener('click', () => {
            currentStudy = null;
            if (currentProject) {
                openProject(currentProject.id);
            } else {
                goBackToMain();
            }
        });


        // Usability Report invocation back button
        document.getElementById('backToStudyBtn')?.addEventListener('click', () => {
            currentInvocation = null;
            if (currentStudy) {
                openStudy(currentStudy.id);
            } else if (currentProject) {
                openProject(currentProject.id);
            } else {
                goBackToMain();
            }
        });

        // Invocation view tab switching
        document.querySelectorAll('.invocation-tab-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const tabId = btn.dataset.tab;
                document.querySelectorAll('.invocation-tab-btn').forEach(b => b.classList.toggle('active', b === btn));
                document.querySelectorAll('.invocation-tab-content').forEach(c => c.classList.toggle('active', c.id === tabId));
            });
        });

        // Category management event listeners
        elements.addCategoryBtn?.addEventListener('click', showAddCategoryModal);
        elements.categoryForm?.addEventListener('submit', handleCategoryFormSubmit);
        elements.cancelCategoryBtn?.addEventListener('click', closeCategoryModal);
        elements.closeCategoryModal?.addEventListener('click', closeCategoryModal);
        elements.categoryName?.addEventListener('input', updateCategoryNameCounter);

        // Question management event listeners
        elements.categoryQuestionForm?.addEventListener('submit', handleQuestionFormSubmit);
        elements.cancelQuestionFormBtn?.addEventListener('click', closeCategoryQuestionModal);
        elements.closeCategoryQuestionModal?.addEventListener('click', closeCategoryQuestionModal);

        // File summary copy button
        elements.copySummaryTextBtn?.addEventListener('click', () => {
            if (window.currentFileSummary) {
                copyToClipboard(window.currentFileSummary);
            }
        });
        
        // Setup copy summary button
        document.getElementById('copySummaryBtn')?.addEventListener('click', () => {
            const summaryContent = elements.summaryResult.querySelector('.summary-content').textContent;
            copyToClipboard(summaryContent);
        });
        
        // Enter key on project name input
        elements.projectName?.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                submitProjectForm();
            }
        });
        
        setupFileUpload();
        setupModals();
        setupAdmin();
        setupTenantAdmin();
        
        // Setup hash-based routing
        window.addEventListener('hashchange', handleHashChange);

        // Handle initial hash only if authenticated
        if (AuthManager.isAuthenticated()) {
            handleHashChange();
        }
        
    } catch (error) {
        console.error('Error initializing app:', error);
        showToast('Error', 'Failed to initialize application', 'error');
    } finally {
        // All event listeners are attached (the OAuth config fetch above is
        // awaited first) — signals E2E tests the page is safe to interact with
        window.__appReady = true;
    }
});

// Summary polling functionality
let summaryPollingInterval = null;

function startSummaryPolling() {
    // Clear any existing polling
    if (summaryPollingInterval) {
        clearInterval(summaryPollingInterval);
    }

    // Poll every 3 seconds for summary updates
    summaryPollingInterval = setInterval(async () => {
        if (!currentProject) {
            clearInterval(summaryPollingInterval);
            return;
        }

        try {
            const response = await ApiClient.get(`/projects/${currentProject.id}`);
            const project = response.project || {};
            const files = project.files || [];

            // Check if any files have updated summaries or changed processing status
            const hasUpdatedSummaries = files.some(file => {
                const previousSummary = window.fileSummaries?.[file.id]?.summary;
                const currentSummary = file.ai_summary;

                // File has been processed and summary changed
                return (
                    file.processing_status === 'completed' &&
                    currentSummary &&
                    currentSummary !== 'Processing in background...' &&
                    currentSummary !== 'No content to analyze' &&
                    currentSummary !== previousSummary
                );
            });

            if (hasUpdatedSummaries) {
                console.log('[Polling] Detected updated summaries, refreshing file list');

                // Update the file list display with new summaries
                loadProjectFiles(files);

                // Check if all files are done processing
                const stillProcessing = files.some(file =>
                    file.processing_status === 'processing' ||
                    file.ai_summary === 'Processing in background...'
                );

                if (!stillProcessing) {
                    console.log('[Polling] All files processed, stopping polling');
                    // All summaries complete, stop polling
                    clearInterval(summaryPollingInterval);
                    summaryPollingInterval = null;

                    // Show success notification
                    showToast('Success', 'All files have been processed!', 'success');
                }
            }
        } catch (error) {
            console.error('Error polling for summary updates:', error);
            // Continue polling despite errors
        }
    }, 10000); // Poll every 10 seconds

    // Stop polling after 5 minutes to prevent infinite polling
    setTimeout(() => {
        if (summaryPollingInterval) {
            clearInterval(summaryPollingInterval);
            summaryPollingInterval = null;
        }
    }, 300000); // 5 minutes
}

function stopSummaryPolling() {
    if (summaryPollingInterval) {
        clearInterval(summaryPollingInterval);
        summaryPollingInterval = null;
    }
}

// Global functions for onclick handlers
window.openProject     = openProject;
window.editProject     = editProject;
window.deleteProject   = deleteProject;
window.toggleFavorite  = toggleFavorite;
window.openStudy           = openStudy;
window.deleteStudy         = deleteStudy;
window.deleteInvocation    = deleteInvocation;
window.openInvocation  = openInvocation;
window.launchTool      = launchTool;
window.deleteFile = async (fileId) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
        await ApiClient.delete(`/files/${fileId}`);
        showToast('Success', 'File deleted successfully', 'success');

        // Reload the correct context after deletion
        if (currentInvocation) {
            await loadInvocationFiles(currentInvocation.id);
        } else if (currentProject) {
            await openProject(currentProject.id);
        }

    } catch (error) {
        console.error('Error deleting file:', error);
        showToast('Error', 'Failed to delete file', 'error');
    }
};

/**
 * Edit participant label inline
 * @param {number} fileId - File ID
 * @param {string} currentLabel - Current label value
 */
window.editParticipantLabel = async (fileId, currentLabel) => {
    const labelElement = document.querySelector(
        `.participant-label[data-file-id="${fileId}"]`
    );

    if (!labelElement || labelElement.classList.contains('editing')) {
        return; // Already editing
    }

    // Create input field
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'participant-label-input';
    input.value = currentLabel || '';
    input.placeholder = 'P1, P2, etc.';
    input.maxLength = 10;

    // Save original content
    const originalContent = labelElement.innerHTML;

    // Replace content with input
    labelElement.innerHTML = '';
    labelElement.appendChild(input);
    labelElement.classList.add('editing');
    input.focus();
    input.select();

    // Handle save
    const saveLabel = async () => {
        const newLabel = input.value.trim();

        // Validate format
        if (newLabel && !/^P\d+$/.test(newLabel)) {
            showToast('Error', 'Invalid format. Use P1, P2, P3, etc.', 'error');
            input.focus();
            return;
        }

        // If unchanged, cancel
        if (newLabel === currentLabel) {
            cancelEdit();
            return;
        }

        try {
            // Show loading state
            input.disabled = true;
            labelElement.classList.add('saving');

            // Update via API
            const response = await ApiClient.patch(`/files/${fileId}/participant-label`, {
                participantLabel: newLabel
            });

            // Update UI
            labelElement.textContent = response.file.participant_label;
            labelElement.setAttribute('data-current-label', response.file.participant_label);
            labelElement.classList.remove('editing', 'saving');

            showToast('Success', 'Participant label updated successfully', 'success');

        } catch (error) {
            console.error('Error updating participant label:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response,
                data: error.response?.data,
                status: error.response?.status
            });

            let errorMessage = 'Failed to update label';
            if (error.response?.data?.code === 'DUPLICATE_LABEL') {
                errorMessage = `${newLabel} is already in use`;
            } else if (error.response?.data?.code === 'INVALID_FORMAT') {
                errorMessage = 'Invalid format. Use P1, P2, P3, etc.';
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                // Show the actual error message for debugging
                errorMessage = `Failed to update label: ${error.message}`;
            }

            showToast('Error', errorMessage, 'error');

            // Restore input for retry
            input.disabled = false;
            labelElement.classList.remove('saving');
            input.focus();
            input.select();
        }
    };

    // Handle cancel
    const cancelEdit = () => {
        labelElement.innerHTML = originalContent;
        labelElement.classList.remove('editing', 'saving');
    };

    // Event listeners
    input.addEventListener('blur', () => {
        // Delay to allow click on other elements
        setTimeout(saveLabel, 200);
    });

    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            saveLabel();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEdit();
        }
    });
};

window.viewFileSummary = (fileId) => {
    const fileData = window.fileSummaries?.[fileId];
    if (!fileData) {
        showToast('Error', 'File summary not found', 'error');
        return;
    }

    const { name: fileName, summary: displaySummary } = fileData;

    // Update file summary page content
    elements.summaryFileName.textContent = fileName;

    if (displaySummary === 'No summary available' || !displaySummary) {
        elements.summaryContentDisplay.innerHTML = '<div class="no-summary">No AI summary available for this file.</div>';
    } else {
        // Use the formatting function to display JSON nicely
        elements.summaryContentDisplay.innerHTML = formatSummaryContent(displaySummary);
    }

    // Store current summary for copy functionality
    window.currentFileSummary = displaySummary;

    // Switch to file summary view
    switchView('fileSummary');
    window.location.hash = `#project/${currentProject.id}/file/${fileId}`;
};
window.deleteQuestion = async (id) => {
    if (confirm('Are you sure you want to delete this question?')) {
        try {
            await ApiClient.delete(`/questions/${id}`);
            showToast('Success', 'Question deleted successfully', 'success');
            loadAdminQuestions();
        } catch (error) {
            showToast('Error', 'Failed to delete question', 'error');
        }
    }
};
window.deleteTemplate = async (id) => {
    if (confirm('Are you sure you want to delete this template?')) {
        try {
            await ApiClient.delete(`/templates/${id}`);
            showToast('Success', 'Template deleted successfully', 'success');
            loadAdminTemplates();
        } catch (error) {
            showToast('Error', 'Failed to delete template', 'error');
        }
    }
};
window.viewTemplate = (name, content) => {
    alert(`Template: ${name}\n\n${content}`);
};
window.downloadTemplate = downloadTemplate;
window.activateTemplate = activateTemplate;

// Expose user management functions globally
window.loadAdminUsers = loadAdminUsers;
window.editUser = editUser;
window.deleteUser = deleteUser;
window.dcExportReport    = dcExportReport;
window.hfToggleCitation  = hfToggleCitation;
window.dcToggleCard      = dcToggleCard;
window.dcLoadPastAnalysis = dcLoadPastAnalysis;

// ====================
// CSV Import Functionality
// ====================

let csvPreviewData = null;

/**
 * Initialize CSV import event listeners
 */
function initCsvImport() {
    const importCsvBtn = document.getElementById('importCsvBtn');
    const inlineCsvImport = document.getElementById('inlineCsvImport');
    const closeCsvImportModal = document.getElementById('closeCsvImportModal');
    const csvFileInput = document.getElementById('csvFileInput');
    const previewCsvBtn = document.getElementById('previewCsvBtn');
    const importDirectBtn = document.getElementById('importDirectBtn');
    const cancelCsvUploadBtn = document.getElementById('cancelCsvUploadBtn');
    const confirmCsvImportBtn = document.getElementById('confirmCsvImportBtn');
    const acceptAllAndImportBtn = document.getElementById('acceptAllAndImportBtn');
    const denyAllAndImportBtn = document.getElementById('denyAllAndImportBtn');
    const backToCsvUploadBtn = document.getElementById('backToCsvUploadBtn');

    // Open inline panel
    if (importCsvBtn) {
        importCsvBtn.addEventListener('click', () => {
            elements.inlineCategoryForm.style.display = 'none';
            if (elements.categoryQuestionModal) elements.categoryQuestionModal.style.display = 'none';
            inlineCsvImport.style.display = 'block';
            resetCsvImport();
        });
    }

    // Close inline panel
    if (closeCsvImportModal) {
        closeCsvImportModal.addEventListener('click', () => {
            inlineCsvImport.style.display = 'none';
        });
    }

    // File input change
    if (csvFileInput) {
        csvFileInput.addEventListener('change', (e) => {
            const file = e.target.files[0];
            previewCsvBtn.disabled = !file;
            if (importDirectBtn) importDirectBtn.disabled = !file;
        });
    }

    // Preview button
    if (previewCsvBtn) {
        previewCsvBtn.addEventListener('click', handleCsvPreview);
    }

    // Import without analyzing button
    if (importDirectBtn) {
        importDirectBtn.addEventListener('click', handleCsvPreviewDirect);
    }

    // Cancel upload
    if (cancelCsvUploadBtn) {
        cancelCsvUploadBtn.addEventListener('click', () => {
            inlineCsvImport.style.display = 'none';
        });
    }

    // Back to upload
    if (backToCsvUploadBtn) {
        backToCsvUploadBtn.addEventListener('click', () => {
            document.getElementById('csvUploadStep').style.display = 'block';
            document.getElementById('csvPreviewStep').style.display = 'none';
        });
    }

    // Confirm import
    if (confirmCsvImportBtn) {
        confirmCsvImportBtn.addEventListener('click', handleCsvImport);
    }

    // Accept all AI suggestions, then import
    if (acceptAllAndImportBtn) {
        acceptAllAndImportBtn.addEventListener('click', () => {
            applyBulkAiAction('accept');
            handleCsvImport();
        });
    }

    // Deny all AI suggestions, then import
    if (denyAllAndImportBtn) {
        denyAllAndImportBtn.addEventListener('click', () => {
            applyBulkAiAction('decline');
            handleCsvImport();
        });
    }
}

/**
 * Apply Accept or Decline to every still-pending AI suggestion card.
 */
function applyBulkAiAction(action) {
    const cards = document.querySelectorAll('.ai-question-card');
    cards.forEach(card => {
        const actions = card.querySelector('.ai-card-actions');
        if (!actions) return; // already accepted / edited / declined
        const selector = action === 'accept' ? '.btn-success' : '.btn-danger-outline';
        const btn = actions.querySelector(selector);
        if (btn) btn.click();
    });
}

/**
 * Reset CSV import modal to initial state
 */
function resetCsvImport() {
    document.getElementById('csvUploadStep').style.display = 'block';
    document.getElementById('csvPreviewStep').style.display = 'none';
    document.getElementById('csvFileInput').value = '';
    document.getElementById('previewCsvBtn').disabled = true;
    const importDirectBtn = document.getElementById('importDirectBtn');
    if (importDirectBtn) importDirectBtn.disabled = true;
    document.getElementById('csvUploadProgress').style.display = 'none';
    document.getElementById('csvImportProgress').style.display = 'none';
    setCsvPreviewMode(false);
    csvPreviewData = null;
}

/**
 * Handle CSV preview - parse and validate
 */
async function handleCsvPreview() {
    if (!currentProject) {
        showToast('Error', 'No project selected. Please navigate to a project first.', 'error');
        return;
    }

    const fileInput = document.getElementById('csvFileInput');
    const file = fileInput.files[0];

    if (!file) {
        showToast('Error', 'Please select a CSV file', 'error');
        return;
    }

    const progressContainer = document.getElementById('csvUploadProgress');
    progressContainer.style.display = 'block';

    const progressBar = new ProgressBar(progressContainer, {
        message: 'Parsing CSV file...',
        showPercentage: true,
        showSteps: true,
        steps: ['Reading File', 'Parsing Data', 'Validating', 'Generating Preview']
    });

    try {
        progressBar.nextStep('Reading file...');

        const formData = new FormData();
        formData.append('file', file);
        // Pass invocation ID when importing into a usability report invocation
        if (currentInvocation?.id) {
            formData.append('invocationId', currentInvocation.id);
        }

        progressBar.nextStep('Parsing and validating...');

        // Use direct fetch (not ApiClient) for file upload to avoid Content-Type issues
        const url = `${API_BASE_URL}/projects/${currentProject.id}/categories/import-csv-preview`;
        const token = AuthManager.getToken();

        const fetchResponse = await fetch(url, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: formData
        });

        if (!fetchResponse.ok) {
            const errorText = await fetchResponse.text();
            let errorMessage = `Upload failed: ${fetchResponse.status}`;
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorMessage;
            } catch (e) {
                // If not JSON, use the text
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const response = await fetchResponse.json();
        csvPreviewData = response;

        progressBar.nextStep('Generating preview...');

        // Show preview
        displayCsvPreview(response);

        // Trigger AI analysis (non-blocking)
        analyzeQuestionsWithAI(response);

        progressBar.complete('Preview ready!');

        // Switch to preview step
        setCsvPreviewMode(false);
        setTimeout(() => {
            document.getElementById('csvUploadStep').style.display = 'none';
            document.getElementById('csvPreviewStep').style.display = 'block';
        }, 1000);

    } catch (error) {
        progressBar.reset();
        progressContainer.style.display = 'none';
        console.error('CSV preview error:', error);
        showToast('Error', error.message || 'Failed to parse CSV file', 'error');
    }
}

/**
 * Same as handleCsvPreview but skips AI analysis.
 */
async function handleCsvPreviewDirect() {
    if (!currentProject) {
        showToast('Error', 'No project selected. Please navigate to a project first.', 'error');
        return;
    }

    const fileInput = document.getElementById('csvFileInput');
    const file = fileInput.files[0];

    if (!file) {
        showToast('Error', 'Please select a CSV file', 'error');
        return;
    }

    const progressContainer = document.getElementById('csvUploadProgress');
    progressContainer.style.display = 'block';

    const progressBar = new ProgressBar(progressContainer, {
        message: 'Parsing CSV file...',
        showPercentage: true,
        showSteps: true,
        steps: ['Reading File', 'Parsing Data', 'Validating', 'Generating Preview']
    });

    try {
        progressBar.nextStep('Reading file...');

        const formData = new FormData();
        formData.append('file', file);
        if (currentInvocation?.id) {
            formData.append('invocationId', currentInvocation.id);
        }

        progressBar.nextStep('Parsing and validating...');

        const url = `${API_BASE_URL}/projects/${currentProject.id}/categories/import-csv-preview`;
        const token = AuthManager.getToken();

        const fetchResponse = await fetch(url, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
        });

        if (!fetchResponse.ok) {
            const errorText = await fetchResponse.text();
            let errorMessage = `Upload failed: ${fetchResponse.status}`;
            try {
                const errorJson = JSON.parse(errorText);
                errorMessage = errorJson.error || errorMessage;
            } catch (e) {
                errorMessage = errorText || errorMessage;
            }
            throw new Error(errorMessage);
        }

        const response = await fetchResponse.json();
        csvPreviewData = response;

        progressBar.nextStep('Generating preview...');

        displayCsvPreview(response);

        progressBar.complete('Preview ready!');

        setCsvPreviewMode(true);
        setTimeout(() => {
            document.getElementById('csvUploadStep').style.display = 'none';
            document.getElementById('csvPreviewStep').style.display = 'block';
        }, 1000);

    } catch (error) {
        progressBar.reset();
        progressContainer.style.display = 'none';
        console.error('CSV preview error:', error);
        showToast('Error', error.message || 'Failed to parse CSV file', 'error');
    }
}

/**
 * Toggle Step 2 button labels/visibility based on whether AI analysis was skipped.
 * direct=true  → "Import" label, hide deny/import-updated buttons
 * direct=false → restore analyze-mode labels and visibility
 */
function setCsvPreviewMode(direct) {
    const acceptBtn = document.getElementById('acceptAllAndImportBtn');
    const denyBtn = document.getElementById('denyAllAndImportBtn');
    const confirmBtn = document.getElementById('confirmCsvImportBtn');
    if (acceptBtn) {
        acceptBtn.innerHTML = direct
            ? '<i class="fas fa-file-import"></i> Import'
            : '<i class="fas fa-check-double"></i> Accept all and import';
    }
    if (denyBtn) denyBtn.style.display = direct ? 'none' : '';
    if (confirmBtn) confirmBtn.style.display = direct ? 'none' : '';
}

/**
 * Display CSV preview data
 */
function displayCsvPreview(data) {
    const { summary, categories, questions, errors, warnings } = data;

    // Update summary stats
    document.getElementById('newCategoriesCount').textContent = summary.newCategories || 0;
    document.getElementById('existingCategoriesCount').textContent = summary.existingCategories || 0;
    document.getElementById('newQuestionsCount').textContent = summary.newQuestions || 0;
    document.getElementById('duplicatesCount').textContent = summary.duplicates || 0;

    // Show errors if any
    const errorsContainer = document.getElementById('csvPreviewErrors');
    const errorsList = document.getElementById('csvErrorsList');
    if (errors && errors.length > 0) {
        errorsList.innerHTML = errors.map(err => `<li>${escapeHtml(err)}</li>`).join('');
        errorsContainer.style.display = 'block';
        setImportButtonsDisabled(true);
    } else {
        errorsContainer.style.display = 'none';
        // Check if there's anything to import
        const hasNewData = summary.newQuestions > 0;
        setImportButtonsDisabled(!hasNewData);
    }

    // Show warnings if any (including duplicate warning)
    const warningsContainer = document.getElementById('csvPreviewWarnings');
    const warningsList = document.getElementById('csvWarningsList');
    const allWarnings = [...(warnings || [])];

    // Add warning if all questions are duplicates
    if (summary.newQuestions === 0 && summary.duplicates > 0) {
        allWarnings.unshift('All questions already exist in the project. No new data to import.');
    }

    if (allWarnings.length > 0) {
        warningsList.innerHTML = allWarnings.map(warn => `<li>${escapeHtml(warn)}</li>`).join('');
        warningsContainer.style.display = 'block';
    } else {
        warningsContainer.style.display = 'none';
    }

    // Build preview table
    const previewTable = document.getElementById('csvPreviewTable');
    previewTable.innerHTML = `
        <table class="preview-table">
            <thead>
                <tr>
                    <th>Category</th>
                    <th class="q-num-col">#</th>
                    <th>Question</th>
                    <th>Status</th>
                </tr>
            </thead>
            <tbody>
                ${renderPreviewRows(categories, questions)}
            </tbody>
        </table>
    `;
}

/**
 * Render preview table rows
 */
function renderPreviewRows(categories, questions) {
    const rows = [];
    let qNum = 0;

    Object.keys(questions).forEach(categoryName => {
        const categoryQuestions = questions[categoryName];
        const isNewCategory = categories.new.includes(categoryName);

        categoryQuestions.forEach((q, index) => {
            const categoryCell = index === 0
                ? `<td rowspan="${categoryQuestions.length}">
                     <strong>${escapeHtml(categoryName)}</strong>
                     ${isNewCategory ? '<span class="badge badge-new">New</span>' : '<span class="badge badge-existing">Existing</span>'}
                   </td>`
                : '';

            const thisQNum = !q.isDuplicate ? ++qNum : null;
            const qNumCell = thisQNum !== null
                ? `<td class="q-num-cell" id="q-row-${thisQNum}"><span class="q-num-label">Q${thisQNum}</span></td>`
                : `<td class="q-num-cell"></td>`;
            const questionContent = thisQNum !== null
                ? `<span id="q-text-${thisQNum}">${escapeHtml(q.question)}</span><div class="q-inline-analysis" id="q-analysis-${thisQNum}"></div>`
                : escapeHtml(q.question);

            rows.push(`
                <tr>
                    ${categoryCell}
                    ${qNumCell}
                    <td>${questionContent}</td>
                    <td>
                        ${q.isDuplicate
                            ? '<span class="badge badge-duplicate">Duplicate (Skip)</span>'
                            : '<span class="badge badge-new">New</span>'}
                    </td>
                </tr>
            `);
        });
    });

    return rows.join('');
}

/**
 * Analyze study questions with AI after CSV preview loads.
 * Non-blocking — errors are shown as a soft message, not blocking the import flow.
 */
async function analyzeQuestionsWithAI(previewData) {
    if (!currentProject) return;

    // Collect non-duplicate questions from previewData.questions
    // (importData.questions is an array of plain strings; previewData.questions has { question, isDuplicate } objects)
    const questions = [];
    let num = 1;
    for (const [categoryName, qs] of Object.entries(previewData.questions || {})) {
        for (const q of qs) {
            if (!q.isDuplicate) {
                questions.push({ number: num++, text: q.question, category: categoryName });
            }
        }
    }
    if (questions.length === 0) return;

    const panel = document.getElementById('questionAnalysisPanel');
    if (!panel) return;
    panel.style.display = 'block';
    panel.innerHTML = `<div class="ai-analysis-loading">
        <i class="fas fa-spinner fa-spin"></i>&nbsp; Analyzing questions...
    </div>`;

    setImportButtonsDisabled(true);

    try {
        const { jobId } = await ApiClient.post(
            `/projects/${currentProject.id}/questions/analyze`,
            { questions }
        );

        // Poll until complete
        while (true) {
            await new Promise(r => setTimeout(r, 3000));
            const result = await ApiClient.get(
                `/projects/${currentProject.id}/questions/analyze/${jobId}`
            );
            if (result.status === 'complete') {
                panel.style.display = 'none';
                renderInlineQuestionAnalysis(result.analysis || []);
                break;
            }
            if (result.status === 'error') {
                throw new Error(result.error || 'Analysis failed');
            }
        }
    } catch (error) {
        console.error('Question analysis failed:', error);
        panel.innerHTML = `<div class="ai-analysis-error">
            <i class="fas fa-exclamation-circle"></i>
            AI analysis unavailable — you can still import your questions.
        </div>`;
    } finally {
        setImportButtonsDisabled(false);
    }
}

/**
 * Toggle disabled state on all CSV import action buttons together.
 */
function setImportButtonsDisabled(disabled) {
    ['confirmCsvImportBtn', 'acceptAllAndImportBtn', 'denyAllAndImportBtn'].forEach(id => {
        const btn = document.getElementById(id);
        if (btn) btn.disabled = disabled;
    });
}

/**
 * Inject per-question AI analysis inline below each question in the preview table.
 */
function renderInlineQuestionAnalysis(analysis) {
    for (const item of (analysis || [])) {
        const slot = document.getElementById(`q-analysis-${item.questionNumber}`);
        if (!slot) continue;

        const hasIssues = item.issuesFlagged && item.issuesFlagged.length > 0;
        const hasInterIssues = item.interQuestionFlags && item.interQuestionFlags.length > 0;
        if (!hasIssues && !hasInterIssues) continue;

        const hasSuggestion = item.suggestedReword && item.suggestedReword !== 'null';

        const issueBadges = hasIssues
            ? item.issuesFlagged.map(issue => `<span class="ai-issue-badge">${escapeHtml(issue)}</span>`).join('')
            : '';

        const interBadgesBlock = hasInterIssues
            ? `<div class="ai-inter-block">
                <span class="ai-label">Inter-question</span>
                ${item.interQuestionFlags.map(f => `
                <div class="ai-inter-flag">
                    <span class="ai-inter-badge">${escapeHtml(f.issueType)}</span>
                    <p class="ai-inter-description">${linkifyQuestionRefs(f.description)}</p>
                </div>`).join('')}
               </div>`
            : '';

        const suggestionBlock = hasSuggestion
            ? `<div class="ai-suggestion-block">
                <span class="ai-label">Suggested reword:</span>
                <p class="ai-suggested-text" data-suggested="${escapeHtml(item.suggestedReword)}">${escapeHtml(item.suggestedReword)}</p>
               </div>`
            : '';

        const actionButtons = hasSuggestion
            ? `<div class="ai-card-actions">
                <button class="btn btn-sm btn-success" onclick="acceptQuestionReword(this)">
                    <i class="fas fa-check"></i> Accept
                </button>
                <button class="btn btn-sm btn-outline" onclick="editQuestionReword(this)">
                    <i class="fas fa-edit"></i> Edit
                </button>
                <button class="btn btn-sm btn-danger-outline" onclick="declineQuestionReword(this)">
                    <i class="fas fa-times"></i> Decline
                </button>
               </div>`
            : '';

        const issuesBlock = hasIssues
            ? `<div class="ai-issues-block">${issueBadges}</div>`
            : '';

        const justificationBlock = item.justification
            ? `<div class="ai-justification-block">
                <span class="ai-label">Why:</span>
                <p class="ai-justification-text">${escapeHtml(item.justification || '')}</p>
               </div>`
            : '';

        slot.innerHTML = `
            <div class="ai-question-card" data-question-number="${item.questionNumber}">
                ${issuesBlock}
                ${justificationBlock}
                ${interBadgesBlock}
                ${suggestionBlock}
                ${actionButtons}
            </div>`;
    }
}


/**
 * Find and update the question text in csvPreviewData by question number (1-based).
 * Returns true if updated.
 */
function updateQuestionInPreviewData(questionNumber, newText) {
    if (!csvPreviewData) return false;
    let count = 0;
    for (const qs of Object.values(csvPreviewData.questions || {})) {
        for (const q of qs) {
            if (!q.isDuplicate) {
                count++;
                if (count === questionNumber) {
                    q.question = newText;
                    return true;
                }
            }
        }
    }
    return false;
}

function acceptQuestionReword(btn) {
    const card = btn.closest('.ai-question-card');
    const questionNumber = parseInt(card.dataset.questionNumber);
    const suggestedText = card.querySelector('.ai-suggested-text')?.dataset.suggested;
    if (!suggestedText) return;

    updateQuestionInPreviewData(questionNumber, suggestedText);

    const textSpan = document.getElementById(`q-text-${questionNumber}`);
    if (textSpan) textSpan.textContent = suggestedText;

    card.querySelector('.ai-card-actions').innerHTML =
        '<span class="ai-status-badge ai-status-accepted"><i class="fas fa-check"></i> Accepted</span>';
    card.querySelector('.ai-suggested-text').style.fontWeight = 'bold';
}

function editQuestionReword(btn) {
    const card = btn.closest('.ai-question-card');
    const questionNumber = parseInt(card.dataset.questionNumber);
    const suggestedText = card.querySelector('.ai-suggested-text')?.dataset.suggested || '';

    const suggestionBlock = card.querySelector('.ai-suggestion-block');
    const originalSuggestionHTML = suggestionBlock.innerHTML;
    const actionsEl = card.querySelector('.ai-card-actions');

    suggestionBlock.innerHTML = `
        <span class="ai-label">Edit suggestion:</span>
        <textarea class="ai-edit-textarea" style="width:100%;box-sizing:border-box;resize:vertical;overflow:hidden;">${escapeHtml(suggestedText)}</textarea>
        <div style="display:flex;gap:6px;margin-top:6px;">
            <button class="btn btn-sm btn-success" onclick="saveQuestionReword(this, ${questionNumber})">
                <i class="fas fa-save"></i> Save
            </button>
            <button class="btn btn-sm btn-outline" onclick="cancelQuestionReword(this)">
                <i class="fas fa-times"></i> Cancel
            </button>
        </div>
    `;
    suggestionBlock.dataset.originalHtml = originalSuggestionHTML;
    actionsEl.style.display = 'none';

    // Auto-size the textarea to fit its content
    const ta = suggestionBlock.querySelector('.ai-edit-textarea');
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
    ta.addEventListener('input', () => {
        ta.style.height = 'auto';
        ta.style.height = ta.scrollHeight + 'px';
    });
}

function saveQuestionReword(btn, questionNumber) {
    const card = btn.closest('.ai-question-card');
    const textarea = card.querySelector('.ai-edit-textarea');
    const newText = textarea.value.trim();
    if (!newText) return;

    updateQuestionInPreviewData(questionNumber, newText);

    const textSpan = document.getElementById(`q-text-${questionNumber}`);
    if (textSpan) textSpan.textContent = newText;

    const suggestionBlock = card.querySelector('.ai-suggestion-block');
    suggestionBlock.innerHTML = `
        <span class="ai-label">Suggested reword:</span>
        <p class="ai-suggested-text" style="font-weight: bold;">${escapeHtml(newText)}</p>
    `;
    card.querySelector('.ai-card-actions').outerHTML =
        '<span class="ai-status-badge ai-status-edited"><i class="fas fa-edit"></i> Edited</span>';
}

function cancelQuestionReword(btn) {
    const card = btn.closest('.ai-question-card');
    const suggestionBlock = card.querySelector('.ai-suggestion-block');
    suggestionBlock.innerHTML = suggestionBlock.dataset.originalHtml;
    delete suggestionBlock.dataset.originalHtml;
    card.querySelector('.ai-card-actions').style.display = '';
}

function declineQuestionReword(btn) {
    const card = btn.closest('.ai-question-card');
    card.querySelector('.ai-card-actions').innerHTML =
        '<span class="ai-status-badge ai-status-declined"><i class="fas fa-times"></i> Declined</span>';
    card.classList.add('ai-card-declined');
}

window.acceptQuestionReword = acceptQuestionReword;
window.editQuestionReword = editQuestionReword;
window.saveQuestionReword = saveQuestionReword;
window.cancelQuestionReword = cancelQuestionReword;
window.declineQuestionReword = declineQuestionReword;
window.scrollToQuestion = scrollToQuestion;

/**
 * Handle CSV import confirmation
 */
async function handleCsvImport() {
    if (!currentProject) {
        showToast('Error', 'No project selected. Please navigate to a project first.', 'error');
        return;
    }

    if (!csvPreviewData) {
        showToast('Error', 'No preview data available', 'error');
        return;
    }

    setImportButtonsDisabled(true);

    const progressContainer = document.getElementById('csvImportProgress');
    progressContainer.style.display = 'block';

    const progressBar = new ProgressBar(progressContainer, {
        message: 'Importing categories and questions...',
        showPercentage: true,
        showSteps: true,
        steps: ['Creating Categories', 'Creating Questions', 'Finalizing Import']
    });

    try {
        progressBar.nextStep('Creating categories...');

        // Rebuild importData from csvPreviewData.questions so accepted rewrites are included
        const importData = Object.entries(csvPreviewData.questions || {})
            .map(([categoryName, qs]) => ({
                categoryName,
                questions: qs.filter(q => !q.isDuplicate).map(q => q.question)
            }))
            .filter(cat => cat.questions.length > 0);
        const importBody = { importData };
        if (currentInvocation?.id) importBody.invocationId = currentInvocation.id;

        const response = await ApiClient.post(
            `/projects/${currentProject.id}/categories/import-csv`,
            importBody
        );

        progressBar.nextStep('Creating questions...');

        await new Promise(resolve => setTimeout(resolve, 500)); // Simulate progress

        progressBar.complete('Import completed!');

        showToast(
            'Success',
            `Successfully imported ${response.summary.categoriesCreated} categories and ${response.summary.questionsCreated} questions`,
            'success'
        );

        // Close panel and refresh categories (invocation-scoped or project-scoped)
        setTimeout(() => {
            document.getElementById('inlineCsvImport').style.display = 'none';
            if (currentInvocation?.id) {
                loadInvocationCategories(currentInvocation.id);
            } else {
                loadCategories(currentProject.id);
            }
        }, 2000);

    } catch (error) {
        progressBar.reset();
        progressContainer.style.display = 'none';
        if (confirmBtn) confirmBtn.disabled = false;
        console.error('CSV import error:', error);
        showToast('Error', error.message || 'Failed to import CSV data', 'error');
    }
}

// Initialize CSV import when page loads
document.addEventListener('DOMContentLoaded', () => {
    initCsvImport();
});

// ==================== HF STUDY ADVISOR ====================

// HF Advisor state
const hfState = {
    step:                  1,
    predicateDevices:      [],
    researchSources:       { maudeDb: true, fda510k: true, pubmed: false, marketIntelligence: false },
    // Array of { name: string, active: boolean, source: 'ai' | 'manual' }
    deviceNames:           [],
    deviceNameInputMatch:  false,
    currentAnalysisId:     null,
    pollingInterval:       null,
    currentResult:         null,
    // Study context — set when launched from a study tool invocation
    studyId:               null,
    invocationId:          null,
};

const HF_LOADING_MESSAGES = [
    'Analyzing device information…',
    'Applying FDA guidance frameworks…',
    'Identifying user group factors…',
    'Generating screener criteria…',
    'Evaluating task scenarios…',
    'Compiling regulatory rationale…',
];

let hfMessageInterval = null;

function hfStartCyclingMessages() {
    const dotsEl = document.getElementById('hfRunningDots');
    if (dotsEl) {
        dotsEl.innerHTML = HF_LOADING_MESSAGES.map((_, i) =>
            `<div class="hf-running-dot${i === 0 ? ' active' : ''}"></div>`
        ).join('');
    }
    let idx = 0;
    hfMessageInterval = setInterval(() => {
        idx = (idx + 1) % HF_LOADING_MESSAGES.length;
        const statusEl = document.getElementById('hfRunningStatus');
        if (statusEl) statusEl.textContent = HF_LOADING_MESSAGES[idx];
        if (dotsEl) {
            dotsEl.querySelectorAll('.hf-running-dot').forEach((dot, i) => {
                dot.classList.toggle('active', i === idx);
            });
        }
    }, 2500);
}

function hfStopCyclingMessages() {
    if (hfMessageInterval) {
        clearInterval(hfMessageInterval);
        hfMessageInterval = null;
    }
}

let hfInitialized = false;

function hfAdvisorInit() {
    if (hfInitialized) {
        return;
    }
    hfInitialized = true;

    // Back button — returns to study if launched from one, otherwise main
    document.getElementById('hfAdvisorBackBtn')?.addEventListener('click', () => {
        hfSaveDraft();
        if (hfState.studyId) {
            openStudy(hfState.studyId);
        } else {
            goBackToMain();
        }
    });

    // Step navigation
    document.getElementById('hfStep1Next')?.addEventListener('click', hfStep1Next);
    document.getElementById('hfStep2Back')?.addEventListener('click', () => {
        hfGoToStep(1);
    });
    document.getElementById('hfStep2Next')?.addEventListener('click', hfStep2Next);
    document.getElementById('hfStep3Back')?.addEventListener('click', () => hfGoToStep(2));
    document.getElementById('hfRunAnalysisBtn')?.addEventListener('click', hfRunAnalysis);

    hfInitDeviceNameAutocomplete();

    // Output tabs
    document.querySelectorAll('.hf-tab-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const tab = btn.dataset.hftab;
            document.querySelectorAll('.hf-tab-btn').forEach((b) => b.classList.remove('active'));
            btn.classList.add('active');
            document.querySelectorAll('.hf-tab-content').forEach((c) => { c.style.display = 'none'; });
            const el = document.getElementById(`hfTab${tab.charAt(0).toUpperCase() + tab.slice(1)}`);
            if (el) el.style.display = 'block';
        });
    });

    // Export button
    document.getElementById('hfExportDocxBtn')?.addEventListener('click', hfExportDocx);

    hfGoToStep(1);
}

// ── Draft persistence ───────────────────────────────────────────────────────

function hfSaveDraft() {
    if (!hfState.currentAnalysisId) return;
    const predicatesRaw = document.getElementById('hfPredicates')?.value.trim() || '';
    ApiClient.patch(`/hf-advisor/analyses/${hfState.currentAnalysisId}/draft`, {
        predicateDevices: predicatesRaw
            ? predicatesRaw.split('\n').map(s => s.trim()).filter(Boolean)
            : hfState.predicateDevices,
        researchSources: {
            maudeDb:            document.getElementById('hfSrcMaude')?.checked  ?? hfState.researchSources.maudeDb,
            fda510k:            document.getElementById('hfSrc510k')?.checked   ?? hfState.researchSources.fda510k,
            pubmed:             document.getElementById('hfSrcPubmed')?.checked ?? hfState.researchSources.pubmed,
            marketIntelligence: document.getElementById('hfSrcMarket')?.checked ?? hfState.researchSources.marketIntelligence,
        },
        genericDeviceNames: hfState.deviceNames.map(d => d.name),
    }).catch(err => console.warn('HF draft save failed:', err));
}

// ── FDA Classification autocomplete ────────────────────────────────────────

let _hfAcTimer = null;
let _hfAcActiveIdx = -1;

function hfSetFdaValidated(matched) {
    hfState.deviceNameInputMatch = matched;
    const badge = document.getElementById('hfFdaValidatedBadge');
    if (badge) badge.style.display = matched ? 'inline-flex' : 'none';
    const addBtn = document.getElementById('hfDeviceNameAddBtn');
    if (addBtn) addBtn.disabled = !matched;
}

function hfUpdateStep2NextBtn() {
    const nextBtn = document.getElementById('hfStep2Next');
    if (nextBtn) nextBtn.disabled = hfState.deviceNames.filter(d => d.active).length === 0;
}

function hfHideDeviceDropdown() {
    const dd = document.getElementById('hfDeviceNameDropdown');
    if (dd) dd.style.display = 'none';
    _hfAcActiveIdx = -1;
}

function hfShowDeviceDropdown(names) {
    const dd = document.getElementById('hfDeviceNameDropdown');
    if (!dd) return;
    if (!names.length) { hfHideDeviceDropdown(); return; }

    _hfAcActiveIdx = -1;
    dd.innerHTML = names.map((n) =>
        `<li class="hf-autocomplete-item" data-value="${escapeHtml(n)}">${escapeHtml(n)}</li>`
    ).join('');
    dd.style.display = 'block';

    dd.querySelectorAll('.hf-autocomplete-item').forEach((item) => {
        item.addEventListener('mousedown', (e) => {
            e.preventDefault();
            document.getElementById('hfDeviceNameInput').value = item.dataset.value;
            hfSetFdaValidated(true);
            hfHideDeviceDropdown();
        });
    });
}

async function hfFdaClassificationSearch(query) {
    if (!query || query.length < 3) { hfHideDeviceDropdown(); return; }
    try {
        const phrase = `"${query}"`;
        const url = `https://api.fda.gov/device/classification.json?search=device_name:${encodeURIComponent(phrase)}&limit=10`;
        const res  = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        const seen = new Set();
        const names = (data.results || [])
            .map((r) => r.device_name)
            .filter((n) => n && !seen.has(n.toLowerCase()) && seen.add(n.toLowerCase()))
            .sort((a, b) => a.localeCompare(b));
        hfShowDeviceDropdown(names);
    } catch (_) { /* network errors are silent */ }
}


function hfAddDeviceName(name, source) {
    const normalised = name.trim();
    if (!normalised) return;
    if (hfState.deviceNames.some(d => d.name.toLowerCase() === normalised.toLowerCase())) return;
    hfState.deviceNames.push({ name: normalised, active: true, source });
    hfRenderDeviceNameList();
    hfUpdateStep2NextBtn();
}

function hfRenderDeviceNameList() {
    const container = document.getElementById('hfDeviceNameList');
    if (!container) return;
    container.innerHTML = hfState.deviceNames.map((d, i) => {
        const pillClass = [
            'hf-device-pill',
            d.source === 'ai' ? 'hf-pill-ai' : '',
            d.active ? '' : 'hf-pill-inactive',
        ].filter(Boolean).join(' ');
        const icon = d.active ? '✕' : '↩';
        const title = d.active
            ? (d.source === 'ai' ? 'AI-suggested — click to exclude' : 'Click to exclude')
            : 'Click to re-include';
        return `<span class="${pillClass}" data-idx="${i}" title="${title}">
            ${escapeHtml(d.name)}
            <span class="hf-device-pill-toggle">${icon}</span>
        </span>`;
    }).join('');
    container.querySelectorAll('.hf-device-pill').forEach(pill => {
        pill.addEventListener('click', () => {
            const idx = parseInt(pill.dataset.idx, 10);
            hfState.deviceNames[idx].active = !hfState.deviceNames[idx].active;
            hfRenderDeviceNameList();
            hfUpdateStep2NextBtn();
        });
    });
}

function hfInitDeviceNameAutocomplete() {
    const input = document.getElementById('hfDeviceNameInput');
    if (!input) return;

    input.addEventListener('input', () => {
        hfSetFdaValidated(false);
        clearTimeout(_hfAcTimer);
        _hfAcTimer = setTimeout(() => hfFdaClassificationSearch(input.value.trim()), 300);
    });

    input.addEventListener('keydown', (e) => {
        const dd    = document.getElementById('hfDeviceNameDropdown');
        const items = dd ? [...dd.querySelectorAll('.hf-autocomplete-item')] : [];

        if (e.key === 'ArrowDown' && items.length) {
            e.preventDefault();
            items[_hfAcActiveIdx]?.classList.remove('hf-ac-active');
            _hfAcActiveIdx = Math.min(_hfAcActiveIdx + 1, items.length - 1);
            items[_hfAcActiveIdx]?.classList.add('hf-ac-active');
        } else if (e.key === 'ArrowUp' && items.length) {
            e.preventDefault();
            items[_hfAcActiveIdx]?.classList.remove('hf-ac-active');
            _hfAcActiveIdx = Math.max(_hfAcActiveIdx - 1, 0);
            items[_hfAcActiveIdx]?.classList.add('hf-ac-active');
        } else if (e.key === 'Enter') {
            if (_hfAcActiveIdx >= 0 && items.length) {
                e.preventDefault();
                input.value = items[_hfAcActiveIdx].dataset.value;
                hfSetFdaValidated(true);
                hfHideDeviceDropdown();
            } else if (hfState.deviceNameInputMatch) {
                e.preventDefault();
                hfAddDeviceName(input.value, 'manual');
                input.value = '';
                hfSetFdaValidated(false);
            }
        } else if (e.key === 'Escape') {
            hfHideDeviceDropdown();
        }
    });

    input.addEventListener('blur', () => setTimeout(hfHideDeviceDropdown, 150));

    document.getElementById('hfDeviceNameAddBtn')?.addEventListener('click', () => {
        const name = input.value.trim();
        if (!name || !hfState.deviceNameInputMatch) return;
        hfAddDeviceName(name, 'manual');
        input.value = '';
        hfSetFdaValidated(false);
    });
}

// ── Step validation & navigation ───────────────────────────────────────────

function hfGoToStep(step) {
    hfState.step = step;
    for (let i = 1; i <= 3; i++) {
        const panel = document.getElementById(`hfStep${i}`);
        if (panel) panel.style.display = i === step ? 'block' : 'none';
        const nav = document.getElementById(`hfNav${i}`);
        if (nav) nav.style.display = i === step ? 'flex' : 'none';
    }
    document.getElementById('hfAnalysisRunning').style.display = 'none';
    document.getElementById('hfResultsPanel').style.display    = 'none';

    // SVG checkmark for completed steps
    const checkmarkSvg = `<svg style="width:14px;height:14px;fill:currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>`;

    // Update wizard step indicators
    document.querySelectorAll('#hfWizardSteps .wizard-step').forEach((el) => {
        const s = parseInt(el.dataset.step);
        const isActive    = s === step;
        const isCompleted = s < step;
        el.classList.toggle('active',    isActive);
        el.classList.toggle('completed', isCompleted);

        const numSpan = el.querySelector('.wizard-step-number');
        if (numSpan) {
            numSpan.innerHTML = isCompleted ? checkmarkSvg : String(s);
        }
    });

    // Update connecting line colors
    document.querySelectorAll('#hfWizardSteps .wizard-step-divider').forEach((el, i) => {
        el.classList.toggle('completed', i < step - 1);
    });

    // Sync step-2 state whenever that panel is shown
    if (step === 2) {
        hfSetFdaValidated(false);
        hfRenderDeviceNameList();
        hfUpdateStep2NextBtn();
    }
}

async function hfStep1Next() {
    const predicatesRaw = document.getElementById('hfPredicates').value.trim();
    hfState.predicateDevices = predicatesRaw
        ? predicatesRaw.split('\n').map((s) => s.trim()).filter(Boolean)
        : [];
    hfState.researchSources = {
        maudeDb:           document.getElementById('hfSrcMaude').checked,
        fda510k:           document.getElementById('hfSrc510k').checked,
        pubmed:            document.getElementById('hfSrcPubmed').checked,
        marketIntelligence: document.getElementById('hfSrcMarket').checked,
    };

    hfSaveDraft();
    document.getElementById('hfStep1Error').style.display = 'none';
    hfGoToStep(2);

    // If no names are loaded yet and we have a draft, ask the AI for suggestions.
    const hint = document.getElementById('hfDeviceNameHint');
    if (hfState.deviceNames.length === 0 && hfState.currentAnalysisId) {
        if (hint) hint.textContent = 'Loading AI suggestions…';
        try {
            const res = await ApiClient.get(`/hf-advisor/analyses/${hfState.currentAnalysisId}/suggest-device-name`);
            const suggestions = res.suggestions || (res.deviceName ? [res.deviceName] : []);
            suggestions.forEach(name => hfAddDeviceName(name, 'ai'));
            if (hint) hint.textContent = suggestions.length
                ? 'Suggested — toggle any off or add more from the FDA list.'
                : 'Could not auto-suggest — please add device types manually.';
        } catch (_) {
            if (hint) hint.textContent = 'Could not auto-suggest — please add device types manually.';
        }
    } else if (hint) {
        hint.textContent = '';
    }
}

function hfStep2Next() {
    const step2Err = document.getElementById('hfStep2Error');
    const activeNames = hfState.deviceNames.filter(d => d.active);
    if (!activeNames.length) {
        if (step2Err) {
            step2Err.textContent = 'At least one device type must be active.';
            step2Err.style.display = 'block';
        }
        return;
    }
    if (step2Err) step2Err.style.display = 'none';

    hfSaveDraft();

    const pathwayLabels = {
        new_predicate:    { label: 'New Product with Predicate', sub: '510(k) Submission' },
        new_no_predicate: { label: 'New Product — No Predicate', sub: 'De Novo or PMA Pathway' },
        design_change:    { label: 'Design Change',              sub: 'Current Released Product' },
        capa:             { label: 'CAPA Iteration',             sub: 'Released Product' },
    };
    const pathway = currentProject?.regulatory_pathway;
    const p = pathwayLabels[pathway] || { label: pathway || 'Not set', sub: '' };
    const envs = (currentProject?.use_environments || []);
    const envTags = envs.map(e => `<span class="hf-review-tag">${escapeHtml(e)}</span>`).join('') || '<span class="hf-review-tag" style="opacity:.5">None specified</span>';

    const sourceLabels = { maudeDb: 'MAUDE Database', fda510k: '510(k) Letters', pubmed: 'PubMed Literature', marketIntelligence: 'Market Intelligence' };
    const enabledSources = Object.entries(hfState.researchSources).filter(([, v]) => v);
    const sourceTags = enabledSources.length
        ? enabledSources.map(([k]) => `<span class="hf-review-tag hf-review-tag-gray">✓ ${sourceLabels[k]}</span>`).join('')
        : '<span class="hf-review-tag hf-review-tag-gray">None</span>';
    const predicateTags = hfState.predicateDevices.length
        ? hfState.predicateDevices.map(d => `<span class="hf-review-tag hf-review-tag-gray">${escapeHtml(d)}</span>`).join('')
        : '';

    document.getElementById('hfReviewSummary').innerHTML = `
        <div class="hf-review-grid">
            <div class="hf-review-tile">
                <div class="hf-review-tile-label">Regulatory Pathway</div>
                <div class="hf-review-tile-value">${escapeHtml(p.label)}</div>
                <div class="hf-review-tile-sub">${escapeHtml(p.sub)}</div>
            </div>
            <div class="hf-review-tile">
                <div class="hf-review-tile-label">Device</div>
                <div class="hf-review-tile-value">${escapeHtml(currentProject?.name || '—')}</div>
                <div class="hf-review-tile-sub" style="font-size:0.85rem;color:#6b7280;">${escapeHtml(currentProject?.description || '')}</div>
            </div>
            <div class="hf-review-tile">
                <div class="hf-review-tile-label">FDA Device Types (for research)</div>
                <div class="hf-review-source-list">${activeNames.map(d => `<span class="hf-review-tag">${escapeHtml(d.name)}</span>`).join('') || '<span class="hf-review-tag" style="opacity:.5">None</span>'}</div>
            </div>
            <div class="hf-review-tile">
                <div class="hf-review-tile-label">Use Environments</div>
                <div class="hf-review-env-tags">${envTags}</div>
            </div>
            <div class="hf-review-tile">
                <div class="hf-review-tile-label">Research Sources</div>
                <div class="hf-review-tile-value">${enabledSources.length} source${enabledSources.length !== 1 ? 's' : ''} enabled</div>
                <div class="hf-review-source-list">${sourceTags}</div>
            </div>
            ${hfState.predicateDevices.length ? `
            <div class="hf-review-tile hf-review-tile-full">
                <div class="hf-review-tile-label">Predicate Devices</div>
                <div class="hf-review-source-list">${predicateTags}</div>
            </div>` : ''}
        </div>`;

    // Update run hint with estimated time
    const hintEl = document.getElementById('hfRunHint');
    if (hintEl) {
        const estSeconds = 30 + enabledSources.length * 12;
        hintEl.textContent = enabledSources.length > 0
            ? `Estimated time: ~${estSeconds}s (including ${enabledSources.length} research source${enabledSources.length !== 1 ? 's' : ''})`
            : '';
    }

    hfGoToStep(3);
}

// ── Run analysis ───────────────────────────────────────────────────────────

async function hfRunAnalysis() {
    // Reset any previous error before starting
    const step3Err = document.getElementById('hfStep3Error');
    if (step3Err) { step3Err.textContent = ''; step3Err.style.display = 'none'; }

    // Hide wizard panels + nav bars, show running panel
    for (let i = 1; i <= 3; i++) {
        const p = document.getElementById(`hfStep${i}`);
        if (p) p.style.display = 'none';
        const n = document.getElementById(`hfNav${i}`);
        if (n) n.style.display = 'none';
    }
    document.getElementById('hfResultsPanel').style.display    = 'none';
    document.getElementById('hfAnalysisRunning').style.display = 'block';
    document.getElementById('hfRunningStatus').textContent     = 'Starting analysis…';
    hfStartCyclingMessages();

    try {
        const payload = {
            projectId:          currentProject?.id,
            predicateDevices:   hfState.predicateDevices,
            researchSources:    hfState.researchSources,
            genericDeviceNames: hfState.deviceNames.filter(d => d.active).map(d => d.name),
        };
        // Pass existing draft ID so the backend updates it in-place rather than creating a new record
        if (hfState.currentAnalysisId) payload.analysisId = hfState.currentAnalysisId;
        // Always pass invocationId so a new analysis gets linked to the tool invocation
        if (hfState.invocationId) payload.invocationId = hfState.invocationId;

        const res = await ApiClient.post('/hf-advisor/analyze', payload);

        hfState.currentAnalysisId = res.jobId;
        document.getElementById('hfRunningStatus').textContent = 'Scraping FDA databases and running analysis…';

        // Start polling
        hfState.pollingInterval = setInterval(hfPollStatus, 5000);
    } catch (err) {
        hfStopCyclingMessages();
        document.getElementById('hfAnalysisRunning').style.display = 'none';
        hfGoToStep(3);
        document.getElementById('hfStep3Error').textContent = err.message || 'Failed to start analysis.';
        document.getElementById('hfStep3Error').style.display = 'block';
    }
}

async function hfPollStatus() {
    if (!hfState.currentAnalysisId) return;
    // Capture the interval id we're polling under. setInterval fires on a
    // fixed cadence regardless of whether the previous callback's awaited
    // HTTP call has resolved — if two polls overlap and both return
    // 'complete', the second one re-renders results and snaps the user's
    // selected tab back to Overview. Bail out if our interval has been
    // cleared (or replaced) while our request was in flight.
    const startedUnderInterval = hfState.pollingInterval;

    try {
        const res = await ApiClient.get(`/hf-advisor/analyze/${hfState.currentAnalysisId}/status`);
        if (hfState.pollingInterval !== startedUnderInterval) return;

        if (res.status === 'complete') {
            clearInterval(hfState.pollingInterval);
            hfState.pollingInterval = null;
            hfStopCyclingMessages();
            hfState.currentResult   = res.result;
            document.getElementById('hfAnalysisRunning').style.display = 'none';
            hfRenderResults(res.result);
        } else if (res.status === 'error') {
            clearInterval(hfState.pollingInterval);
            hfState.pollingInterval = null;
            hfStopCyclingMessages();
            document.getElementById('hfAnalysisRunning').style.display = 'none';
            hfGoToStep(3);
            document.getElementById('hfStep3Error').textContent = `Analysis failed: ${res.errorMessage || 'Unknown error'}`;
            document.getElementById('hfStep3Error').style.display = 'block';
        } else {
            // still running
            const statusMessages = {
                pending: 'Queued…',
                running: 'Scraping FDA databases and running analysis…',
            };
            document.getElementById('hfRunningStatus').textContent = statusMessages[res.status] || 'Processing…';
        }
    } catch (err) {
        console.error('Polling error:', err);
    }
}

// ── Results rendering ──────────────────────────────────────────────────────

async function hfRenderResults(result) {
    // Clause index for citation chip labels/tooltips — loaded once, and the
    // renderers degrade to raw clause ids if the fetch failed.
    await hfLoadRegulatoryRefs();

    const groups     = result.userGroups  || [];
    const scenarios  = result.useScenarios || [];
    const totalParts = groups.reduce((sum, g) => sum + (g.n || 0), 0);

    document.getElementById('hfResultsTitle').textContent = 'Analysis Results';
    const subtitleEl = document.getElementById('hfResultsSubtitle');
    if (subtitleEl) {
        subtitleEl.textContent =
            `${groups.length} user group${groups.length !== 1 ? 's' : ''} · ${totalParts} total participants · ${scenarios.length} use scenario${scenarios.length !== 1 ? 's' : ''}`;
    }
    document.getElementById('hfResultsPanel').style.display = 'block';

    // Export only — v2 results are regenerated by re-running the analysis, not
    // edited in place, so there are no Edit/Save/Cancel controls.
    document.getElementById('hfExportDocxBtn').style.display = 'inline-flex';

    // Ensure first tab (overview) is active
    document.querySelectorAll('.hf-tab-btn').forEach((b, i) => b.classList.toggle('active', i === 0));
    document.querySelectorAll('.hf-tab-content').forEach((c, i) => { c.style.display = i === 0 ? 'block' : 'none'; });

    hfRenderV2Overview(result);
    hfRenderV2UserGroups(result.userGroups || []);
    hfRenderV2UseScenarios(result.useScenarios || [], result.userGroups || []);
    hfRenderFdaSignals(result.fdaSignals || [], result.maudeVerifiedMdrIds, result.maudeMdrMeta);
    hfRenderUfmeaRisks(currentProject?.id);
    hfRenderV2RegulatoryRationale(result.regulatoryRationale || {});
}

// Severity definitions shown as hover tooltips — keep in sync with the Tab 5
// prompt (migration 048), which instructs the LLM to classify with these rules.
const HF_SEVERITY_DEFS = {
    Critical: 'Adverse event — or potential adverse event — reported by a PubMed article or market intelligence that could lead to injury or death.',
    Warning:  'Adverse events that have led to, or could potentially lead to, damage to the device or delay/termination of the procedure.',
    Info:     'Any other results with important and useful information.',
};

function hfSeverityTooltipAttrs(severity) {
    const def = HF_SEVERITY_DEFS[severity];
    return def ? ` data-hf-tooltip="${escapeHtml(def)}" tabindex="0"` : '';
}

function hfRenderSignalsBanner(signals) {
    const critCount = signals.filter((s) => s.severity === 'Critical').length;
    const warnCount = signals.filter((s) => s.severity === 'Warning').length;
    const infoCount = signals.filter((s) => s.severity === 'Info').length;
    const hasCrit   = critCount > 0;

    const parts = [];
    if (critCount) parts.push(`<strong class="hf-tooltip-host"${hfSeverityTooltipAttrs('Critical')}>${critCount} Critical</strong>`);
    if (warnCount) parts.push(`<strong class="hf-tooltip-host"${hfSeverityTooltipAttrs('Warning')}>${warnCount} Warning</strong>`);
    if (infoCount) parts.push(`<strong class="hf-tooltip-host"${hfSeverityTooltipAttrs('Info')}>${infoCount} Info</strong>`);

    return `
    <div class="hf-signal-banner ${hasCrit ? 'critical' : 'info'}">
        <div class="hf-signal-banner-icon">${hasCrit ? '🚨' : 'ℹ️'}</div>
        <div>
            <h3>${hasCrit ? 'Critical FDA Signals Detected' : 'FDA Research Signals'}</h3>
            <p>${signals.length} signal${signals.length !== 1 ? 's' : ''} identified from research sources: ${parts.join(', ')}</p>
        </div>
    </div>`;
}

function hfFormatFdaDate(yyyymmdd) {
    // openFDA dates are "YYYYMMDD" strings
    const m = /^(\d{4})(\d{2})(\d{2})$/.exec(yyyymmdd || '');
    return m ? `${m[1]}-${m[2]}-${m[3]}` : yyyymmdd;
}

function hfLinkMdrRefs(escapedText, verifiedMdrIds, mdrMeta) {
    // Some accessdata detail pages are blank (HTTP 200, empty body), so the
    // scrape Lambda verifies each mdr_id and stores the live ones on the
    // result. A missing list means the analysis predates the check — keep
    // linking everything rather than degrade older results. For dead links,
    // mdrMeta supplies event date + manufacturer so the user can locate the
    // report via MAUDE search.
    const verified = Array.isArray(verifiedMdrIds) ? new Set(verifiedMdrIds.map(String)) : null;

    function linkOne(id) {
        if (verified && !verified.has(id)) {
            const meta  = mdrMeta?.[id] || {};
            const parts = [];
            if (meta.eventDate)    parts.push(`event date ${hfFormatFdaDate(meta.eventDate)}`);
            if (meta.manufacturer) parts.push(escapeHtml(meta.manufacturer));
            const context = parts.length ? `${parts.join(', ')} — ` : '';
            return `MDR ${id} <span style="color:#6b7280">(${context}detail page not available — <a href="https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfmaude/search.cfm" target="_blank" rel="noopener noreferrer">search MAUDE</a>)</span>`;
        }
        return `<a href="https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfmaude/detail.cfm?mdrfoi__id=${id}" target="_blank" rel="noopener noreferrer">MDR ${id}</a> <img src="/external-link.svg" width="11" height="11" style="vertical-align:middle" alt="">`;
    }

    // Match bracketed citation groups [MDR 12345, MDR 67890] or bare inline
    // references like "MDR 12345" in a single pass to avoid double-linking.
    return escapedText.replace(/\[MDR\s+\d+(?:,\s*MDR\s+\d+)*\]|\bMDR\s+(\d+)\b/g, (match, bareId) => {
        if (bareId !== undefined) {
            return linkOne(bareId);
        }
        return '[' + match.slice(1, -1).replace(/MDR\s+(\d+)/g, (_, id) => linkOne(id)) + ']';
    });
}

function hfLink510kRefs(escapedText) {
    return escapedText.replace(/\b(K\d{6})\b/gi, (_, id) =>
        `<a href="https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfPMN/pmn.cfm?id=${id.toUpperCase()}" target="_blank" rel="noopener noreferrer">${id}</a> <img src="/external-link.svg" width="11" height="11" style="vertical-align:middle" alt="">`
    );
}

function hfRenderFdaSignals(signals, verifiedMdrIds, mdrMeta) {
    const el = document.getElementById('hfTabFdaSignals');
    if (!signals.length) {
        el.innerHTML = '<p style="color:#6b7280;text-align:center;padding:2rem">No FDA signals identified for this device.</p>';
        return;
    }

    const itemClass = { Critical: 'hf-signal-item-critical', Warning: 'hf-signal-item-warning', Info: 'hf-signal-item-info' };
    const sevClass  = { Critical: 'hf-sev-critical',         Warning: 'hf-sev-warning',         Info: 'hf-sev-info' };
    const icon      = { Critical: '🚨',                      Warning: '⚠️',                     Info: 'ℹ️' };

    el.innerHTML = `
        ${hfRenderSignalsBanner(signals)}
        <div class="hf-signal-items">
            ${signals.map((s) => `
            <div class="hf-signal-item ${itemClass[s.severity] || 'hf-signal-item-info'}">
                <div class="hf-signal-item-header">
                    <span>${icon[s.severity] || 'ℹ️'}</span>
                    ${(s.source && s.source !== '[DOC]' && s.source !== '[EXTERNAL]') ? `<span class="hf-signal-source-badge">${escapeHtml(s.source)}</span>` : ''}
                    <span class="hf-signal-severity-badge hf-tooltip-host ${sevClass[s.severity] || 'hf-sev-info'}"${hfSeverityTooltipAttrs(s.severity)}>${escapeHtml(s.severity)}</span>
                </div>
                <p class="hf-signal-finding">${hfLink510kRefs(hfLinkMdrRefs(escapeHtml(hfStripInlineSourceTags(s.finding, ['EXTERNAL', 'DOC'])), verifiedMdrIds, mdrMeta))}</p>
                <div class="hf-signal-implication"><strong>Implication: </strong>${hfLink510kRefs(hfLinkMdrRefs(escapeHtml(hfStripInlineSourceTags(s.implication, ['EXTERNAL', 'DOC'])), verifiedMdrIds, mdrMeta))}</div>
            </div>`).join('')}
        </div>`;
}

// ── UFMEA Risks tab ────────────────────────────────────────────────────────
// Reads project_documents.extracted_data for the project's current UFMEA and
// renders ufmea_top_risks, use_scenarios_top_tasks, and use_scenarios using
// the display rules from the ufmea_device_extractor prompt.

async function hfRenderUfmeaRisks(projectId) {
    const el = document.getElementById('hfTabUfmeaRisks');
    if (!el) return;

    if (!projectId) {
        el.innerHTML = hfUfmeaEmptyState('No project selected.');
        return;
    }

    el.innerHTML = '<p style="color:#6b7280;text-align:center;padding:2rem">Loading UFMEA risks…</p>';

    let docs;
    try {
        docs = await ApiClient.get(`/projects/${projectId}/documents`);
    } catch (err) {
        el.innerHTML = hfUfmeaEmptyState(`Failed to load project documents: ${escapeHtml(err.message || 'unknown error')}`);
        return;
    }

    const ufmea = (docs || []).find((d) => d.doc_type === 'ufmea');
    if (!ufmea) {
        el.innerHTML = hfRenderUfmeaRisksFromUrs(docs);
        return;
    }
    if (ufmea.extraction_status === 'pending') {
        el.innerHTML = hfUfmeaEmptyState('UFMEA extraction is still running. Refresh in a moment to see the results.');
        return;
    }
    if (ufmea.extraction_status === 'failed') {
        el.innerHTML = hfUfmeaEmptyState(`UFMEA extraction failed: ${escapeHtml(ufmea.extraction_error || 'unknown error')}`);
        return;
    }

    const data = ufmea.extracted_data || {};
    const risks     = Array.isArray(data.ufmea_top_risks)         ? data.ufmea_top_risks         : [];
    const topTasks  = Array.isArray(data.use_scenarios_top_tasks) ? data.use_scenarios_top_tasks : [];
    const scenarios = Array.isArray(data.use_scenarios)          ? data.use_scenarios           : [];
    const notes     = data.notes || {};

    el.innerHTML = `
        ${data.device_description ? `
            <div class="hf-ufmea-device-desc">
                <h3>Device under analysis</h3>
                <p>${escapeHtml(data.device_description)}</p>
            </div>` : ''}

        ${hfRenderUfmeaNotesBanner(notes)}

        <div class="hf-ufmea-section">
            <div class="hf-ufmea-section-header">
                <h3>Top Risks</h3>
                <span class="hf-ufmea-count">${risks.length} risk${risks.length !== 1 ? 's' : ''}</span>
            </div>
            ${risks.length
                ? `<div class="hf-ufmea-risks">${risks.map(hfRenderUfmeaRiskCard).join('')}</div>`
                : '<p style="color:#6b7280;padding:1rem 0">No qualifying risks identified in the UFMEA.</p>'}
        </div>

        <div class="hf-ufmea-section">
            <div class="hf-ufmea-section-header">
                <h3>Use Scenarios — Top Critical Tasks</h3>
                <span class="hf-ufmea-count">${topTasks.length} task${topTasks.length !== 1 ? 's' : ''}</span>
            </div>
            ${topTasks.length
                ? `<div class="hf-ufmea-risks">${topTasks.map(hfRenderUfmeaTopTaskCard).join('')}</div>`
                : '<p style="color:#6b7280;padding:1rem 0">No critical tasks identified in the UFMEA.</p>'}
        </div>

        <div class="hf-ufmea-section">
            <div class="hf-ufmea-section-header">
                <h3>Use Scenarios</h3>
                <span class="hf-ufmea-count">${scenarios.length} scenario${scenarios.length !== 1 ? 's' : ''}</span>
            </div>
            ${scenarios.length
                ? `<div class="hf-ufmea-scenarios">${scenarios.map((s, i) => hfRenderUfmeaScenarioCard(s, i + 1)).join('')}</div>`
                : '<p style="color:#6b7280;padding:1rem 0">No use scenarios generated from this UFMEA.</p>'}
        </div>

        ${hfRenderKnownUseProblemsBlock(docs)}`;
}

// URS known_use_problems are additive context for the UFMEA Risks tab —
// surfaced regardless of whether a UFMEA is uploaded.
function hfRenderKnownUseProblemsBlock(docs) {
    const urs = (docs || []).find((d) => d.doc_type === 'user-requirements');
    if (!urs || urs.extraction_status !== 'success') return '';
    const problems = Array.isArray(urs.extracted_data?.known_use_problems)
        ? urs.extracted_data.known_use_problems
        : [];
    if (problems.length === 0) return '';
    return `
        <div class="hf-ufmea-section">
            <div class="hf-ufmea-section-header">
                <h3>Known Use Problems (from URS)</h3>
                <span class="hf-ufmea-count">${problems.length} item${problems.length !== 1 ? 's' : ''}</span>
            </div>
            <ul class="hf-urs-problems">
                ${problems.map((p) => `
                    <li>
                        <p>${escapeHtml(p.text || '')}</p>
                        ${p.source_quote ? `<blockquote class="hf-urs-source">${escapeHtml(p.source_quote)}</blockquote>` : ''}
                    </li>`).join('')}
            </ul>
        </div>`;
}

function hfUfmeaEmptyState(msg) {
    return `<p style="color:#6b7280;text-align:center;padding:2rem">${msg}</p>`;
}

// Fallback used when no UFMEA is uploaded: extract critical tasks from URS
// (explicit + inferred) per the HF advisor consumption rules. UFMEA-uploaded
// projects never reach this path, so there is no risk of mixing sources.
function hfRenderUfmeaRisksFromUrs(docs) {
    const urs = (docs || []).find((d) => d.doc_type === 'user-requirements');
    const noUfmeaNotice = `
        <div class="hf-ufmea-fallback-banner">
            <strong>No UFMEA uploaded.</strong>
            Edit current project and upload a UFMEA for the full risk analysis with severity ranking and re-run the HF Advisor tool.
        </div>`;

    if (!urs) {
        return `${noUfmeaNotice}${hfUfmeaEmptyState('No User Requirements document uploaded either — there is nothing to fall back to.')}`;
    }
    if (urs.extraction_status === 'pending') {
        return `${noUfmeaNotice}${hfUfmeaEmptyState('URS extraction is still running. Refresh in a moment.')}`;
    }
    if (urs.extraction_status === 'failed') {
        return `${noUfmeaNotice}${hfUfmeaEmptyState(`URS extraction failed: ${escapeHtml(urs.extraction_error || 'unknown error')}`)}`;
    }

    const data  = urs.extracted_data || {};
    const tasks = Array.isArray(data.critical_tasks) ? data.critical_tasks : [];

    return `
        ${noUfmeaNotice}
        <div class="hf-ufmea-section">
            <div class="hf-ufmea-section-header">
                <h3>Critical Tasks (from URS)</h3>
                <span class="hf-ufmea-count">${tasks.length} task${tasks.length !== 1 ? 's' : ''}</span>
            </div>
            ${tasks.length
                ? `<ul class="hf-urs-tasks">${tasks.map(hfRenderUrsCriticalTaskItem).join('')}</ul>`
                : '<p style="color:#6b7280;padding:1rem 0">No critical tasks were extracted or inferred from the URS.</p>'}
        </div>
        ${hfRenderKnownUseProblemsBlock(docs)}`;
}

function hfRenderUfmeaNotesBanner(notes) {
    const flags = [];
    if (notes.severity_missing)      flags.push('Severity column was not found — the LLM returned no risks.');
    if (notes.risk_level_missing)    flags.push('Risk Level column was not found — ranking used Severity only.');
    if (notes.critical_task_missing) flags.push('Critical Task column was not found — no critical-task tiebreaker applied.');
    if (!flags.length) return '';
    return `
        <div class="hf-ufmea-notes-banner">
            <strong>Extraction notes:</strong>
            <ul>${flags.map((f) => `<li>${escapeHtml(f)}</li>`).join('')}</ul>
        </div>`;
}

function hfRenderUfmeaRiskCard(r) {
    const severityLabel  = r.severity_score == null || r.severity_score === '' ? '—' : r.severity_score;
    const riskLevelLabel = r.risk_level == null || r.risk_level === '' ? '—' : r.risk_level;
    const riskLevelClass = hfUfmeaRiskLevelClass(r.risk_level);
    return `
        <div class="hf-ufmea-risk-card">
            <div class="hf-ufmea-risk-header">
                <div class="hf-ufmea-risk-title">
                    <span class="hf-ufmea-risk-line">Line ${escapeHtml(String(r.line ?? '—'))}</span>
                    <span class="hf-ufmea-risk-step">${escapeHtml(r.use_step || '')}</span>
                </div>
            </div>
            <div class="hf-ufmea-risk-field">
                <span class="hf-ufmea-risk-label">Use Error:</span>
                <span>${escapeHtml(r.use_error || '')}</span>
            </div>
            <div class="hf-ufmea-risk-meta">
                <span><strong>Severity:</strong> ${escapeHtml(String(severityLabel))}</span>
                <span><strong>Risk Level:</strong> <span class="hf-ufmea-risk-pill ${riskLevelClass}">${escapeHtml(String(riskLevelLabel))}</span></span>
            </div>
        </div>`;
}

function hfRenderUfmeaTopTaskCard(t) {
    const riskLevelLabel = t.risk_level == null || t.risk_level === '' ? '—' : t.risk_level;
    const riskLevelClass = hfUfmeaRiskLevelClass(t.risk_level);
    return `
        <div class="hf-ufmea-risk-card">
            <div class="hf-ufmea-risk-header">
                <div class="hf-ufmea-risk-title">
                    <span class="hf-ufmea-risk-line">Line ${escapeHtml(String(t.line ?? '—'))}</span>
                    <span class="hf-ufmea-risk-step">${escapeHtml(t.use_step || '')}</span>
                </div>
            </div>
            <div class="hf-ufmea-risk-meta">
                <span><strong>Risk Level:</strong> <span class="hf-ufmea-risk-pill ${riskLevelClass}">${escapeHtml(String(riskLevelLabel))}</span></span>
            </div>
        </div>`;
}

function hfRenderUfmeaScenarioCard(s, num) {
    const tasks = Array.isArray(s.critical_tasks) ? s.critical_tasks : [];
    return `
        <div class="hf-ufmea-scenario-card">
            <div class="hf-ufmea-scenario-header">
                <span class="hf-ufmea-scenario-num">${num}</span>
                <h4>${escapeHtml(s.title || `Scenario ${num}`)}</h4>
            </div>
            <div class="hf-ufmea-scenario-tags">
                ${s.setting      ? `<span class="hf-ufmea-tag"><strong>Setting:</strong> ${escapeHtml(s.setting)}</span>`           : ''}
                ${s.user_profile ? `<span class="hf-ufmea-tag"><strong>User profile:</strong> ${escapeHtml(s.user_profile)}</span>` : ''}
            </div>
            ${s.description ? `<p class="hf-ufmea-scenario-desc">${escapeHtml(s.description)}</p>` : ''}
            ${tasks.length ? `
                <div class="hf-ufmea-scenario-tasks">
                    <strong>Critical tasks in this scenario:</strong>
                    <ul>
                        ${tasks.map((t) => `<li>${escapeHtml(t.task || '')} <span class="hf-ufmea-line-ref">(Line ${escapeHtml(String(t.ufmea_line ?? '—'))})</span></li>`).join('')}
                    </ul>
                </div>` : ''}
        </div>`;
}

function hfUfmeaRiskLevelClass(level) {
    if (!level) return 'hf-ufmea-pill-none';
    const v = String(level).trim().toLowerCase();
    if (v === 'critical' || v === 'unacceptable' || v === 'red')         return 'hf-ufmea-pill-critical';
    if (v === 'high'                                                   ) return 'hf-ufmea-pill-high';
    if (v === 'medium' || v === 'moderate' || v === 'alarp' || v === 'yellow') return 'hf-ufmea-pill-medium';
    if (v === 'low' || v === 'acceptable' || v === 'green')              return 'hf-ufmea-pill-low';
    return 'hf-ufmea-pill-none';
}

// ── Shared URS helpers ──────────────────────────────────────────────────────
// The standalone "User Requirements" tab was removed — its extracted sections
// are already surfaced in the Overview, User Groups, and UFMEA Risks tabs.
// These two helpers remain because the UFMEA Risks fallback (when no UFMEA is
// uploaded) renders critical tasks pulled from the URS.

function hfUrsSourceQuote(text) {
    if (!text) return '';
    return `<blockquote class="hf-urs-source">${escapeHtml(text)}</blockquote>`;
}

function hfRenderUrsCriticalTaskItem(t) {
    const inferred = t.inferred === true;
    return `
        <li class="hf-urs-task ${inferred ? 'hf-urs-task-inferred' : ''}">
            <div class="hf-urs-task-head">
                <span class="hf-urs-task-text">${escapeHtml(t.task || '')}</span>
                ${inferred ? '<span class="hf-urs-badge hf-urs-badge-inferred">[INFERRED — not explicit in document]</span>' : ''}
            </div>
            ${inferred && t.inferred_reasoning ? `<p class="hf-urs-inferred-reason">${escapeHtml(t.inferred_reasoning)}</p>` : ''}
            ${hfUrsSourceQuote(t.source_quote)}
        </li>`;
}

// ── DOCX Export ────────────────────────────────────────────────────────────

async function hfExportDocx() {
    if (!hfState.currentAnalysisId) return;

    const btn = document.getElementById('hfExportDocxBtn');
    btn.disabled = true;
    btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Generating…';

    try {
        const response = await fetch(`${API_BASE_URL}/hf-advisor/export`, {
            method:  'POST',
            headers: {
                'Content-Type':  'application/json',
                Authorization:   `Bearer ${authToken}`,
            },
            body: JSON.stringify({ analysisId: hfState.currentAnalysisId }),
        });

        if (!response.ok) {
            const err = await response.json().catch(() => ({}));
            throw new Error(err.error || 'Export failed');
        }

        const disposition = response.headers.get('Content-Disposition') || '';
        const filenameMatch = disposition.match(/filename="([^"]+)"/);
        const filename = filenameMatch ? filenameMatch[1] : `HF_Study_Protocol_${currentProject?.name || 'Device'}.docx`;

        const blob = await response.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);

        showToast('Success', 'DOCX downloaded', 'success');
    } catch (err) {
        showToast('Error', err.message || 'Export failed', 'error');
    } finally {
        btn.disabled = false;
        btn.innerHTML = '<i class="fas fa-file-word"></i> Export DOCX';
    }
}

// ── Past analyses list ─────────────────────────────────────────────────────

async function hfLoadPastAnalyses() {
    const section = document.getElementById('hfPastAnalyses');
    const list    = document.getElementById('hfPastList');
    if (!section || !list) return;

    try {
        const res  = await ApiClient.get('/hf-advisor/analyses?limit=10');
        const rows = res.analyses || [];

        if (rows.length === 0) {
            section.style.display = 'none';
            return;
        }

        section.style.display = 'block';
        list.innerHTML = rows.map((a) => {
            const statusIcon = {
                complete: '<i class="fas fa-check-circle text-success"></i>',
                running:  '<i class="fas fa-spinner fa-spin text-warning"></i>',
                pending:  '<i class="fas fa-clock text-muted"></i>',
                error:    '<i class="fas fa-exclamation-circle text-danger"></i>',
            }[a.status] || '<i class="fas fa-circle"></i>';

            const date = a.created_at ? new Date(a.created_at).toLocaleDateString() : '';

            return `<div class="hf-past-row" data-id="${a.id}">
                ${statusIcon}
                <div class="hf-past-info">
                    <strong>${escapeHtml(a.device_name || '—')}</strong>
                    <span class="text-muted">${date}</span>
                </div>
                <div class="hf-past-actions">
                    ${a.status === 'complete' ? `<button class="btn btn-small btn-outline hf-reload-btn" data-id="${a.id}">View</button>
                    <button class="btn btn-small btn-outline hf-export-past-btn" data-id="${a.id}">DOCX</button>` : ''}
                    <button class="btn btn-small btn-outline btn-danger hf-delete-btn" data-id="${a.id}">Delete</button>
                </div>
            </div>`;
        }).join('');

        // Wire up past analysis actions
        list.querySelectorAll('.hf-reload-btn').forEach((btn) => {
            btn.addEventListener('click', async () => {
                try {
                    const detail = await ApiClient.get(`/hf-advisor/analyses/${btn.dataset.id}`);
                    const a = detail.analysis;
                    hfState.currentAnalysisId = a.id;
                    hfState.currentResult     = typeof a.result === 'string' ? JSON.parse(a.result) : a.result;

                    // Hide wizard steps, show results
                    for (let i = 1; i <= 3; i++) {
                        const p = document.getElementById(`hfStep${i}`);
                        if (p) p.style.display = 'none';
                    }
                    document.getElementById('hfAnalysisRunning').style.display = 'none';
                    hfRenderResults(hfState.currentResult);
                } catch (err) {
                    showToast('Error', 'Failed to load analysis', 'error');
                }
            });
        });

        list.querySelectorAll('.hf-export-past-btn').forEach((btn) => {
            btn.addEventListener('click', async () => {
                const prevId         = hfState.currentAnalysisId;
                hfState.currentAnalysisId = btn.dataset.id;
                await hfExportDocx();
                hfState.currentAnalysisId = prevId;
            });
        });

        list.querySelectorAll('.hf-delete-btn').forEach((btn) => {
            btn.addEventListener('click', async () => {
                if (!confirm('Delete this analysis?')) return;
                try {
                    await ApiClient.delete(`/hf-advisor/analyses/${btn.dataset.id}`);
                    showToast('Success', 'Analysis deleted', 'success');
                } catch (err) {
                    showToast('Error', 'Failed to delete', 'error');
                }
            });
        });
    } catch (err) {
        console.error('Failed to load past analyses:', err);
        section.style.display = 'none';
    }
}

// Load and display an existing analysis by ID (used when opening an invocation that already has a result)
async function hfLoadAnalysis(analysisId) {
    try {
        const res = await ApiClient.get(`/hf-advisor/analyses/${analysisId}`);
        const analysis = res.analysis;
        if (!analysis) { hfResetWizard(); return; }

        hfState.currentAnalysisId = analysisId;
        hfState.currentResult     = typeof analysis.result === 'string'
            ? JSON.parse(analysis.result) : analysis.result;

        if ((analysis.status === 'complete' || (analysis.status === 'error' && hfState.currentResult)) && hfState.currentResult) {
            // Hide wizard, show results (also recovers when status='error' but result was already committed)
            for (let i = 1; i <= 3; i++) {
                const p = document.getElementById(`hfStep${i}`);
                if (p) p.style.display = 'none';
                const n = document.getElementById(`hfNav${i}`);
                if (n) n.style.display = 'none';
            }
            document.getElementById('hfWizardSteps').style.display = 'none';
            document.getElementById('hfAnalysisRunning').style.display = 'none';
            hfRenderResults(hfState.currentResult);
        } else if (analysis.status === 'running' || analysis.status === 'pending') {
            // Re-attach polling
            for (let i = 1; i <= 3; i++) {
                const p = document.getElementById(`hfStep${i}`);
                if (p) p.style.display = 'none';
                const n = document.getElementById(`hfNav${i}`);
                if (n) n.style.display = 'none';
            }
            document.getElementById('hfResultsPanel').style.display = 'none';
            document.getElementById('hfAnalysisRunning').style.display = 'block';
            hfStartCyclingMessages();
            hfState.pollingInterval = setInterval(hfPollStatus, 5000);
        } else if (analysis.status === 'draft') {
            hfResetWizard();
            hfState.currentAnalysisId = analysisId;
            // Restore saved inputs from the DB record
            const predDevices = analysis.predicate_devices || [];
            const resSources  = analysis.research_sources  || {};
            hfState.predicateDevices = predDevices;
            hfState.researchSources  = {
                maudeDb:            !!resSources.maudeDb,
                fda510k:            !!resSources.fda510k,
                pubmed:             !!resSources.pubmed,
                marketIntelligence: !!resSources.marketIntelligence,
            };
            hfState.deviceNames = (analysis.generic_device_names || []).map(name => ({
                name, active: true, source: 'ai',
            }));
            const pr = document.getElementById('hfPredicates');
            if (pr) pr.value = predDevices.join('\n');
            const srcMaude  = document.getElementById('hfSrcMaude');
            const src510k   = document.getElementById('hfSrc510k');
            const srcPubmed = document.getElementById('hfSrcPubmed');
            const srcMarket = document.getElementById('hfSrcMarket');
            if (srcMaude)  srcMaude.checked  = hfState.researchSources.maudeDb;
            if (src510k)   src510k.checked   = hfState.researchSources.fda510k;
            if (srcPubmed) srcPubmed.checked = hfState.researchSources.pubmed;
            if (srcMarket) srcMarket.checked = hfState.researchSources.marketIntelligence;
        } else {
            hfResetWizard();
        }
    } catch (err) {
        console.error('Error loading HF analysis:', err);
        hfResetWizard();
    }
}

function hfResetWizard() {
    if (hfState.pollingInterval) {
        clearInterval(hfState.pollingInterval);
        hfState.pollingInterval = null;
    }
    hfStopCyclingMessages();
    hfState.step             = 1;
    hfState.predicateDevices = [];
    hfState.deviceNames      = [];
    hfState.deviceNameInputMatch = false;
    hfState.currentAnalysisId   = null;
    hfState.currentResult        = null;

    // Clear form fields
    const pr = document.getElementById('hfPredicates'); if (pr) pr.value = '';
    document.getElementById('hfSrcMaude').checked    = true;
    document.getElementById('hfSrc510k').checked     = true;
    document.getElementById('hfSrcPubmed').checked   = false;
    document.getElementById('hfSrcMarket').checked   = false;
    const deviceInput = document.getElementById('hfDeviceNameInput');
    if (deviceInput) deviceInput.value = '';
    const deviceHint = document.getElementById('hfDeviceNameHint');
    if (deviceHint) deviceHint.textContent = '';
    hfSetFdaValidated(false);
    hfRenderDeviceNameList();

    document.getElementById('hfResultsPanel').style.display    = 'none';
    document.getElementById('hfAnalysisRunning').style.display = 'none';
    const step3Err = document.getElementById('hfStep3Error');
    if (step3Err) { step3Err.textContent = ''; step3Err.style.display = 'none'; }
    hfGoToStep(1);
}

// Wire up Home button
document.addEventListener('DOMContentLoaded', () => {
    document.getElementById('homeBtn')?.addEventListener('click', () => goBackToMain());
});

// ═══════════════════════════════════════════════════════════════════════════
// DESIGN CHANGE ANALYZER
// ═══════════════════════════════════════════════════════════════════════════

const dcState = {
    initialized: false,
    pills: {},                 // group → selected value (single-select)
    tasks: [],                 // Q8 rows: { name, critical, effect }
    useErrors: [],             // Q11b rows: { taskIndex, type, participants, rootCause, rootCauseNotes, designTested, fixedByChange }
    currentStep: 1,
    totalSteps: 4,
    // Study context — set when launched from a study tool invocation
    studyId:           null,
    invocationId:      null,
    currentAnalysisId: null,   // UUID of the linked draft / analysis record
};

// User-facing verdict labels. Stored values (equivalency / gray-zone /
// resubmission) are unchanged; v2 results carry their own variant label.
const DC_VERDICT_LABELS = {
    equivalency:  'Short HF summary likely sufficient',
    'gray-zone':  'Written rationale likely needed. Review required',
    resubmission: 'New HF validation likely needed',
};
const DC_PATHWAY_LABELS = { '510k': '510(k)', pma: 'PMA', de_novo: 'De Novo', hde: 'HDE', dev: 'In development' };
const DC_SELECT = {
    critical:     [['', 'Critical?'], ['yes', 'Critical: Yes'], ['no', 'Critical: No'], ['unsure', 'Critical: Unsure']],
    severity:     [['', 'URRA severity'], ['high', 'Severity: High'], ['medium', 'Severity: Medium'], ['low', 'Severity: Low'], ['not_in_urra', 'Not in URRA']],
    effect:       [['', 'How does this change affect the task?'], ['new', 'New task'], ['changed', 'Task steps changed'],
                   ['adjacent', 'Steps unchanged, could still be affected'], ['removed', 'Task removed'], ['unsure', 'Unsure']],
    type:         [['', 'Type'], ['use_error', 'Use error'], ['close_call', 'Close call'], ['use_difficulty', 'Use difficulty']],
    rootCause:    [['', 'Root cause'], ['hardware', 'Hardware controls'], ['display', 'Display or software'], ['alarms', 'Alarms'],
                   ['labeling', 'Labeling or IFU'], ['packaging', 'Packaging'], ['training', 'Training'], ['use_environment', 'Use environment'],
                   ['test_artifact', 'Test artifact'], ['device_malfunction', 'Device malfunction'], ['undetermined', 'Undetermined']],
    designTested: [['', 'Design tested'], ['changed', 'Changed design'], ['previous', 'Previous design']],
    fixedByChange:[['', 'Fixed by this change?'], ['yes', 'Fixed: Yes'], ['no', 'Fixed: No']],
};

// Hover hint for the task-effect dropdown (native <option>s can't carry an info icon).
const DC_EFFECT_HINT = [
    "New task: Users didn't perform this task before the change.",
    'Task steps changed: Users will see, do or decide something differently when performing this task. Changes meant to make a task safer still count as changed.',
    'Steps unchanged, could still be affected: Users perform this task the same way, but it shares a screen, component or step sequence with what changed.',
    "Task removed: Users no longer perform this task. If removing it changes how another critical task is done, list that task as 'Task steps changed'.",
].join('\n\n');
const DC_SEVERITY_HINT = 'Optional. Take the severity from your URRA. High means the hazard could cause serious injury or worse. Most URRAs use a 5-point scale.';

// Mirrors needsTaskCheck in the API (src/utils/dcVerdict.js): rows whose effect
// on Q5 the user must confirm, with a model suggestion to help.
function dcNeedsTaskCheck(t) {
    return dcState.pills.taskStepsChanged === 'no' && t.critical === 'no'
        && ['new', 'changed', 'removed'].includes(t.effect) && t.severity !== 'high';
}
const dcNeedsJustification = (c) => c?.answer === 'no' && (c.suggestion === 'yes' || c.suggestion === 'unsure');

function dcTaskCheckHtml(t, i) {
    const c = t.q5Check;
    if (!c || !dcNeedsTaskCheck(t)) return '';
    const label = { yes: 'Yes', no: 'No', unsure: 'Unsure' };
    const cls = { yes: 'dc-pill-selected-yes', no: 'dc-pill-selected-no', unsure: 'dc-pill-selected-unknown' };
    const suggestion = c.suggestion
        ? `Suggested: <strong>${label[c.suggestion]}</strong>${c.reason ? ` — ${escapeHtml(c.reason)}` : ''}`
        : 'No suggestion available. Answer from the URRA.';
    return `
        <div class="dc-row-check">
            <div class="dc-row-check-text">Does this alter how the task is done? <span class="dc-muted">${suggestion}</span></div>
            <div class="dc-radio-row">${['yes', 'no', 'unsure'].map((v) =>
                `<div class="dc-pill${c.answer === v ? ` ${cls[v]}` : ''}" data-check-answer="${v}">${label[v]}</div>`).join('')}</div>
            ${dcNeedsJustification(c) ? `<input type="text" class="form-input" data-field="justification" maxlength="500" placeholder="Why No? (required)" value="${escapeHtml(c.justification || '')}" aria-label="Task ${i + 1} justification for No">` : ''}
        </div>`;
}

// Ask the model about rows that need a check and don't have one yet.
// Returns true when new suggestions were added (the user should review them).
async function dcFetchTaskChecks() {
    const pending = dcState.tasks.filter((t) => dcNeedsTaskCheck(t) && !t.q5Check);
    if (!pending.length) return false;
    let rows = [];
    try {
        ({ rows } = await ApiClient.post('/dc-analyzer/task-checks', {
            changeDescription: document.getElementById('dcChangeDesc').value.trim(),
            extraContext:      document.getElementById('dcExtraContext').value.trim() || undefined,
            tasks:             pending.map((t) => ({ name: t.name.trim(), effect: t.effect, severity: t.severity || undefined })),
        }));
    } catch (err) {
        console.warn('DC task-check suggestions failed:', err);
    }
    pending.forEach((t, n) => {
        const r = rows[n] || {};
        t.q5Check = { suggestion: r.suggestion || null, reason: r.reason || '', answer: null, justification: '' };
    });
    dcRenderTaskRows();
    return true;
}

function dcOptions(kind, selected) {
    return DC_SELECT[kind].map(([v, l]) => `<option value="${v}"${v === (selected ?? '') ? ' selected' : ''}>${escapeHtml(l)}</option>`).join('');
}

function dcAnalyzerInit() {
    if (dcState.initialized) {
        dcShowPage('wizard');
        return;
    }
    dcState.initialized = true;

    // Back button — returns to study if launched from one, otherwise main
    document.getElementById('dcBackBtn')?.addEventListener('click', () => {
        dcSaveDraft();
        if (dcState.studyId) {
            openStudy(dcState.studyId);
        } else {
            goBackToMain();
        }
    });

    // New Analysis from results
    document.getElementById('dcNewAnalysisBtn')?.addEventListener('click', () => {
        if (dcState.studyId) {
            openStudy(dcState.studyId);
        } else {
            goBackToMain();
        }
    });

    document.querySelectorAll('#designChangeView .dc-pill').forEach((pill) => {
        pill.addEventListener('click', () => { dcSelectPill(pill); dcSyncConditionals(); });
    });

    // "None" is exclusive in both multi-select checkbox groups
    ['dcUiParts', 'dcUsersEnv'].forEach((id) => {
        document.getElementById(id)?.addEventListener('change', (e) => {
            if (e.target.type !== 'checkbox' || !e.target.checked) return;
            document.querySelectorAll(`#${id} input[type="checkbox"]`).forEach((cb) => {
                if (cb !== e.target && (e.target.value === 'none' || cb.value === 'none')) cb.checked = false;
            });
        });
    });
    document.getElementById('dcUiParts')?.addEventListener('change', dcSyncConditionals);

    document.getElementById('dcSubmissionUndecided')?.addEventListener('change', dcSyncConditionals);
    document.getElementById('dcNoObservations')?.addEventListener('change', dcSyncConditionals);

    // Repeating rows (event delegation; rows are re-rendered from dcState)
    document.getElementById('dcAddTaskBtn')?.addEventListener('click', () => {
        dcState.tasks.push({ name: '', critical: '', severity: '', effect: '' });
        dcRenderTaskRows();
    });
    document.getElementById('dcAddUseErrorBtn')?.addEventListener('click', () => {
        dcState.useErrors.push({ taskIndex: null, type: '', participants: null, rootCause: '', rootCauseNotes: '', designTested: '', fixedByChange: '' });
        dcRenderUseErrorRows();
    });
    document.getElementById('dcTaskRows')?.addEventListener('input', dcOnRowEdit);
    document.getElementById('dcTaskRows')?.addEventListener('change', dcOnRowEdit);
    document.getElementById('dcTaskRows')?.addEventListener('click', dcOnRowRemove);
    document.getElementById('dcTaskRows')?.addEventListener('click', (e) => {
        const pill = e.target.closest('[data-check-answer]');
        if (!pill) return;
        dcState.tasks[Number(pill.closest('[data-row]').dataset.row)].q5Check.answer = pill.dataset.checkAnswer;
        dcRenderTaskRows();
    });
    document.getElementById('dcUseErrorRows')?.addEventListener('input', dcOnRowEdit);
    document.getElementById('dcUseErrorRows')?.addEventListener('change', dcOnRowEdit);
    document.getElementById('dcUseErrorRows')?.addEventListener('click', dcOnRowRemove);

    // Wizard navigation
    document.getElementById('dcWizardPrevBtn')?.addEventListener('click', dcWizardPrev);
    document.getElementById('dcWizardNextBtn')?.addEventListener('click', dcWizardNext);

    dcWizardGoTo(1);
}

function dcOnRowEdit(e) {
    const el = e.target.closest('[data-field]');
    const rowEl = e.target.closest('[data-row]');
    if (!el || !rowEl) return;
    const list = rowEl.dataset.list === 'tasks' ? dcState.tasks : dcState.useErrors;
    const row = list[Number(rowEl.dataset.row)];
    const f = el.dataset.field;
    if (f === 'taskIndex') row.taskIndex = el.value === '' ? null : Number(el.value);
    else if (f === 'participants') row.participants = el.value === '' ? null : parseInt(el.value, 10);
    else if (f === 'justification') { row.q5Check.justification = el.value; return; }
    else row[f] = el.value;

    // Editing what the task check was based on makes it stale; re-ask on Next.
    if (rowEl.dataset.list === 'tasks' && row.q5Check && ['name', 'critical', 'severity', 'effect'].includes(f)) {
        delete row.q5Check;
        if (e.type === 'change') { dcRenderTaskRows(); return; }
    }
    if (rowEl.dataset.list === 'tasks' && f === 'name' && e.type === 'change') dcRenderUseErrorRows();
    if (rowEl.dataset.list === 'useErrors' && (f === 'rootCause' || f === 'designTested') && e.type === 'change') dcRenderUseErrorRows();
}

function dcOnRowRemove(e) {
    const btn = e.target.closest('[data-remove]');
    if (!btn) return;
    const rowEl = btn.closest('[data-row]');
    const idx = Number(rowEl.dataset.row);
    if (rowEl.dataset.list === 'tasks') {
        dcState.tasks.splice(idx, 1);
        // Keep use-error rows pointing at the same task after the list shifts
        dcState.useErrors.forEach((r) => {
            if (r.taskIndex === idx) r.taskIndex = null;
            else if (r.taskIndex > idx) r.taskIndex -= 1;
        });
        dcRenderTaskRows();
    } else {
        dcState.useErrors.splice(idx, 1);
        dcRenderUseErrorRows();
    }
}

function dcRenderTaskRows() {
    const wrap = document.getElementById('dcTaskRows');
    if (!wrap) return;
    wrap.innerHTML = dcState.tasks.map((t, i) => `
        <div class="dc-row" data-list="tasks" data-row="${i}">
            <input type="text" class="form-input" data-field="name" placeholder="Task name" value="${escapeHtml(t.name || '')}" aria-label="Task ${i + 1} name">
            <select class="form-input" data-field="critical" aria-label="Task ${i + 1} critical">${dcOptions('critical', t.critical)}</select>
            <span class="dc-select-hint">
                <select class="form-input" data-field="severity" aria-label="Task ${i + 1} URRA severity">${dcOptions('severity', t.severity)}</select>
                <i class="fas fa-info-circle" tabindex="0" title="${escapeHtml(DC_SEVERITY_HINT)}" aria-label="${escapeHtml(DC_SEVERITY_HINT)}"></i>
            </span>
            <span class="dc-select-hint">
                <select class="form-input" data-field="effect" aria-label="Task ${i + 1} how the change affects it">${dcOptions('effect', t.effect)}</select>
                <i class="fas fa-info-circle" tabindex="0" title="${escapeHtml(DC_EFFECT_HINT)}" aria-label="${escapeHtml(DC_EFFECT_HINT)}"></i>
            </span>
            <button type="button" class="btn btn-outline btn-sm dc-row-remove" data-remove aria-label="Remove task ${i + 1}"><i class="fas fa-times"></i></button>
            ${dcTaskCheckHtml(t, i)}
        </div>`).join('');
    dcRenderUseErrorRows(); // task dropdowns depend on the task list
}

function dcRenderUseErrorRows() {
    const wrap = document.getElementById('dcUseErrorRows');
    if (!wrap) return;
    const taskOpts = (sel) => `<option value="">Task</option>` + dcState.tasks.map((t, i) =>
        `<option value="${i}"${sel === i ? ' selected' : ''}>${escapeHtml(t.name || `Task ${i + 1}`)}</option>`).join('');
    wrap.innerHTML = dcState.useErrors.map((r, i) => `
        <div class="dc-row dc-row-wide" data-list="useErrors" data-row="${i}">
            <select class="form-input" data-field="taskIndex" aria-label="Observation ${i + 1} task">${taskOpts(r.taskIndex)}</select>
            <select class="form-input" data-field="type" aria-label="Observation ${i + 1} type">${dcOptions('type', r.type)}</select>
            <input type="number" class="form-input dc-num-input" data-field="participants" min="1" max="500" placeholder="#" value="${Number.isInteger(r.participants) ? r.participants : ''}" aria-label="Observation ${i + 1} participants" title="Number of participants">
            <select class="form-input" data-field="rootCause" aria-label="Observation ${i + 1} root cause">${dcOptions('rootCause', r.rootCause)}</select>
            <select class="form-input" data-field="designTested" aria-label="Observation ${i + 1} design tested">${dcOptions('designTested', r.designTested)}</select>
            ${r.designTested === 'previous' ? `<select class="form-input" data-field="fixedByChange" aria-label="Observation ${i + 1} fixed by this change">${dcOptions('fixedByChange', r.fixedByChange)}</select>` : ''}
            <button type="button" class="btn btn-outline btn-sm dc-row-remove" data-remove aria-label="Remove observation ${i + 1}"><i class="fas fa-times"></i></button>
            <input type="text" class="form-input dc-row-notes" data-field="rootCauseNotes" placeholder="Root cause notes${r.rootCause === 'test_artifact' ? ' (required for a test artifact)' : ''}" value="${escapeHtml(r.rootCauseNotes || '')}" aria-label="Observation ${i + 1} root cause notes">
        </div>`).join('');
}

// Show / hide conditional questions from the current answers.
function dcSyncConditionals() {
    const show = (id, on) => { const el = document.getElementById(id); if (el) el.style.display = on ? '' : 'none'; };
    const p = dcState.pills;
    show('dcFieldDetails', p.fieldData === 'yes');
    show('dcDamageDelayField', p.fieldData === 'yes' && p.fieldOutcome === 'equipment_damage');
    const tested = p.testing === 'formative' || p.testing === 'hf_validation';
    show('dcTestingDetails', tested);
    const noObs = document.getElementById('dcNoObservations')?.checked;
    show('dcUseErrorRows', !noObs);
    show('dcAddUseErrorBtn', !noObs);
    show('dcPriorFdaText', p.priorFdaFeedback === 'yes');
    show('dcTasksReqDot', dcCheckedValues('dcUiParts').some((v) => v !== 'none'));
    const dateEl = document.getElementById('dcSubmissionDate');
    if (dateEl) dateEl.disabled = !!document.getElementById('dcSubmissionUndecided')?.checked;
}

function dcCheckedValues(containerId) {
    return Array.from(document.querySelectorAll(`#${containerId} input:checked`)).map((i) => i.value);
}

/** The intake object the API validates and the verdict rules read. */
function dcCollectIntake() {
    const p = dcState.pills;
    const undecided = !!document.getElementById('dcSubmissionUndecided')?.checked;
    const tested = p.testing === 'formative' || p.testing === 'hf_validation';
    const noObs = !!document.getElementById('dcNoObservations')?.checked;
    const intake = {
        plannedSubmissionDate:   undecided ? null : (document.getElementById('dcSubmissionDate')?.value || null),
        submissionDateUndecided: undecided,
        uiParts:          dcCheckedValues('dcUiParts'),
        taskStepsChanged: p.taskStepsChanged,
        usersEnv:         dcCheckedValues('dcUsersEnv'),
        urraUpdated:      p.urraUpdated,
        priorValidation:  p.priorValidation,
        tasks:            dcState.tasks.map((t) => ({
            name: (t.name || '').trim(), critical: t.critical, severity: t.severity || undefined, effect: t.effect,
            q5Check: t.q5Check ? { ...t.q5Check, justification: (t.q5Check.justification || '').trim() } : undefined,
        })),
        newUseError:      p.newUseError,
        fieldData:        p.fieldData,
        testing:          p.testing,
        priorFdaFeedback: p.priorFdaFeedback,
    };
    if (p.fieldData === 'yes') {
        intake.fieldOutcome = p.fieldOutcome;
        if (p.fieldOutcome === 'equipment_damage') intake.damageCouldDelay = p.damageCouldDelay;
        intake.useRelated = p.useRelated;
    }
    if (tested) {
        intake.testParticipants = parseInt(document.getElementById('dcTestParticipants')?.value, 10) || null;
        intake.noObservations = noObs;
        intake.useErrors = noObs ? [] : dcState.useErrors.map((r) => ({
            ...r,
            rootCauseNotes: (r.rootCauseNotes || '').trim(),
            fixedByChange:  r.designTested === 'previous' ? r.fixedByChange : undefined,
        }));
    }
    if (p.priorFdaFeedback === 'yes') intake.priorFdaFeedbackText = document.getElementById('dcPriorFdaText')?.value.trim() || '';
    return intake;
}

function dcResetWizard() {
    dcState.pills             = {};
    dcState.tasks             = [];
    dcState.useErrors         = [];
    dcState.currentStep       = 1;
    dcState.currentAnalysisId = null;

    ['dcChangeDesc', 'dcExtraContext', 'dcTestParticipants', 'dcSubmissionDate', 'dcPriorFdaText'].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.value = '';
    });
    document.querySelectorAll('#designChangeView input[type="checkbox"]').forEach((cb) => { cb.checked = false; });
    document.querySelectorAll('#designChangeView .dc-pill').forEach((pill) => { pill.className = 'dc-pill'; });

    const typeEl = document.getElementById('dcSubmissionType');
    if (typeEl) {
        const pw = currentProject?.original_regulatory_pathway;
        typeEl.textContent = DC_PATHWAY_LABELS[pw] || 'Not set on the project';
    }

    const errEl = document.getElementById('dcFormError');
    if (errEl) errEl.style.display = 'none';

    dcRenderTaskRows();
    dcSyncConditionals();
    dcWizardGoTo(1);
    dcShowPage('wizard');
}

function dcSaveDraft() {
    if (!dcState.currentAnalysisId) return;
    ApiClient.patch(`/dc-analyzer/analyses/${dcState.currentAnalysisId}/draft`, {
        changeDescription: document.getElementById('dcChangeDesc')?.value.trim()  || '',
        extraContext:      document.getElementById('dcExtraContext')?.value.trim() || undefined,
        intake:            dcCollectIntake(),
    }).catch(err => console.warn('DC draft save failed:', err));
}

function dcRestoreDraftFromRecord(rec) {
    const setVal = (id, v) => { const el = document.getElementById(id); if (el) el.value = v ?? ''; };
    setVal('dcChangeDesc', rec.change_description);
    setVal('dcExtraContext', rec.extra_context);

    // Drafts saved before the v2 form have no intake; only the text carries over.
    const i = (typeof rec.intake === 'string' ? JSON.parse(rec.intake) : rec.intake) || {};
    setVal('dcSubmissionDate', i.plannedSubmissionDate);
    setVal('dcTestParticipants', i.testParticipants);
    setVal('dcPriorFdaText', i.priorFdaFeedbackText);
    const setCheck = (id, on) => { const el = document.getElementById(id); if (el) el.checked = !!on; };
    setCheck('dcSubmissionUndecided', i.submissionDateUndecided);
    setCheck('dcNoObservations', i.noObservations);
    document.querySelectorAll('#dcUiParts input[type="checkbox"]').forEach((cb) => { cb.checked = (i.uiParts || []).includes(cb.value); });
    document.querySelectorAll('#dcUsersEnv input[type="checkbox"]').forEach((cb) => { cb.checked = (i.usersEnv || []).includes(cb.value); });

    dcState.pills = {};
    ['taskStepsChanged', 'urraUpdated', 'priorValidation', 'newUseError', 'fieldData', 'fieldOutcome',
     'damageCouldDelay', 'useRelated', 'testing', 'priorFdaFeedback'].forEach((group) => {
        if (!i[group]) return;
        const pill = document.querySelector(`#designChangeView [data-group="${group}"][data-val="${i[group]}"]`);
        if (pill) dcSelectPill(pill);
    });

    dcState.tasks = Array.isArray(i.tasks) ? i.tasks.map((t) => ({ ...t })) : [];
    dcState.useErrors = Array.isArray(i.useErrors) ? i.useErrors.map((r) => ({ ...r })) : [];
    dcRenderTaskRows();
    dcSyncConditionals();
}

function dcShowPage(page) {
    document.getElementById('dcWizardPage').style.display  = page === 'wizard'  ? '' : 'none';
    document.getElementById('dcResultsPage').style.display = page === 'results' ? '' : 'none';
    document.getElementById('dcHistoryPage').style.display = page === 'history' ? '' : 'none';
}

function dcWizardGoTo(step) {
    dcState.currentStep = step;
    if (step === 2) dcRenderTaskRows(); // task checks depend on the step-1 Q5 answer

    for (let i = 1; i <= dcState.totalSteps; i++) {
        const el = document.getElementById(`dcStep${i}`);
        if (el) el.style.display = i === step ? '' : 'none';
    }

    document.querySelectorAll('.dc-progress-track .wizard-step').forEach((dot) => {
        const s = parseInt(dot.dataset.step);
        dot.classList.toggle('active',    s === step);
        dot.classList.toggle('completed', s < step);
    });
    document.querySelectorAll('.dc-progress-track .wizard-step-divider').forEach((line, idx) => {
        line.classList.toggle('completed', idx < step - 1);
    });

    const prevBtn = document.getElementById('dcWizardPrevBtn');
    const nextBtn = document.getElementById('dcWizardNextBtn');
    if (prevBtn) prevBtn.style.visibility = step > 1 ? 'visible' : 'hidden';
    if (nextBtn) {
        nextBtn.innerHTML = step === dcState.totalSteps
            ? '<i class="fas fa-search" id="dcAnalyzeIcon"></i> <span id="dcAnalyzeBtnLabel">Run analysis</span>'
            : 'Next <i class="fas fa-arrow-right"></i>';
    }

    const errEl = document.getElementById('dcFormError');
    if (errEl) errEl.style.display = 'none';
}

// Per-step required-field check. The API re-validates the whole intake with
// the same rules that decide the verdict (src/utils/dcVerdict.js).
function dcStepErrors(step) {
    const i = dcCollectIntake();
    const p = dcState.pills;
    const e = [];
    if (step === 1) {
        if (!i.submissionDateUndecided && !i.plannedSubmissionDate) e.push('Enter the planned submission date, or tick "Not decided".');
        if (!document.getElementById('dcChangeDesc').value.trim()) e.push('Describe the change.');
        if (!i.uiParts.length) e.push('Select the parts of the user interface the change touches (or "None").');
        if (!p.taskStepsChanged) e.push('Answer whether the change alters how any task is done.');
        if (!i.usersEnv.length) e.push('Answer who uses it and where (or "None of these").');
    }
    if (step === 2) {
        if (!p.urraUpdated) e.push('Answer whether the URRA/UFMEA has been updated.');
        if (!p.priorValidation) e.push('Answer whether a prior HF validation study covers today\'s users, environments and critical tasks.');
        if (i.uiParts.some((v) => v !== 'none') && !i.tasks.length) e.push('Add at least one affected task.');
        i.tasks.forEach((t, n) => {
            if (!t.name || !t.critical || !t.effect) e.push(`Complete task ${n + 1} (name, critical, how the change affects it).`);
            if (dcNeedsTaskCheck(t) && dcNeedsJustification(t.q5Check) && !t.q5Check.justification) {
                e.push(`Task ${n + 1}: explain why you changed the suggested answer to No.`);
            }
        });
        if (!p.newUseError) e.push('Answer whether the change could cause a new use error.');
    }
    if (step === 3) {
        if (!p.fieldData) e.push('Answer whether the change was prompted by a complaint, MAUDE report or adverse event.');
        if (p.fieldData === 'yes') {
            if (!p.fieldOutcome) e.push('Select the worst outcome reported.');
            if (p.fieldOutcome === 'equipment_damage' && !p.damageCouldDelay) e.push('Answer whether the damage could delay or prevent treatment.');
            if (!p.useRelated) e.push('Answer whether the cause was use-related.');
        }
    }
    if (step === 4) {
        if (!p.testing) e.push('Answer whether the changed design has been tested with users.');
        if (p.testing === 'formative' || p.testing === 'hf_validation') {
            if (!i.testParticipants) e.push('Enter the number of participants.');
            if (!i.noObservations && !i.useErrors.length) e.push('Add the use errors, close calls and difficulties, or tick "No use errors, close calls or difficulties observed".');
            i.useErrors.forEach((r, n) => {
                if (r.taskIndex === null || !r.type || !r.participants || !r.rootCause || !r.designTested) e.push(`Complete observation ${n + 1} (task, type, participants, root cause, design tested).`);
                if (r.participants && i.testParticipants && r.participants > i.testParticipants) e.push(`Observation ${n + 1}: more participants than were tested (${i.testParticipants}).`);
                if (r.rootCause === 'test_artifact' && !r.rootCauseNotes) e.push(`Observation ${n + 1}: add root cause notes for the test artifact.`);
                if (r.designTested === 'previous' && !r.fixedByChange) e.push(`Observation ${n + 1}: say whether this change fixes it.`);
            });
        }
    }
    return e;
}

function dcWizardValidateStep(step) {
    const errEl = document.getElementById('dcFormError');
    const errors = dcStepErrors(step);
    if (errors.length) {
        errEl.textContent = errors.join(' ');
        errEl.style.display = 'block';
        return false;
    }
    errEl.style.display = 'none';
    return true;
}

async function dcWizardNext() {
    if (!dcWizardValidateStep(dcState.currentStep)) return;
    if (dcState.currentStep === 2) {
        // New suggestions: stay on this step once so the user can review them.
        const nextBtn = document.getElementById('dcWizardNextBtn');
        nextBtn.disabled = true;
        const added = await dcFetchTaskChecks().finally(() => { nextBtn.disabled = false; });
        if (added) {
            const errEl = document.getElementById('dcFormError');
            errEl.textContent = 'Some tasks need a check: confirm Yes, No or Unsure under each one, then click Next. Unconfirmed tasks are treated as Yes.';
            errEl.style.display = 'block';
            return;
        }
    }
    if (dcState.currentStep < dcState.totalSteps) {
        dcSaveDraft();
        dcWizardGoTo(dcState.currentStep + 1);
    } else {
        dcRunAnalysis();
    }
}

function dcWizardPrev() {
    if (dcState.currentStep > 1) {
        dcSaveDraft();
        dcWizardGoTo(dcState.currentStep - 1);
    }
}

function dcSelectPill(pill) {
    const group = pill.dataset.group;
    // Clear current selection in this group
    document.querySelectorAll(`[data-group="${group}"]`).forEach((p) => {
        p.className = 'dc-pill';
    });
    // Apply selected class
    const val = pill.dataset.val;
    const colorMap = { yes: 'dc-pill-selected-yes', no: 'dc-pill-selected-no', unsure: 'dc-pill-selected-unknown' };
    pill.classList.add(colorMap[val] || 'dc-pill-selected-active');
    dcState.pills[group] = val;
}

async function dcRunAnalysis() {
    const nextBtn = document.getElementById('dcWizardNextBtn');
    const iconEl  = document.getElementById('dcAnalyzeIcon');
    const labelEl = document.getElementById('dcAnalyzeBtnLabel');

    if (nextBtn) nextBtn.disabled = true;
    if (iconEl)  iconEl.className = 'fas fa-spinner fa-spin';
    if (labelEl) labelEl.textContent = 'Running analysis…';

    const payload = {
        deviceName:        currentProject?.name || undefined,
        changeDescription: document.getElementById('dcChangeDesc').value.trim(),
        pathway:           currentProject?.original_regulatory_pathway || 'unknown',
        useEnvironment:    (currentProject?.use_environments || []).join(', ') || undefined,
        extraContext:      document.getElementById('dcExtraContext').value.trim() || undefined,
        intake:            dcCollectIntake(),
    };

    try {
        // Pass existing draft ID so the backend updates it in-place rather than creating a new record
        if (dcState.currentAnalysisId) payload.analysisId = dcState.currentAnalysisId;
        // Always pass invocationId so a new analysis gets linked to the tool invocation
        if (dcState.invocationId) payload.invocationId = dcState.invocationId;
        const { analysisId } = await ApiClient.post('/dc-analyzer/analyze', payload);
        // Track so draft saves and stale-fetch checks work after this point
        dcState.currentAnalysisId = analysisId;

        // Poll until complete (backend runs Bedrock async to avoid API Gateway timeout)
        if (labelEl) labelEl.textContent = 'Analyzing…';
        const result = await dcPollAnalysis(analysisId);
        await dcRenderResults(result);
        dcShowPage('results');
    } catch (err) {
        const errEl = document.getElementById('dcFormError');
        errEl.textContent = `Analysis failed: ${err.message || 'Please try again.'}`;
        errEl.style.display = 'block';
    } finally {
        if (nextBtn) nextBtn.disabled = false;
        if (iconEl)  iconEl.className = 'fas fa-search';
        if (labelEl) labelEl.textContent = 'Run analysis';
    }
}

async function dcPollAnalysis(analysisId, intervalMs = 3000, timeoutMs = 120000) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await new Promise(r => setTimeout(r, intervalMs));
        const { analysis } = await ApiClient.get(`/dc-analyzer/analyses/${analysisId}`);
        if (analysis.status === 'complete') return analysis.result;
        if (analysis.status === 'error')    throw new Error(analysis.error_message || 'Analysis failed on server');
    }
    throw new Error('Analysis timed out — please try again.');
}

async function dcRenderResults(result) {
    await hfLoadRegulatoryRefs(); // citation chip tooltips

    // System admins can inspect the registry snapshot the analysis ran against
    // (lives on the dc_analyses row, not in `result`).
    window._dcSnapshot = null;
    if (AuthManager.getUser()?.role === 'system_admin' && dcState.currentAnalysisId) {
        try {
            const res = await ApiClient.get(`/dc-analyzer/analyses/${dcState.currentAnalysisId}`);
            window._dcSnapshot = res.analysis?.regulatory_snapshot || null;
        } catch (err) {
            console.error('Failed to load DC regulatory snapshot', err);
        }
    }
    const ra = document.getElementById('dcResultsArea');
    ra.innerHTML = '';

    const dcSnapshotBtn = window._dcSnapshot
        ? `<button type="button" class="project-doc-eye-btn" title="View regulatory snapshot" onclick="showExtractedDataModal('regulatory-snapshot', window._dcSnapshot)"><i class="fas fa-eye"></i></button>`
        : '';
    ra.innerHTML += `<div style="position:relative;"><div class="dc-disclaimer-bar"${window._dcSnapshot ? ' style="padding-right:2rem;"' : ''}>This analysis is a professional assessment to guide decision-making — not a regulatory determination. Your Regulatory Affairs lead must make the final filing decision.</div>${dcSnapshotBtn}</div>`;

    const dcUnknownCitations = result.citationValidation?.unknownCount || 0;
    if (dcUnknownCitations > 0) {
        ra.innerHTML += `<div class="hf-signal-banner critical">
            <div class="hf-signal-banner-icon">⚠️</div>
            <div>
                <h3>Unrecognized regulatory citations</h3>
                <p>${dcUnknownCitations} citation${dcUnknownCitations !== 1 ? 's' : ''} reference clauses not in the regulatory reference registry (marked ⚠ below). Verify before relying on them in a submission.</p>
            </div>
        </div>`;
    }

    // Verdict card — the label comes from the code rules (v2) or the stored
    // verdict value (older analyses).
    const vClass   = { resubmission: 'dc-verdict-resubmission', 'gray-zone': 'dc-verdict-gray-zone', equivalency: 'dc-verdict-equivalency' }[result.verdict] || 'dc-verdict-gray-zone';
    const dotClass = { resubmission: 'dc-dot-red', 'gray-zone': 'dc-dot-yellow', equivalency: 'dc-dot-green' }[result.verdict] || 'dc-dot-yellow';
    const label    = result.verdict_label || DC_VERDICT_LABELS[result.verdict] || result.verdict_title || '';
    const category = result.category || { equivalency: 1, 'gray-zone': 2, resubmission: 3 }[result.verdict];
    ra.innerHTML += `
        <div class="dc-verdict-card ${vClass}">
            <div class="dc-verdict-header">
                <div class="dc-verdict-dot ${dotClass}"></div>
                <div class="dc-verdict-title">${escapeHtml(label)}</div>
            </div>
            ${category ? `<div class="dc-verdict-category">FDA Submission Category ${category}</div>` : ''}
            <div class="dc-verdict-body">${escapeHtml(result.verdict_rationale)}</div>
        </div>`;

    // What drove the verdict (v2: code triggers tagged by category)
    const triggers    = result.triggers || [];
    const firedCount  = triggers.filter((t) => t.status === 'fired').length;
    const triggersHtml = triggers.length ? triggers.map((t) => {
        const cls  = { fired: 'dc-trigger-fired', warning: 'dc-trigger-warning', clear: 'dc-trigger-clear' }[t.status] || 'dc-trigger-warning';
        const icon = t.category ? String(t.category) : ({ fired: '✕', warning: '!', clear: '✓' }[t.status] || '!');
        const tag  = t.source === 'submitta' ? ' <span class="dc-trigger-tag">Submitta rule, not FDA</span>' : '';
        return `<div class="dc-trigger-item ${cls}">
            <div class="dc-trigger-icon">${icon}</div>
            <div class="dc-trigger-text">
                <strong>${escapeHtml(t.label)}</strong>${tag} — ${escapeHtml(t.finding)}
                ${t.condition ? `<div class="dc-trigger-condition">Rule: ${escapeHtml(t.condition)}</div>` : ''}
                ${hfCitationChips(t.citations)}
            </div>
        </div>`;
    }).join('') : '<p class="dc-muted">No Category 2 or 3 trigger fired.</p>';
    const isV2 = result.schema_version === 2;
    const triggerBadgeCls = firedCount > 0 ? 'dc-badge-risk' : 'dc-badge-check';
    const triggerBadgeTxt = isV2
        ? `${triggers.length} trigger${triggers.length === 1 ? '' : 's'}`
        : (firedCount > 0 ? `${firedCount} trigger${firedCount > 1 ? 's' : ''} fired` : 'All clear');
    ra.innerHTML += dcBuildCard('dcTriggersBody', 'dcTriggersChev', isV2 ? 'What Drove the Verdict' : 'Risk Trigger Assessment', triggerBadgeCls, triggerBadgeTxt, triggersHtml, true);

    // Intake notes: timing, warnings, assumptions, gaps, user-testing stats
    if (isV2) {
        const list = (title, items, cls) => (items || []).length
            ? `<div class="dc-notes-group ${cls || ''}"><div class="dc-notes-title">${title}</div><ul>${items.map((x) => `<li>${escapeHtml(x)}</li>`).join('')}</ul></div>` : '';
        const st = result.use_error_stats;
        const statsHtml = st ? `<div class="dc-notes-group"><div class="dc-notes-title">User testing (context only; does not set the verdict)</div>
            <div class="dc-class-grid">
                ${dcClassTile('Participants', String(st.participants ?? '—'))}
                ${dcClassTile('Critical-task use errors', `${st.critical_task_use_errors}${st.critical_task_use_error_rate != null ? ` (${st.critical_task_use_error_rate}%)` : ''}`)}
                ${dcClassTile('All use errors', String(st.use_errors))}
                ${dcClassTile('Close calls / difficulties', `${st.close_calls} / ${st.use_difficulties}`)}
            </div></div>` : '';
        const notesHtml = `
            ${result.transition_timing ? `<div class="dc-notes-group"><div class="dc-notes-title">Transition timing</div><p>${escapeHtml(result.transition_timing.text)}</p></div>` : ''}
            ${list('Warnings', result.warnings, 'dc-notes-warn')}
            ${list('Assumptions made for Unsure answers', result.assumptions)}
            ${list('Task checks: does each new, changed or removed task alter how a task is done?', result.task_checks)}
            ${list('Not provided', result.not_provided)}
            ${statsHtml}`;
        const warnCount = (result.warnings || []).length;
        ra.innerHTML += dcBuildCard('dcNotesBody', 'dcNotesChev', 'Intake Notes', warnCount ? 'dc-badge-risk' : 'dc-badge-check',
            warnCount ? `${warnCount} warning${warnCount === 1 ? '' : 's'}` : 'No warnings', notesHtml, true);
    }

    // Decision Point D
    const dpd = result.decision_point_d;
    if (dpd) {
        const ready = dpd.status === 'submission-ready';
        const dpdHtml = `
            <p class="dc-muted">Whether this change's HF information belongs in the marketing submission, or can be maintained internally and produced if FDA asks (2026 final guidance).</p>
            <p>${escapeHtml(dpd.rationale)}</p>
            ${(dpd.gaps || []).length ? `<div class="dc-notes-title">Gaps to close</div><ul class="dc-doc-check">${dpd.gaps.map((g) => `<li>${escapeHtml(g)}</li>`).join('')}</ul>` : ''}
            ${hfCitationChips(dpd.citations)}`;
        ra.innerHTML += dcBuildCard('dcDpdBody', 'dcDpdChev', 'Decision Point D', ready ? 'dc-badge-check' : 'dc-badge-risk',
            ready ? 'Submission-ready' : 'Inspection-ready only', dpdHtml, true);
    }

    // Change Classification
    const cc = result.change_classification || {};
    ra.innerHTML += dcBuildCard('dcClassBody', 'dcClassChev', 'Change Classification', 'dc-badge-change', 'Classification', `
        <div class="dc-class-grid">
            ${dcClassTile('Interface affected',    cc.interface_type)}
            ${dcClassTile('Task criticality',      cc.task_criticality)}
            ${dcClassTile('Change scope',          cc.change_scope)}
            ${dcClassTile('User population risk',  cc.user_population_risk)}
        </div>`, true);

    // Document Checklist
    const docsHtml = (result.document_checklist || []).map((d) => `
        <div class="dc-doc-item">
            <div class="dc-doc-name">${escapeHtml(d.document)}</div>
            <ul class="dc-doc-check">${(d.what_to_check || []).map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul>
            ${hfCitationChips(d.citations)}
        </div>`).join('');
    ra.innerHTML += dcBuildCard('dcDocsBody', 'dcDocsChev', 'Document Checklist', 'dc-badge-docs', 'Review required', docsHtml, true);

    // Next Steps + Q-Sub
    const stepsHtml = (result.next_steps || []).map((s, i) => `
        <div class="dc-step-item">
            <div class="dc-step-num">${i + 1}</div>
            <div class="dc-step-text">${escapeHtml(s)}</div>
        </div>`).join('');
    const qsubHtml = result.qsub_recommended
        ? `<div class="dc-trigger-item dc-trigger-warning" style="margin-top:12px;"><div class="dc-trigger-icon">!</div><div class="dc-trigger-text"><strong>Pre-submission meeting (Q-Sub) recommended</strong> — ${escapeHtml(result.qsub_rationale)}</div></div>`
        : `<div class="dc-trigger-item dc-trigger-clear"   style="margin-top:12px;"><div class="dc-trigger-icon">✓</div><div class="dc-trigger-text"><strong>Q-Sub not required</strong> — ${escapeHtml(result.qsub_rationale)}</div></div>`;
    ra.innerHTML += dcBuildCard('dcStepsBody', 'dcStepsChev', 'Recommended Next Steps', 'dc-badge-next', 'Action required', stepsHtml + qsubHtml, true);

    // Export
    ra.innerHTML += `<div class="dc-export-row">
        <button class="btn btn-primary" onclick="dcExportReport()">
            <i class="fas fa-download"></i> Export assessment
        </button>
        <button class="btn btn-outline" onclick="window.print()">
            <i class="fas fa-print"></i> Print
        </button>
    </div>`;

    ra.style.display = 'block';
    window._dcLastResult = result;
}

function dcBuildCard(bodyId, chevId, title, badgeCls, badgeTxt, content, startOpen) {
    return `
    <div class="dc-result-card">
        <div class="dc-result-card-header" onclick="dcToggleCard('${bodyId}','${chevId}')">
            <div class="dc-result-card-title">${title}</div>
            <span class="dc-result-card-badge ${badgeCls}">${badgeTxt}</span>
            <svg class="dc-result-card-chevron ${startOpen ? 'open' : ''}" id="${chevId}" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </div>
        <div class="dc-result-card-body ${startOpen ? 'open' : ''}" id="${bodyId}">${content}</div>
    </div>`;
}

function dcToggleCard(bodyId, chevId) {
    document.getElementById(bodyId)?.classList.toggle('open');
    document.getElementById(chevId)?.classList.toggle('open');
}

function dcClassTile(label, val) {
    return `<div class="dc-class-tile">
        <div class="dc-class-tile-label">${label}</div>
        <div class="dc-class-tile-value">${escapeHtml(val || '—')}</div>
    </div>`;
}

async function dcExportReport() {
    const analysisId = dcState.currentAnalysisId;
    if (!analysisId) return;
    try {
        const dateStr  = new Date().toISOString().slice(0, 10);
        const filename = `DC_Impact_Assessment_${dateStr}.docx`;
        const blob = await ApiClient.postBlob('/dc-analyzer/export', { analysisId });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    } catch (err) {
        showToast('Export failed', err.message || 'Could not generate report', 'error');
    }
}

async function dcShowHistoryPage() {
    dcShowPage('history');
    const list = document.getElementById('dcHistoryList');
    list.innerHTML = '<div class="loading">Loading…</div>';
    try {
        const res = await ApiClient.get('/dc-analyzer/analyses?limit=20');
        if (!res.analyses || res.analyses.length === 0) {
            list.innerHTML = '<p style="color:#6b7280;padding:1rem 0;">No past analyses found.</p>';
            return;
        }
        list.innerHTML = res.analyses.map((a) => {
            // The list endpoint returns `verdict` (result->>'verdict'), not the full result.
            const verdictVal = a.verdict || 'pending';
            const verdictCls = `dc-past-verdict-${verdictVal}`;
            const verdictTxt = DC_VERDICT_LABELS[verdictVal] || (verdictVal === 'pending' ? 'Pending' : verdictVal);
            const date       = new Date(a.created_at).toLocaleDateString();
            const changePreview = a.change_description ? escapeHtml(a.change_description) : 'Design Change Assessment';
            return `<div class="dc-past-item" onclick="dcLoadPastAnalysis(${jsArg(String(a.id))})">
                <div class="dc-past-item-left">
                    <div class="dc-past-device">${changePreview}</div>
                    <div class="dc-past-meta">${date}</div>
                </div>
                <span class="dc-past-verdict ${escapeHtml(verdictCls)}">${escapeHtml(verdictTxt)}</span>
            </div>`;
        }).join('');
    } catch {
        list.innerHTML = '<p style="color:#6b7280;padding:1rem 0;">Could not load past analyses.</p>';
    }
}

async function dcLoadPastAnalysis(id) {
    try {
        const res = await ApiClient.get(`/dc-analyzer/analyses/${id}`);
        if (res.analysis?.result) {
            dcState.currentAnalysisId = id;
            await dcRenderResults(res.analysis.result);
            dcShowPage('results');
        }
    } catch (err) {
        console.error('Failed to load past DC analysis', err);
    }
}

// ════════════════════════════════════════════════════════════════════════════
// HF Advisor v2 renderers
// ════════════════════════════════════════════════════════════════════════════
// Triggered when result.schemaVersion === 2. Render the six-tab output from
// the new prompt-cached invoke-agent Lambda. Reuses the same DOM containers
// (hfTabOverview, hfTabUserGroups, hfTabTaskScenarios, hfTabFdaSignals,
// hfTabRationale) so the tab navigation, summary header, and DOCX export
// button continue to work without HTML changes.

function hfV2SourcePill(source) {
    if (!source) return '';
    const cls = source === '[DOC]' ? 'hf-v2-src-doc' : 'hf-v2-src-ext';
    return `<span class="hf-v2-src-pill ${cls}">${escapeHtml(source)}</span>`;
}

// ── Regulatory reference citations ──────────────────────────────────────────
// clauseId → { displayLabel, title, summary, refTitle, status } built once from
// GET /api/regulatory-references (the same registry the analysis pipeline
// injects into the model's context). Null until loaded; chips degrade to the
// raw clause id when the registry can't be fetched.

let hfRegulatoryClauseIndex = null;

async function hfLoadRegulatoryRefs() {
    if (hfRegulatoryClauseIndex) return hfRegulatoryClauseIndex;
    try {
        const res = await ApiClient.get('/regulatory-references');
        const index = {};
        for (const ref of res.references || []) {
            for (const clause of ref.clauses || []) {
                index[clause.clauseId] = {
                    displayLabel: clause.displayLabel,
                    title:        clause.title,
                    summary:      clause.summary,
                    refTitle:     ref.title,
                    status:       ref.status,
                };
            }
        }
        hfRegulatoryClauseIndex = index;
    } catch (err) {
        console.error('Failed to load regulatory references:', err);
    }
    return hfRegulatoryClauseIndex;
}

// Renders a citations array ([{refKey, clauseId, valid?}]) as clickable pills.
// Clicking a pill toggles a panel below the row showing that clause (label,
// title, summary); several can be open at once, each headed by its own label.
// Citations flagged valid:false by the validate-store Lambda (clauseId not in
// the registry snapshot) are dimmed with a warning marker instead of hidden.
function hfCitationChips(citations) {
    if (!Array.isArray(citations)) return '';
    const items = citations.filter((c) => c && c.clauseId);
    if (!items.length) return '';
    const pills = [];
    const panels = [];
    items.forEach((c, i) => {
        const clause  = hfRegulatoryClauseIndex?.[c.clauseId];
        const label   = clause?.displayLabel || c.clauseId;
        const invalid = c.valid === false;
        pills.push(`<button type="button" class="hf-v2-legend-pill" style="cursor:pointer;${invalid ? 'opacity:0.55;' : ''}" data-cite="${i}" onclick="hfToggleCitation(this)">${invalid ? '⚠ ' : '§ '}${escapeHtml(label)}</button>`);
        const body = invalid
            ? `Unrecognized citation — ${escapeHtml(c.clauseId)} is not in the regulatory reference registry.`
            : clause
                ? `<div><strong>${escapeHtml(clause.title)}</strong>${clause.status !== 'current' ? ` <em>(${escapeHtml(clause.status)})</em>` : ''}</div>
                   <div>${escapeHtml(clause.summary)}</div>
                   <div style="opacity:0.7;margin-top:0.25rem">${escapeHtml(clause.refTitle)}</div>`
                : 'Clause text unavailable.';
        panels.push(`<div data-cite-panel="${i}" style="display:none;margin-top:0.5rem;padding:0.5rem 0.75rem;border-left:3px solid currentColor;font-size:0.9em"><div style="font-weight:600">§ ${escapeHtml(label)}</div>${body}</div>`);
    });
    return `<div class="hf-cite" style="margin-top:0.5rem"><div class="hf-v2-legend">${pills.join('')}</div>${panels.join('')}</div>`;
}

function hfToggleCitation(btn) {
    const panel = btn.closest('.hf-cite').querySelector(`[data-cite-panel="${btn.dataset.cite}"]`);
    const open = panel.style.display === 'none';
    panel.style.display = open ? 'block' : 'none';
    btn.style.fontWeight = open ? '700' : '';
}

// Splits a text block into multiple <p> elements so blank-line-separated
// paragraphs from the model render with spacing instead of collapsing into one
// run-on block. Falls back to a single paragraph when there are no breaks.
function hfV2Paragraphs(text) {
    if (!text) return '';
    const chunks = String(text).split(/\n+/).map((p) => p.trim()).filter(Boolean);
    return chunks.map((p) => `<p>${escapeHtml(p)}</p>`).join('');
}

function hfV2Checklist(items) {
    if (!Array.isArray(items) || items.length === 0) return '';
    return `<ul class="hf-v2-checklist">${items.map((i) => `<li>${escapeHtml(String(i))}</li>`).join('')}</ul>`;
}

// Overview sections where the [DOC]/[EXTERNAL] source pill is hidden in the UI
// to reduce visual clutter. The pill is still written to the DOCX export
// (generated server-side from the stored analysis), so traceability is kept.
const HF_V2_HIDE_SOURCE_SECTIONS = new Set([
    'Simulation Fidelity',
]);

function hfV2Section(title, section) {
    if (!section) return '';
    const paragraph = hfV2Paragraphs(section.paragraph);
    const sourcePill = HF_V2_HIDE_SOURCE_SECTIONS.has(title) ? '' : hfV2SourcePill(section.source);
    return `
        <div class="hf-v2-overview-section">
            <div class="hf-v2-overview-section-head">
                <h4>${escapeHtml(title)}</h4>
                ${sourcePill}
            </div>
            ${paragraph}
            ${hfV2Section.detailRenderers[title] ? hfV2Section.detailRenderers[title](section) : ''}
            ${hfV2Checklist(section.checklist)}
            ${hfCitationChips(section.citations)}
        </div>`;
}
hfV2Section.detailRenderers = {
    'Study Tasks': (s) => {
        const rows = Array.isArray(s.count_by_user_group) ? s.count_by_user_group : [];
        if (!rows.length) return '';
        return `<ul class="hf-v2-inline-list">${rows.map((r) =>
            `<li><strong>${escapeHtml(r.user_profile || '')}:</strong> ${escapeHtml(String(r.critical_task_count ?? '—'))} critical task${r.critical_task_count === 1 ? '' : 's'}</li>`
        ).join('')}</ul>`;
    },
    'Simulation Fidelity': (s) => {
        const reqs = Array.isArray(s.requirements) ? s.requirements : [];
        if (!reqs.length) return '';
        return `<ol class="hf-v2-inline-list">${reqs.map((r) => `<li>${escapeHtml(String(r))}</li>`).join('')}</ol>`;
    },
    'Data Capture': (s) => {
        const parts = [];
        if (s.observers)    parts.push(`<div><strong>Observers:</strong> ${escapeHtml(s.observers)}</div>`);
        if (Array.isArray(s.form_fields) && s.form_fields.length) {
            parts.push(`<div><strong>Form fields:</strong> ${s.form_fields.map((f) => escapeHtml(String(f))).join(', ')}</div>`);
        }
        if (s.video_coding) parts.push(`<div><strong>Video coding:</strong> ${escapeHtml(s.video_coding)}</div>`);
        if (Array.isArray(s.device_fields) && s.device_fields.length) {
            parts.push(`<div><strong>Device fields:</strong> ${s.device_fields.map((f) => escapeHtml(String(f))).join(', ')}</div>`);
        }
        return parts.length ? `<div class="hf-v2-inline-block">${parts.join('')}</div>` : '';
    },
    'Session Duration': (s) => {
        const rows = Array.isArray(s.ranges_by_user_group) ? s.ranges_by_user_group : [];
        const fatigueBanner = s.fatigueRisk
            ? `<div class="hf-v2-fatigue-banner">⚠️ Fatigue risk — one or more sessions exceed 120 minutes.</div>`
            : '';
        const list = rows.length
            ? `<ul class="hf-v2-inline-list">${rows.map((r) =>
                `<li><strong>${escapeHtml(r.user_profile || '')}:</strong> ${escapeHtml(String(r.minutes_min ?? '?'))}–${escapeHtml(String(r.minutes_max ?? '?'))} min</li>`).join('')}</ul>`
            : '';
        return fatigueBanner + list;
    },
    'Protocol Deviations': (s) => {
        const devs = Array.isArray(s.deviations) ? s.deviations : [];
        if (!devs.length) return '';
        return `<div class="hf-v2-deviations">${devs.map((d) => `
            <div class="hf-v2-deviation">
                <div><strong>What:</strong> ${escapeHtml(d.what || '')}</div>
                <div><strong>Why:</strong> ${escapeHtml(d.why || '')}</div>
                <div><strong>Regulatory / logistical implication:</strong> ${escapeHtml(d.regulatoryOrLogisticalImplication || '')}</div>
                <div><strong>Protocol must account for:</strong> ${escapeHtml(d.protocolMustAccountFor || '')}</div>
            </div>`).join('')}</div>`;
    },
    'Regulatory Documentation': (s) => {
        const obs = Array.isArray(s.obligations) ? s.obligations : [];
        if (!obs.length) return '';
        return `<ul class="hf-v2-inline-list">${obs.map((o) => `<li>${escapeHtml(String(o))}</li>`).join('')}</ul>`;
    },
    'Ethics': (s) => {
        const parts = [];
        if (s.irb_level) parts.push(`<div><strong>IRB review level:</strong> ${escapeHtml(s.irb_level)}</div>`);
        if (s.hipaa)     parts.push(`<div><strong>HIPAA consent:</strong> ${escapeHtml(s.hipaa)}</div>`);
        if (Array.isArray(s.vulnerable_populations) && s.vulnerable_populations.length) {
            parts.push(`<div><strong>Vulnerable populations:</strong> ${s.vulnerable_populations.map((v) => escapeHtml(String(v))).join(', ')}</div>`);
        }
        return parts.length ? `<div class="hf-v2-inline-block">${parts.join('')}</div>` : '';
    },
};

function hfRenderV2Overview(result) {
    const el = document.getElementById('hfTabOverview');
    if (!el) return;
    const ov = result.overview || {};
    // Citation validation warning — the validate-store Lambda flags citations
    // whose clauseId is not in the registry snapshot the run used.
    const cv = result.citationValidation;
    const citationBanner = cv?.unknownCount > 0
        ? `<div class="hf-signal-banner critical">
            <div class="hf-signal-banner-icon">⚠️</div>
            <div>
                <h3>Unrecognized regulatory citations</h3>
                <p>${cv.unknownCount} citation${cv.unknownCount !== 1 ? 's' : ''} reference clauses not in the regulatory reference registry (marked ⚠ below). Verify before relying on them in a submission.</p>
            </div>
           </div>`
        : '';
    el.innerHTML = `
        ${citationBanner}
        ${hfV2Section('Study Tasks',              ov.studyTasks)}
        ${hfV2Section('Simulation Fidelity',      ov.simulationFidelity)}
        ${hfV2Section('Data Capture',             ov.dataCapture)}
        ${hfV2Section('Session Duration',         ov.sessionDuration)}
        ${hfV2Section('Protocol Deviations',      ov.protocolDeviations)}
        ${hfV2Section('Regulatory Documentation', ov.regulatoryDocumentation)}
        ${ov.ethics ? hfV2Section('Ethics', ov.ethics) : ''}`;
}

function hfRenderV2UserGroups(groups) {
    const el = document.getElementById('hfTabUserGroups');
    if (!el) return;
    if (!groups.length) { el.innerHTML = '<p style="color:#6b7280;text-align:center;padding:2rem">No user groups generated.</p>'; return; }

    el.innerHTML = groups.map((g) => {
        const f  = g.userGroupFactors || {};
        const ta = g.trainingAssumption || {};
        const inclusion = Array.isArray(g.screenerCriteria?.inclusion) ? g.screenerCriteria.inclusion : [];
        const exclusion = Array.isArray(g.screenerCriteria?.exclusion) ? g.screenerCriteria.exclusion : [];
        const trainingBlock = ta.source === '[MISSING]' || !ta.text
            ? `<div class="hf-group-training hf-group-training-missing">
                  <h5>Training <span class="hf-urs-badge hf-urs-badge-missing">[MISSING]</span></h5>
                  <p>No URS training assumption available. Resolve before writing the study protocol — common FDA deficiency trigger.</p>
               </div>`
            : `<div class="hf-group-training">
                  <h5>Training (from URS)</h5>
                  <p>${escapeHtml(ta.text)}</p>
               </div>`;

        return `
        <div class="hf-group-card" id="hfV2Group${escapeHtml(g.id || '')}">
            <button class="hf-group-toggle" onclick="document.getElementById(${jsArg('hfV2Group' + (g.id || ''))}).classList.toggle('expanded')">
                <div class="hf-group-toggle-inner">
                    <div class="hf-group-toggle-left">
                        <div class="hf-group-title-row">
                            <h4>${escapeHtml(g.id || '')}: ${escapeHtml(g.title || '')}</h4>
                            ${g.descriptor ? `<span class="hf-badge-exp">${escapeHtml(g.descriptor)}</span>` : ''}
                        </div>
                        <div class="hf-group-summary-row">
                            <span><strong>n=</strong> ${escapeHtml(String(g.n ?? '?'))}</span>
                            ${g.experienceMix ? `<span><strong>Experience mix:</strong> ${escapeHtml(g.experienceMix)}</span>` : ''}
                        </div>
                    </div>
                    <svg class="hf-chevron" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/></svg>
                </div>
            </button>
            <div class="hf-group-body">
                <div class="hf-group-factors">
                    <h5>User Group Factors (FDA Required)</h5>
                    <div class="hf-factor-item"><strong>Skills:</strong><p>${escapeHtml(f.skills || '')}</p></div>
                    <div class="hf-factor-item"><strong>Experience with Similar Devices:</strong><p>${escapeHtml(f.experienceWithSimilarDevices || '')}</p></div>
                    <div class="hf-factor-item"><strong>Procedure Volume / Frequency:</strong><p>${escapeHtml(f.procedureVolumeFrequency || '')}</p></div>
                    <div class="hf-factor-item"><strong>Use Environment:</strong><p>${escapeHtml(f.useEnvironment || '')}</p></div>
                    <div class="hf-factor-item"><strong>Overall Experience:</strong><p>${escapeHtml(f.overallExperience || '')}</p></div>
                </div>
                ${trainingBlock}
                <div class="hf-screener">
                    <h5>Recruitment Screener Criteria</h5>
                    ${inclusion.length ? `<div><strong>Inclusion:</strong><ul>${inclusion.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul></div>` : ''}
                    ${exclusion.length ? `<div><strong>Exclusion:</strong><ul>${exclusion.map((c) => `<li>${escapeHtml(c)}</li>`).join('')}</ul></div>` : ''}
                </div>
                <div class="hf-rationale">
                    <h5>FDA Rationale</h5>
                    <div class="hf-rationale-box">${escapeHtml(hfStripInlineSourceTags(g.fdaRationale || '', ['EXTERNAL', 'DOC']))}</div>
                    ${hfCitationChips(g.citations)}
                </div>
            </div>
        </div>`;
    }).join('');
}

function hfRenderV2UseScenarios(scenarios, userGroups) {
    const el = document.getElementById('hfTabTaskScenarios');
    if (!el) return;
    if (!scenarios.length) { el.innerHTML = '<p style="color:#6b7280;text-align:center;padding:2rem">No use scenarios generated.</p>'; return; }

    // Legend showing UG-NN → title so a reader can decode the targetUserGroups
    // tags on each scenario card without scrolling back to Tab 2.
    const legend = userGroups.length
        ? `<div class="hf-v2-legend">
            <strong>User group legend:</strong>
            ${userGroups.map((g) => `<span class="hf-v2-legend-pill"><strong>${escapeHtml(g.id || '')}</strong> · ${escapeHtml(g.title || '')}</span>`).join('')}
           </div>`
        : '';

    const riskCardClass  = { Low: 'hf-scenario-low', Moderate: 'hf-scenario-medium', High: 'hf-scenario-high', Critical: 'hf-scenario-critical' };
    const riskBadgeClass = { Low: 'hf-risk-badge-low', Moderate: 'hf-risk-badge-medium', High: 'hf-risk-badge-high', Critical: 'hf-risk-badge-critical' };

    el.innerHTML = `
        <div style="margin-bottom:1rem">
            <h3 style="font-size:1rem;font-weight:600;color:#111827;margin-bottom:0.25rem">Use Scenarios</h3>
            <p style="font-size:0.82rem;color:#6b7280">${scenarios.length} scenario${scenarios.length !== 1 ? 's' : ''} for formative usability testing</p>
        </div>
        ${legend}
        ${scenarios.map((s) => {
            const stress   = Array.isArray(s.stressConditions) ? s.stressConditions : [];
            const observer = Array.isArray(s.hfObserverNotes)  ? s.hfObserverNotes  : [];
            const targets  = Array.isArray(s.targetUserGroups) ? s.targetUserGroups : [];
            const tr       = s.traceability || {};
            return `
            <div class="hf-scenario-card ${riskCardClass[s.riskLevel] || 'hf-scenario-medium'}">
                <div class="hf-scenario-header">
                    <div class="hf-scenario-title-group">
                        <span class="hf-scenario-num">${escapeHtml(s.id || '')}</span>
                        <h4>${escapeHtml(s.title || '')}</h4>
                    </div>
                    <span class="hf-scenario-risk-badge ${riskBadgeClass[s.riskLevel] || 'hf-risk-badge-medium'}">${escapeHtml(s.riskLevel || '')} Risk</span>
                </div>
                <p>${escapeHtml(s.description || '')}</p>
                <div class="hf-pill-group">
                    <span class="hf-pill-label">Target User Groups:</span>
                    <div class="hf-pills">
                        ${targets.length
                            ? targets.map((t) => `<span class="hf-pill-target">${escapeHtml(t)}</span>`).join('')
                            : '<span class="hf-pill-target" style="opacity:0.6;font-style:italic">None assigned</span>'}
                    </div>
                </div>
                ${stress.length ? `
                <div class="hf-pill-group">
                    <span class="hf-pill-label">Stress Conditions:</span>
                    <div class="hf-pills">
                        ${stress.map((c) => `<span class="hf-pill-stress">⚡ ${escapeHtml(c)}</span>`).join('')}
                    </div>
                </div>` : ''}
                ${observer.length ? `
                <div class="hf-pill-group">
                    <span class="hf-pill-label">HF Observer Notes:</span>
                    <div class="hf-pills">
                        ${observer.map((c) => `<span class="hf-pill-observer">👁 ${escapeHtml(c)}</span>`).join('')}
                    </div>
                </div>` : ''}
                <div class="hf-v2-traceability">
                    ${tr.ufmeaCoverage     ? `<div><strong>UFMEA coverage:</strong> ${escapeHtml(tr.ufmeaCoverage)}</div>` : ''}
                    ${tr.requirementLinks  ? `<div><strong>Requirement links:</strong> ${escapeHtml(tr.requirementLinks)}</div>` : ''}
                    ${tr.passFailCriterion ? `<div><strong>Pass / fail:</strong> ${escapeHtml(tr.passFailCriterion)}</div>` : ''}
                    ${hfCitationChips(s.citations)}
                </div>
            </div>`;
        }).join('')}`;
}

// Strip inline source tags (e.g. "[EXTERNAL]") the model embeds in free-text
// paragraphs that have no dedicated source field. UI-only — the DOCX export is
// generated server-side from the stored analysis and keeps the tags.
function hfStripInlineSourceTags(text, tags) {
    if (!text) return text || '';
    let out = String(text);
    for (const tag of tags) {
        // Only consume horizontal whitespace before the tag — preserve newlines
        // so blank-line paragraph breaks survive for hfV2Paragraphs to split on.
        out = out.replace(new RegExp('[ \\t]*\\[' + tag + '\\]', 'gi'), '');
    }
    return out.replace(/[ \t]{2,}/g, ' ').trim();
}

// ── Theme toggle ──────────────────────────────────────────────────
document.getElementById('themeToggleBtn')?.addEventListener('click', () => {
    const root = document.documentElement;
    const next = root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
    root.setAttribute('data-theme', next);
    localStorage.setItem('theme', next);
    document.getElementById('themeIcon').className = next === 'dark' ? 'fas fa-sun' : 'fas fa-moon';
});
(function(){const s=localStorage.getItem('theme'),i=document.getElementById('themeIcon');if(i&&s==='dark')i.className='fas fa-sun';})();

function hfRenderV2RegulatoryRationale(rationale) {
    const el = document.getElementById('hfTabRationale');
    if (!el) return;
    const cites = rationale.sectionCitations || {};
    const section = (title, text, citations) => text
        ? `<div class="hf-v2-rat-section"><h4>${escapeHtml(title)}</h4>${hfV2Paragraphs(hfStripInlineSourceTags(text, ['EXTERNAL', 'DOC']))}${hfCitationChips(citations)}</div>`
        : '';
    el.innerHTML = `
        <h3 style="font-size:1rem;font-weight:600;color:#111827;margin-bottom:1rem;">Regulatory Rationale</h3>
        ${section('Opening',                   rationale.opening,                  cites.opening)}
        ${section('Predicate Anchoring',       rationale.predicateAnchoring,       cites.predicateAnchoring)}
        ${section('Predicate Deltas',          rationale.predicateDeltas,          cites.predicateDeltas)}
        ${section('Foreseeable User Expansion',rationale.foreseeableUserExpansion, cites.foreseeableUserExpansion)}
        ${section('User Group Distinction',    rationale.userGroupDistinction,     cites.userGroupDistinction)}
        ${section('Participant Counts',        rationale.participantCounts,        cites.participantCounts)}`;
}

